import React, { useState, useEffect, useRef } from "react";
import WelcomePage from "./WelcomePage";
import BeatsSimulation from "./BeatsSimulation";
import QuizPage from "./QuizPage";
import LatexRenderer from "./LatexRenderer";
import FinancePage from "./FinancePage";
import AboutProf from "./AboutProf";
import StudyGuides from "./StudyGuides";
import FamilyVideos from "./FamilyVideos";
import { NoticeManager } from "./Notices";
import { MessagesInbox } from "./Messages";
import AITutor from "./components/AITutor";
import MathToolsPage from "./MathToolsPage";
import TeacherDashboard from "./TeacherDashboard";
import FacultyClassroom, { StudentMyClasses } from "./FacultyClassroom";
import ClassMappingPanel from "./ClassMappingPanel";
import BroadcastPanel from "./BroadcastPanel";
import PhysicsPlotterPage from "./PhysicsPlotterPage";
import OnlineClassroom from "./components/OnlineClassroom";
import StudentActivityTracker from "./components/StudentActivityTracker";
import { db, auth, googleProvider, analytics, logEvent } from "./firebase";
import ImageCropModal from "./components/ImageCropModal";
import {
  collection, addDoc, getDocs, deleteDoc, doc,
  query, where, setDoc, writeBatch, getDoc, updateDoc,
  orderBy, limit, onSnapshot, serverTimestamp, deleteField, arrayUnion,
} from "firebase/firestore";
import { signInWithPopup, signOut, onAuthStateChanged } from "firebase/auth";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { getUserRole, getTeacherAssignedClasses, isAdmin as isAdminRole, requestTeacherAccess, getPendingTeacherRequest } from "./utils/rbac";
import { validateFileUpload } from "./utils/validation";
import { SuspendManager, loadSuspended } from "./ClassSuspend";
import "./App.css";

const ADMIN_EMAIL = process.env.REACT_APP_ADMIN_EMAIL || "g.devaraju@gmail.com";
const ALLOWED_DOMAINS = (process.env.REACT_APP_ALLOWED_DOMAINS || "rgukt.ac.in,gmail.com").split(",");
const SECTIONS = ["Reading Materials", "Assignments", "Question Papers", "Videos", "Simulations"];
const CLASSES = ["PUC-I", "PUC-II", "Engineering Physics"];

// ── Smart file ordering ─────────────────────────────────────────────
// Reads "UNIT-III", "Unit 2", "Chapter 4", "Ch-5", "L3", or a leading number
// from a file name so materials appear in syllabus order automatically.
const ROMAN = { i: 1, v: 5, x: 10, l: 50, c: 100 };
const romanToInt = (r) => {
  let total = 0;
  const str = r.toLowerCase();
  for (let i = 0; i < str.length; i++) {
    const cur = ROMAN[str[i]], next = ROMAN[str[i + 1]] || 0;
    if (!cur) return NaN;
    total += cur < next ? -cur : cur;
  }
  return total;
};
const extractSeqNumber = (name = "") => {
  const n = name.toLowerCase();
  // 1) Keyword + number: "UNIT-III", "Unit 2", "Assignment_02", "Chapter-10", "MT-II"
  const kw = n.match(/(?:^|[^a-z])(?:unit|chapter|chap|ch|module|mod|lecture|lec|part|week|assignment|assign|set|mt|test)[\s\-_:.#]*([ivx]+|\d+)(?![a-z])/);
  if (kw) {
    const v = /^\d+$/.test(kw[1]) ? parseInt(kw[1], 10) : romanToInt(kw[1]);
    if (!isNaN(v)) return v;
  }
  // 2) Any number in the name (last one): "Electrodynamics-02", "Mathematical Physics_01", "Electrodynamics-02_answers"
  const nums = n.match(/\d+/g);
  if (nums) return parseInt(nums[nums.length - 1], 10);
  // 3) Trailing roman numeral: "Optics - II"
  const rm = n.match(/[\s\-_]([ivx]+)\s*$/);
  if (rm) { const v = romanToInt(rm[1]); if (!isNaN(v)) return v; }
  return Infinity;
};
const autoCompareFiles = (a, b) => {
  const na = extractSeqNumber(a.name), nb = extractSeqNumber(b.name);
  if (na !== nb) return na - nb;
  const byName = (a.name || "").localeCompare(b.name || "", undefined, { numeric: true, sensitivity: "base" });
  return byName || (a.createdAt || 0) - (b.createdAt || 0);
};
// Custom (manually saved) order wins; files without a saved order are auto-sorted after them.
const smartSortFiles = (list) => [...list].sort((a, b) => {
  const ha = typeof a.order === "number", hb = typeof b.order === "number";
  if (ha && hb) return a.order - b.order;
  if (ha) return -1;
  if (hb) return 1;
  return autoCompareFiles(a, b);
});
const BATCHES = ["C1", "C2", "Both"];
const CLOUDINARY_CLOUD = process.env.REACT_APP_CLOUDINARY_CLOUD;
const CLOUDINARY_PRESET = process.env.REACT_APP_CLOUDINARY_PRESET;

const SIMULATIONS = {
  "PUC-I": [
    { name: "⚠️ Common Mistakes in Physics (PREVIEW)", desc: "9 most-tested misconceptions in entrance exams — Newton's laws, projectile sign convention, relative velocity, centripetal force, ladder, COM, MOI. Curated content with worked examples + MCQs.", url: "/simulations/plugplay/common_mistakes.html", icon: "⚠️", local: true },
    { name: "🌀 Kinematics", desc: "Distance, Velocity, Acceleration, Projectile Motion, Graphs — interactive simulations with Dr. Devaraju's voice narration", url: "/simulations/kinematics_puc1.html", icon: "🚀", local: true },
    { name: "💧 Fluid Mechanics", desc: "Pressure, Archimedes' Principle, Bernoulli, Continuity — conceptual simulations with live visualizations", url: "/simulations/fluids_puc1.html", icon: "🌊", local: true },
    { name: "Projectile Motion", desc: "Explore projectile motion with vectors, angles and speed", url: "https://phet.colorado.edu/en/simulations/projectile-motion", icon: "🎯" },
    { name: "Wave on a String", desc: "Explore how waves travel through a medium", url: "https://phet.colorado.edu/en/simulations/wave-on-a-string", icon: "〰️" },
    { name: "Forces and Motion", desc: "Explore forces, friction and Newton's laws", url: "https://phet.colorado.edu/en/simulations/forces-and-motion-basics", icon: "💪" },
    { name: "Simple Pendulum", desc: "Explore pendulum motion, period and how gravity affects oscillation", url: "https://phet.colorado.edu/en/simulations/pendulum-lab", icon: "🕰️" },
    { name: "Conservation of Energy", desc: "Visualize how kinetic and potential energy transform on a skate park", url: "https://phet.colorado.edu/en/simulations/energy-skate-park-basics", icon: "⚡" },
    { name: "Conservation of Angular Momentum", desc: "Explore rotational motion, torque and angular momentum conservation", url: "https://phet.colorado.edu/en/simulations/my-solar-system", icon: "🌀" },
    { name: "🚀 Projectile Motion Lab",         url: "/simulations/plugplay/projectile.html",        icon: "🟣", local: true },
    { name: "⚗️ Newton's 2nd Law — F=ma",      url: "/simulations/plugplay/newtons2nd.html",         icon: "🔵", local: true, locked: true },
    { name: "🕰️ Simple Pendulum — Measure g",   url: "/simulations/plugplay/pendulum.html",           icon: "🟢", local: true, locked: true },
    { name: "🌀 Hooke's Law — Spring Stretch",  url: "/simulations/plugplay/hookes.html",             icon: "🔴", local: true, locked: true },
    { name: "🧱 Friction — Static & Kinetic",   url: "/simulations/plugplay/friction.html",           icon: "🟠", local: true, locked: true },
    { name: "🏢 Ball from Building",            url: "/simulations/plugplay/building_ball.html",      icon: "🔷", local: true, locked: true },
    { name: "🚤 Boat & River — Relative Velocity", url: "/simulations/plugplay/boat_river.html",      icon: "🔵", local: true, locked: true },
    { name: "🌧️ Man & Rain — Relative Velocity",  url: "/simulations/plugplay/man_rain.html",         icon: "🟤", local: true, locked: true },
    { name: "📐 Vectors Lab",                   url: "/simulations/plugplay/vectors.html",            icon: "🟤", local: true, locked: true },
    { name: "🔄 Circular Motion Lab",           url: "/simulations/plugplay/circular_motion.html",   icon: "🔵", local: true, locked: true },
    { name: "🧱 Friction Lab",                  url: "/simulations/plugplay/friction_lab.html",      icon: "🟤", local: true, locked: true },
    { name: "🪙 Friction as Centripetal Force", url: "/simulations/plugplay/friction_centripetal.html", icon: "⚪", local: true, locked: true },
    { name: "⚖️ Centre of Mass & Gravity",      url: "/simulations/plugplay/center_of_mass.html",    icon: "⚖️", local: true, locked: true },
    { name: "📏 Measurement Errors Lab",        url: "/simulations/plugplay/errors.html",             icon: "🟡", local: true, locked: true },
    { name: "⚠️ Classic Mistakes Lab",          url: "/simulations/plugplay/classic_mistakes.html",  icon: "⚠️", local: true, locked: true },
    { name: "〰️ Diffraction",                  url: "/simulations/plugplay/diffraction.html",        icon: "〰️", local: true, locked: true },
    { name: "🌊 Pressure & Fluids",             url: "/simulations/plugplay/pressure_fluids.html",   icon: "🌊", local: true, locked: true },
    { name: "🪂 Newton's Laws — Conceptual",    url: "/simulations/plugplay/newtons_conceptual.html",icon: "🪂", local: true, locked: true },
    { name: "🪜 Ladder — Slipping & Equilibrium", url: "/simulations/plugplay/ladder_problem.html",  icon: "🪜", local: true, locked: true },
    { name: "🌀 Moment of Inertia",             url: "/simulations/plugplay/moment_of_inertia.html", icon: "🌀", local: true, locked: true },
    { name: "🐍 Rotational Dynamics",           url: "/simulations/plugplay/rotational_dynamics_python.html", icon: "🐍", local: true, locked: true },
    { name: "🧊 Heat of Mixing & Calorimetry — Audio", url: "/simulations/plugplay/calorimetry.html",   icon: "🧊", local: true, locked: true },
    { name: "🔥 Transmission of Heat — JEE Audio",    url: "/simulations/plugplay/heat_transmission.html", icon: "🔥", local: true, locked: true },
    { name: "⚡ Conservation of Momentum & Energy",   url: "/simulations/plugplay/momentum_energy.html",   icon: "⚡", local: true, locked: true },
    { name: "〰️ Simple Harmonic Motion — Audio Summary", url: "/simulations/plugplay/shm.html",           icon: "〰️", local: true, locked: true },
    { name: "🌀 Hooke's Law & Spring Systems — Audio Summary", url: "/simulations/plugplay/spring_systems.html", icon: "🌀", local: true, locked: true },
    { name: "🌊 Fluid Mechanics — Audio Summary", url: "/simulations/plugplay/fluids_audio.html",         icon: "🌊", local: true, locked: true },
    { name: "🔩 Elasticity — Stress, Strain & Moduli", url: "/simulations/plugplay/elasticity.html", icon: "🔩", local: true, locked: true },
    { name: "🌊 Wave Optics — Interference & YDSE Audio", url: "/simulations/plugplay/wave_optics.html", icon: "🌊", local: true, locked: true },
    { name: "⭐ Physics Greatest Discoveries",  url: "/simulations/plugplay/greatest_discoveries.html",       icon: "⭐", local: true, locked: true },
    { name: "🧩 Physics Scratch — Build & Simulate", url: "/simulations/plugplay/physics_scratch.html",        icon: "🧩", local: true, locked: true },
  ],
  "PUC-II": [
    { name: "Electric Field Hockey", desc: "Play hockey with electric charges and fields", url: "https://phet.colorado.edu/en/simulations/electric-field-hockey", icon: "⚡" },
    { name: "Circuit Construction Kit", desc: "Build and explore electrical circuits", url: "https://phet.colorado.edu/en/simulations/circuit-construction-kit-dc", icon: "🔌" },
    { name: "Geometric Optics", desc: "Explore lenses, mirrors and how light bends", url: "https://phet.colorado.edu/en/simulations/geometric-optics", icon: "🔭" },
    { name: "🐍 Electric Field — Python Plots", url: "/simulations/plugplay/electric_field_simulator.html", icon: "🐍", local: true, locked: true },
    { name: "Potentiometer", url: "/simulations/plugplay/potentiometer.html", icon: "⚡", local: true, locked: true },
    { name: "Galvanometer", url: "/simulations/plugplay/galvanometer.html", icon: "🧭", local: true, locked: true },
    { name: "Meter Bridge", url: "/simulations/plugplay/meter_bridge.html", icon: "⚖️", local: true, locked: true },
    { name: "Post Office Box", url: "/simulations/plugplay/post_office_box.html", icon: "📦", local: true, locked: true },
    { name: "🔥 Thermodynamics — Complete Summary", url: "/simulations/plugplay/thermodynamics.html", icon: "🔥", local: true, locked: true },
  ],
  "Engineering Physics": [
    { name: "⚡ Poynting's Theorem", desc: "Poynting vector, electromagnetic energy density, energy in capacitors and inductors", url: "/simulations/poynting_engphy.html", icon: "🌐", local: true },
    { name: "✨ Extraordinary Optical Transmission", desc: "Ebbesen effect: subwavelength hole arrays, surface plasmon resonance, Fano lineshapes — tune hole size & periodicity", url: "/simulations/plugplay/eot.html", icon: "✨", local: true, locked: true },
    { name: "🔬 Au Nanoclusters + Er³⁺ Plasmon Enhancement", desc: "Quantum-confined 1 nm Au clusters sensitize Er³⁺ emission at 1535 nm — Förster transfer, quenching, optimal distance", url: "/simulations/plugplay/au_er_nanoclusters.html", icon: "🔬", local: true, locked: true },
    { name: "Beats Phenomenon", desc: "Visualize and hear beats!", url: "beats", icon: "🎵", internal: true },
    { name: "Quantum Wave Interference", desc: "Explore quantum mechanics and wave-particle duality", url: "https://phet.colorado.edu/en/simulations/quantum-wave-interference", icon: "⚛️" },
    { name: "⚛️ Quantum Tunneling & Wave Packets", desc: "Explore quantum tunneling through barriers, delta potential, and wave packet dynamics", url: "https://phet.colorado.edu/en/simulations/quantum-tunneling", icon: "🌀" },
    { name: "⚡ Hall Effect", desc: "Carrier deflection by magnetic field, Hall field build-up, Hall voltage polarity & Hall coefficient — Metal, n-type and p-type semiconductors", url: "/simulations/plugplay/hall_effect.html", icon: "⚡", local: true },
    { name: "🔵 Quantum Bound States & Scattering", desc: "Finite square well — bound states (E<0) with quantized levels & evanescent tails, scattering states (E>0) with transmission T and reflection R", url: "/simulations/plugplay/quantum_bound_states.html", icon: "⚛️", local: true },
    { name: "🧊 Bose-Einstein Condensation", desc: "Visualise how bosons condense into the ground state at ultra-low temperatures — BEC phenomenon", url: "https://phet.colorado.edu/en/simulations/bose-einstein-condensation", icon: "❄️" },
    { name: "Photoelectric Effect", desc: "See how light ejects electrons from metal", url: "https://phet.colorado.edu/en/simulations/photoelectric", icon: "💡" },
    { name: "Wave Interference", desc: "Explore constructive and destructive interference", url: "https://phet.colorado.edu/en/simulations/wave-interference", icon: "🌊" },
  ],
};

const todayStr = () => new Date().toISOString().split("T")[0];

// ── Cloudinary image upload helper with validation ──
async function uploadImageToCloudinary(file) {
  // Validate file before upload
  validateFileUpload(file, {
    allowedTypes: ["image/jpeg", "image/png", "image/webp"],
    maxSize: 5 * 1024 * 1024, // 5MB
  });

  const formData = new FormData();
  formData.append("file", file);
  formData.append("upload_preset", "ug9qggxb");
  const res = await fetch(`https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD}/image/upload`, { method: "POST", body: formData });
  const data = await res.json();
  if (!data.secure_url) throw new Error("Image upload failed");
  return data.secure_url;
}

export default function App() {
  const [page, setPage] = useState("home");
  const [user, setUser] = useState(null);
  const [userRole, setUserRole] = useState(null); // "admin" | "teacher" | "student" | null
  const [teacherAssignedClasses, setTeacherAssignedClasses] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false); // Legacy, kept for compatibility
  const [isAllowed, setIsAllowed] = useState(false);
  const [studentAccess, setStudentAccess] = useState([]); // ["E1","P1","P2"]
  const [familyAllowed, setFamilyAllowed] = useState(false); // private Family Videos page
  const [loading, setLoading] = useState(true);
  const [pendingTeacherRequest, setPendingTeacherRequest] = useState(null);
  const [showTeacherRequestModal, setShowTeacherRequestModal] = useState(false);

  // Inactivity timeout - auto logout after 30 minutes of inactivity
  useEffect(() => {
    if (!user) return;

    const INACTIVITY_TIMEOUT = 30 * 60 * 1000; // 30 minutes
    let inactivityTimer;

    const resetTimer = () => {
      clearTimeout(inactivityTimer);
      inactivityTimer = setTimeout(() => {
        console.log("Session expired due to inactivity");
        handleLogout();
        alert("⏰ Your session has expired due to inactivity. Please login again.");
      }, INACTIVITY_TIMEOUT);
    };

    // Reset timer on user activity
    const events = ["mousedown", "keydown", "scroll", "touchstart"];
    events.forEach(event => window.addEventListener(event, resetTimer));

    resetTimer(); // Start initial timer

    return () => {
      clearTimeout(inactivityTimer);
      events.forEach(event => window.removeEventListener(event, resetTimer));
    };
  }, [user]);

  useEffect(() => {
    const handler = (e) => setPage(e.detail);
    window.addEventListener("navigate", handler);
    return () => window.removeEventListener("navigate", handler);
  }, []);

  useEffect(() => {
    onAuthStateChanged(auth, async (u) => {
      setUser(u);
      if (u) {
        try {
          const email = u.email || "";

          // Fetch user role from Firestore
          let role = null;
          try {
            role = await getUserRole(db, email);
          } catch (e) {
            console.error("Error fetching role from Firestore:", e);
          }

          // Backward compatibility: check if email is the hardcoded admin
          if (!role && email === ADMIN_EMAIL) {
            role = "admin";
            // Optionally save to Firestore for future migrations
            try {
              await setDoc(doc(db, "userRoles", email), {
                email,
                role: "admin",
                name: u.displayName || "",
                createdAt: new Date(),
                updatedAt: new Date(),
              });
            } catch (e) {
              console.error("Error saving admin role:", e);
            }
          }

          // Default to student if no role found
          if (!role) {
            role = "student";
          }

          setUserRole(role);
          setIsAdmin(role === "admin"); // Legacy support
          setIsAllowed(true);

          // Initialize with defaults
          let accessCodes = [];
          let assignedClasses = [];

          if (role === "admin") {
            accessCodes = ["E1", "P1", "P2", "S1"];
            assignedClasses = ["PUC-I", "PUC-II", "Engineering Physics"];
          } else if (role === "teacher") {
            // Fetch teacher's assigned classes
            try {
              const classes = await getTeacherAssignedClasses(db, email);
              assignedClasses = classes || [];
              // Teachers also get access to their classes
              const accessCodes = (classes || []).map(cls =>
                cls === "Engineering Physics" ? "E1" : cls === "PUC-I" ? "P1" : "P2"
              );
              setStudentAccess(accessCodes);
            } catch (e) {
              console.error("Error fetching teacher classes:", e);
              setTeacherAssignedClasses([]);
              setStudentAccess([]);
            }
          } else {
            // Student: fetch access from studentAccess collection
            try {
              const accessDoc = await getDoc(doc(db, "studentAccess", email));
              if (accessDoc.exists()) {
                const suspended = await loadSuspended();
                setStudentAccess((accessDoc.data().access || []).filter(c => !suspended.includes(c)));
              } else {
                setStudentAccess([]);
              }
            } catch (e) {
              console.error("Error fetching student access:", e);
              setStudentAccess([]);
            }
            setTeacherAssignedClasses([]);

            // Private Family Videos: only readable if this email is on the list
            try {
              const fam = await getDoc(doc(db, "familyAccess", "config"));
              setFamilyAllowed(fam.exists() && (fam.data().emails || []).includes(email));
            } catch { setFamilyAllowed(false); }

            // Check for pending teacher request
            try {
              const pendingReq = await getPendingTeacherRequest(db, email);
              setPendingTeacherRequest(pendingReq);
            } catch (e) {
              console.error("Error fetching pending teacher request:", e);
            }
          }
        } catch (e) {
          console.error("Error during auth state change:", e);
          // Fallback: set as student
          setUserRole("student");
          setIsAdmin(false);
          setIsAllowed(true);
          setStudentAccess([]);
          setTeacherAssignedClasses([]);
        }
      } else {
        setUserRole(null);
        setIsAdmin(false);
        setIsAllowed(false);
        setStudentAccess([]);
        setTeacherAssignedClasses([]);
      }
      setLoading(false);
    });
  }, []);

  // Track current page in presence (non-blocking)
  useEffect(() => {
    if (!user) return;
    setDoc(doc(db, "presence", user.email), {
      email: user.email,
      name: user.displayName || "",
      photoURL: user.photoURL || "",
      currentPage: page,
      lastSeen: new Date().toISOString(),
      online: true,
    }, { merge: true }).catch(() => {});
  }, [page, user]);

  // Log every portal open (once per browser session) — signed-in users only
  useEffect(() => {
    if (!user || !user.email || user.email === ADMIN_EMAIL) return;
    let key = "";
    try { key = sessionStorage.getItem("visitLogged") || ""; } catch {}
    if (key === user.email) return;
    try { sessionStorage.setItem("visitLogged", user.email); } catch {}
    const ua = navigator.userAgent || "";
    addDoc(collection(db, "visitLog"), {
      email: user.email,
      name: user.displayName || "",
      photoURL: user.photoURL || "",
      loginAt: new Date().toISOString(),
      date: todayStr(),
      device: /iphone|ipad/i.test(ua) ? "iPhone/iPad" : /android/i.test(ua) ? "Android" : /mac/i.test(ua) ? "Mac" : /windows/i.test(ua) ? "Windows" : "Other",
    }).catch(() => {});
  }, [user]);

  // Heartbeat every 10 minutes (non-blocking) — keeps write usage safe for 2000+ students
  useEffect(() => {
    if (!user) return;
    const iv = setInterval(() => {
      setDoc(doc(db, "presence", user.email), { lastSeen: new Date().toISOString(), online: true }, { merge: true }).catch(() => {});
    }, 600000);
    return () => clearInterval(iv);
  }, [user]);

  const handleLogin = async () => {
    try {
      const result = await signInWithPopup(auth, googleProvider);
      const email = result.user.email || "";

      // All Firestore writes are non-blocking — they never fail the login
      if (email !== ADMIN_EMAIL) {
        setDoc(doc(db, "users", email), {
          name: result.user.displayName, email,
          lastLogin: new Date().toISOString(),
          photoURL: result.user.photoURL || "",
        }).catch(() => {});
      }

      // Visit is logged by the "log every portal open" effect below

      // Track app installation (non-blocking)
      const trackInstallation = async () => {
        // Create a bound function for AdminPage to use
        const isStandalone = window.navigator.standalone === true ||
                             window.matchMedia("(display-mode: standalone)").matches ||
                             window.matchMedia("(display-mode: fullscreen)").matches;
        if (!isStandalone) return;

        const userAgent = navigator.userAgent;
        const now = new Date().toISOString();
        try {
          const existingSnap = await getDocs(
            query(collection(db, "appInstallations"), where("email", "==", email))
          );
          if (existingSnap.empty) {
            await addDoc(collection(db, "appInstallations"), {
              email,
              name: result.user.displayName || "",
              installedAt: now,
              lastOpenedAt: now,
              openCount: 1,
              userAgent,
              deviceType: /mobile|android|iphone/i.test(userAgent) ? "Mobile" : "Desktop",
            });
          } else {
            const docRef = existingSnap.docs[0].ref;
            const existingData = existingSnap.docs[0].data();
            await updateDoc(docRef, {
              lastOpenedAt: now,
              openCount: (existingData.openCount || 0) + 1,
            });
          }
          logEvent(analytics, "app_installed", {
            email,
            device_type: /mobile|android|iphone/i.test(userAgent) ? "Mobile" : "Desktop",
          });
        } catch(e) {
          console.error("trackAppInstallation error:", e);
        }
      };
      trackInstallation().catch(() => {});
    } catch (e) { alert("Login failed: " + e.message); }
  };

  const handleLogout = async () => {
    if (user) {
      try { await setDoc(doc(db, "presence", user.email), { online: false }, { merge: true }); } catch {}
    }
    signOut(auth); setPage("home");
  };

  // Track simulation access in presence
  const trackSimulationAccess = async (simulationName) => {
    if (!user) return;
    try {
      await setDoc(doc(db, "presence", user.email), {
        email: user.email,
        name: user.displayName || "",
        photoURL: user.photoURL || "",
        currentPage: `Simulation: ${simulationName}`,
        lastSeen: new Date().toISOString(),
        online: true,
      }, { merge: true });
    } catch (e) {
      console.log("Could not track simulation access:", e);
    }
  };

  const handleRequestTeacherAccess = async () => {
    if (!user) return alert("Please login first");
    try {
      await requestTeacherAccess(db, user.email, user.displayName, user.displayName, user.photoURL, "Physics");
      alert("✅ Teacher access request submitted! Admin will review and approve soon.");
      setShowTeacherRequestModal(false);
      // Refresh pending request
      const pending = await getPendingTeacherRequest(db, user.email);
      setPendingTeacherRequest(pending);
    } catch (e) {
      alert("Error: " + e.message);
    }
  };
  if (loading) return <div className="loading">Loading...</div>;

  return (
    <div className="app">
      <header className="header" style={{display: user ? "flex" : "none"}}>
        <div className="header-left" onClick={() => setPage("home")}>
          {!user && <span>Physics Hub - Dr G Devaraju</span>}
          {user && <span>⚛️</span>}
        </div>
        <nav className="header-nav">
          {userRole === "student" && isAllowed && (<>
            <button className={`nav-puc1${page==="puc1"?" nav-active":""}`} onClick={() => setPage("puc1")}>⚛ PUC-I</button>
            <button className={`nav-puc2${page==="puc2"?" nav-active":""}`} onClick={() => setPage("puc2")}>⚛ PUC-II</button>
            <button className={`nav-engphy${page==="engphy"?" nav-active":""}`} onClick={() => setPage("engphy")}>⚛ Engg. Physics</button>
            <button className={`nav-sim${page==="simulations"?" nav-active":""}`} onClick={() => setPage("simulations")}>🔬 Simulations</button>
            <button className={`nav-quiz${page==="quiz"?" nav-active":""}`} onClick={() => setPage("quiz")}>📝 Quizzes</button>
            <button className={`nav-myclasses${page==="myclasses"?" nav-active":""}`} onClick={() => setPage("myclasses")}>📚 My Classes</button>
            <button className={`nav-desmos${page==="desmos"?" nav-active":""}`} onClick={() => setPage("desmos")}>📈 Graphing</button>
            <button className={`nav-about${page==="about"?" nav-active":""}`} onClick={() => setPage("about")}>👨‍🏫 About Prof</button>
            <button className={`nav-ai${page==="aitutor"?" nav-active":""}`} onClick={() => setPage("aitutor")}>🤖 AI Tutor</button>
            <button className={`nav-research${page==="research"?" nav-active":""}`} onClick={() => setPage("research")}>🔭 Research</button>
            <button className={`nav-desmos${page==="mathtools"?" nav-active":""}`} onClick={() => setPage("mathtools")}>📐 Math Tools</button>
            {familyAllowed && <button className={`nav-study${page==="family"?" nav-active":""}`} onClick={() => setPage("family")}>📺 Family Videos</button>}
          </>)}
          {userRole === "teacher" && isAllowed && (<>
            <button className={`nav-classroom${page==="classroom"?" nav-active":""}`} onClick={() => setPage("classroom")}>📚 My Classroom</button>
            <button className={`nav-teacher${page==="teacher"?" nav-active":""}`} onClick={() => setPage("teacher")}>🎓 Teacher Dashboard</button>
            <button className={`nav-quiz${page==="quiz"?" nav-active":""}`} onClick={() => setPage("quiz")}>📝 View Quizzes</button>
          </>)}
          {userRole === "admin" && (<>
            <button className={`nav-puc1${page==="puc1"?" nav-active":""}`} onClick={() => setPage("puc1")}>⚛ PUC-I</button>
            <button className={`nav-puc2${page==="puc2"?" nav-active":""}`} onClick={() => setPage("puc2")}>⚛ PUC-II</button>
            <button className={`nav-engphy${page==="engphy"?" nav-active":""}`} onClick={() => setPage("engphy")}>⚛ Engg. Physics</button>
            <button className={`nav-sim${page==="simulations"?" nav-active":""}`} onClick={() => setPage("simulations")}>🔬 Simulations</button>
            <button className={`nav-quiz${page==="quiz"?" nav-active":""}`} onClick={() => setPage("quiz")}>📝 Quizzes</button>
            <button className={`nav-about${page==="about"?" nav-active":""}`} onClick={() => setPage("about")}>👨‍🏫 About Prof</button>
            <button className={`nav-study${page==="studyguides"?" nav-active":""}`} onClick={() => setPage("studyguides")}>📚 Study Guides</button>
            <button className={`nav-class${page==="onlineclass"?" nav-active":""}`} onClick={() => setPage("onlineclass")}>🎓 Online Class</button>
          </>)}
          {userRole === "admin" && <button className={`nav-classroom${page==="classroom"?" nav-active":""}`} onClick={() => setPage("classroom")}>📚 My Classroom</button>}
          {userRole === "admin" && <button className={`nav-admin${page==="admin"?" nav-active":""}`} onClick={() => setPage("admin")}>🛠 Admin Panel</button>}
          {userRole === "admin" && <button className={`nav-finance${page==="finance"?" nav-active":""}`} onClick={() => setPage("finance")}>💼 Finance</button>}
          {userRole === "admin" && <button className={`nav-study${page==="family"?" nav-active":""}`} onClick={() => setPage("family")}>📺 Family Videos</button>}
        </nav>
        <div className="header-right">
          {user ? (<><span className="user-name">👤 {user.displayName}</span><button className="logout-btn" onClick={handleLogout}>Logout</button></>) :
            <button className="login-btn" onClick={handleLogin}>🔑 Login</button>}
        </div>
      </header>

      {/* Simulations — public, no login required */}
      {!user && page === "simulations" && (
        <main className="main">
          <div style={{ background: "#0d1b2e", padding: "10px 24px", display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: "1px solid rgba(33,150,243,0.2)" }}>
            <span style={{ color: "#64B5F6", fontWeight: 700, fontSize: "1rem", cursor: "pointer" }} onClick={() => setPage("home")}>← Physics Hub</span>
            <button onClick={handleLogin} style={{ background: "linear-gradient(135deg,#1565c0,#42a5f5)", color: "#fff", border: "none", borderRadius: 8, padding: "7px 20px", fontWeight: 700, cursor: "pointer", fontSize: "0.9rem" }}>🔑 Login for Full Access</button>
          </div>
          <SimulationsPage setPage={setPage} userAccess={[]} currentUser={null} isAdmin={false} />
        </main>
      )}
      {!user && page !== "simulations" && page !== "about" && <WelcomePage onEnter={handleLogin} onGoogleSignIn={handleLogin} setPage={setPage} onAbout={() => setPage("about")} />}
      {!user && page === "about" && <main className="main"><AboutPage setPage={setPage} isAdmin={false} /></main>}
      {user && <main className="main">
        {user && (<>
          {page === "home" && <HomePage setPage={setPage} user={user} userRole={userRole} pendingTeacherRequest={pendingTeacherRequest} onRequestTeacher={() => setShowTeacherRequestModal(true)} />}
          {page === "puc1" && <ClassPage pucClass="PUC-I" userAccess={studentAccess} currentUser={user} isAdmin={isAdmin} />}
          {page === "puc2" && <ClassPage pucClass="PUC-II" userAccess={studentAccess} currentUser={user} isAdmin={isAdmin} />}
          {page === "engphy" && <ClassPage pucClass="Engineering Physics" userAccess={studentAccess} currentUser={user} isAdmin={isAdmin} />}
          {page === "simulations" && <SimulationsPage setPage={setPage} userAccess={studentAccess} currentUser={user} isAdmin={isAdmin} trackSimulationAccess={trackSimulationAccess} />}
          {page === "beats" && <BeatsSimulation />}
          {page === "quiz" && <QuizPage user={user} userAccess={studentAccess} role={userRole} />}
          {page === "desmos" && <DesmosPage />}
          {page === "teacher" && userRole === "teacher" && <TeacherDashboard user={user} assignedClasses={teacherAssignedClasses} />}
          {page === "classroom" && (userRole === "teacher" || userRole === "admin") && <FacultyClassroom user={user} isAdmin={isAdmin} />}
          {page === "myclasses" && <StudentMyClasses user={user} setPage={setPage} />}
          {page === "admin" && userRole === "admin" && <AdminPage user={user} />}
          {page === "about" && <AboutPage setPage={setPage} isAdmin={isAdmin} />}
          {page === "studyguides" && <StudyGuides isAdmin={isAdmin} />}
          {page === "family" && (isAdmin || familyAllowed) && <FamilyVideos user={user} isAdmin={isAdmin} />}
          {page === "mathtools" && <MathToolsPage />}
          {page === "research" && <ResearchPage />}
          {page === "finance" && userRole === "admin" && <FinancePage setPage={setPage} />}
          {page === "onlineclass" && <OnlineClassroom isAdmin={isAdmin} userEmail={user?.email} userName={user?.displayName} />}
        </>)}
      </main>}

      {/* Teacher Request Modal */}
      {showTeacherRequestModal && (
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
          <div style={{ background: "#fff", borderRadius: "12px", padding: "30px", maxWidth: "500px", width: "90%", boxShadow: "0 10px 40px rgba(0,0,0,0.3)" }}>
            <h2 style={{ margin: "0 0 20px", color: "#9c27b0" }}>🎓 Request Teacher Access</h2>
            <p style={{ color: "#666", marginBottom: "20px" }}>Submit your request to become a teacher. Admin will review and assign you to classes.</p>

            <div style={{ background: "#f5f5f5", padding: "15px", borderRadius: "8px", marginBottom: "20px" }}>
              <p><strong>Email:</strong> {user?.email}</p>
              <p><strong>Name:</strong> {user?.displayName}</p>
              <p><strong>Department:</strong> Physics</p>
            </div>

            <p style={{ color: "#999", fontSize: "0.9rem", marginBottom: "20px" }}>
              ⏳ After submission, admin will review your request and assign you to classes. You'll be notified once approved.
            </p>

            <div style={{ display: "flex", gap: "10px" }}>
              <button
                onClick={handleRequestTeacherAccess}
                style={{ flex: 1, padding: "12px", background: "#9c27b0", color: "#fff", border: "none", borderRadius: "8px", cursor: "pointer", fontWeight: "bold", fontSize: "1rem" }}
              >
                ✅ Submit Request
              </button>
              <button
                onClick={() => setShowTeacherRequestModal(false)}
                style={{ flex: 1, padding: "12px", background: "#ccc", color: "#000", border: "none", borderRadius: "8px", cursor: "pointer", fontWeight: "bold", fontSize: "1rem" }}
              >
                ❌ Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {user && (
        <footer className="footer">
          <p>© {new Date().getFullYear()} Physics Hub · Dr G Devaraju · All rights reserved</p>
        </footer>
      )}
    </div>
  );
}

function HomePage({ setPage, user, userRole, pendingTeacherRequest, onRequestTeacher }) {
  return (
    <div className="home">
      <div className="home-hero">
        <h1>⚛️ Welcome to Physics Hub</h1>
        <h2 style={{ fontSize: "1.4rem", color: "#ffd600", marginTop: "8px" }}>Dr G Devaraju</h2>
        <p>Your one-stop resource for Physics — notes, assignments & question papers</p>
      </div>
      <div className="class-cards">
        <div className="card" onClick={() => setPage("puc1")}><div className="card-icon">📘</div><h2>PUC - I</h2><p>Reading materials, assignments and question papers for first year</p><button>Explore →</button></div>
        <div className="card" onClick={() => setPage("puc2")}><div className="card-icon">📗</div><h2>PUC - II</h2><p>Reading materials, assignments and question papers for second year</p><button>Explore →</button></div>
        <div className="card" onClick={() => setPage("engphy")}><div className="card-icon">🔬</div><h2>Engineering Physics</h2><p>Reading materials, assignments and question papers for Engineering</p><button>Explore →</button></div>
        <div className="card sim-card" onClick={() => setPage("simulations")}><div className="card-icon">🎮</div><h2>Simulations</h2><p>Interactive PhET simulations for all classes!</p><button>Explore →</button></div>
        <div className="card" onClick={() => setPage("quiz")} style={{ borderTop: "3px solid #7b1fa2" }}><div className="card-icon">📝</div><h2>Quizzes</h2><p>Test your Physics knowledge with timed quizzes!</p><button>Take a Quiz →</button></div>
        <div className="card" onClick={() => setPage("desmos")} style={{ borderTop: "3px solid #e53935" }}><div className="card-icon">📈</div><h2>Desmos</h2><p>Interactive graphing calculator!</p><button>Open →</button></div>

        {/* Faculty Teacher Access Request */}
        {user && userRole === "student" && !pendingTeacherRequest && (
          <div className="card" onClick={onRequestTeacher} style={{ borderTop: "3px solid #9c27b0", cursor: "pointer" }}>
            <div className="card-icon">🎓</div>
            <h2>Faculty Access</h2>
            <p>Request teacher access to manage quizzes and track student performance</p>
            <button>Request Access →</button>
          </div>
        )}

        {/* Show pending request status */}
        {user && pendingTeacherRequest && (
          <div className="card" style={{ borderTop: "3px solid #ff9800" }}>
            <div className="card-icon">⏳</div>
            <h2>Faculty Request</h2>
            <p>Status: <strong>{pendingTeacherRequest.status === "pending" ? "⏳ Pending Approval" : pendingTeacherRequest.status === "approved" ? "✅ Approved" : "❌ Rejected"}</strong></p>
            {pendingTeacherRequest.status === "approved" && <p style={{ color: "#4caf50", marginTop: "8px" }}>Classes: {(pendingTeacherRequest.assignedClasses || []).join(", ")}</p>}
            {pendingTeacherRequest.status === "rejected" && <p style={{ color: "#f44336", marginTop: "8px" }}>{pendingTeacherRequest.rejectionReason || "No reason provided"}</p>}
          </div>
        )}
      </div>
    </div>
  );
}

function ClassPage({ pucClass, userAccess = [], currentUser = null, isAdmin = false }) {
  const [files, setFiles] = useState([]);
  const [openSection, setOpenSection] = useState(null);
  const [loading, setLoading] = useState(true);
  const [classQuizzes, setClassQuizzes] = useState([]);
  const [requestStatus, setRequestStatus] = useState(null); // null | "pending" | "approved" | "rejected"
  const [submittingRequest, setSubmittingRequest] = useState(false);
  // ── Reorder (admin) ──
  const [reorderSection, setReorderSection] = useState(null);
  const [draftOrder, setDraftOrder] = useState([]);
  const [dragIndex, setDragIndex] = useState(null);
  const [overIndex, setOverIndex] = useState(null);
  const [savingOrder, setSavingOrder] = useState(false);

  const startReorder = (section) => {
    setReorderSection(section);
    setDraftOrder(files.filter(f => f.section === section));
  };
  const cancelReorder = () => { setReorderSection(null); setDraftOrder([]); setDragIndex(null); setOverIndex(null); };
  const moveItem = (from, to) => {
    if (to < 0 || to >= draftOrder.length || from === to) return;
    const next = [...draftOrder];
    const [item] = next.splice(from, 1);
    next.splice(to, 0, item);
    setDraftOrder(next);
  };
  const autoArrange = () => setDraftOrder([...draftOrder].sort(autoCompareFiles));
  const saveOrder = async () => {
    setSavingOrder(true);
    try {
      const batch = writeBatch(db);
      draftOrder.forEach((f, i) => batch.update(doc(db, "files", f.id), { order: i }));
      await batch.commit();
      const orderMap = Object.fromEntries(draftOrder.map((f, i) => [f.id, i]));
      setFiles(prev => smartSortFiles(prev.map(f => (f.id in orderMap ? { ...f, order: orderMap[f.id] } : f))));
      cancelReorder();
    } catch (e) { alert("Could not save order: " + e.message); }
    setSavingOrder(false);
  };
  const resetToAuto = async () => {
    if (!window.confirm(`Remove the manual order for "${reorderSection}" and sort automatically by Unit number?`)) return;
    setSavingOrder(true);
    try {
      const batch = writeBatch(db);
      draftOrder.forEach(f => batch.update(doc(db, "files", f.id), { order: deleteField() }));
      await batch.commit();
      const ids = new Set(draftOrder.map(f => f.id));
      setFiles(prev => smartSortFiles(prev.map(f => { if (!ids.has(f.id)) return f; const { order, ...rest } = f; return rest; })));
      cancelReorder();
    } catch (e) { alert("Could not reset order: " + e.message); }
    setSavingOrder(false);
  };
  const sectionIcons = { "Reading Materials": "📄", "Assignments": "📝", "Question Papers": "❓", "Videos": "🎥", "Simulations": "🔬" };

  // Map class name to access code
  const classCode = pucClass === "Engineering Physics" ? "E1" : pucClass === "PUC-I" ? "P1" : "P2";
  const hasAccess = isAdmin || userAccess.includes(classCode);
  const [classSuspended, setClassSuspended] = useState(false);
  useEffect(() => { if (!isAdmin) loadSuspended().then(l => setClassSuspended(l.includes(classCode))); }, [classCode, isAdmin]);

  useEffect(() => { fetchFiles(); if (!hasAccess && currentUser) checkExistingRequest(); }, [pucClass]);

  const checkExistingRequest = async () => {
    if (!currentUser) return;
    try {
      const reqId = `${currentUser.email}_${classCode}`;
      const snap = await getDoc(doc(db, "accessRequests", reqId));
      if (snap.exists()) setRequestStatus(snap.data().status || "pending");
    } catch {}
  };

  const handleRequestAccess = async () => {
    if (!currentUser) return;
    setSubmittingRequest(true);
    try {
      const reqId = `${currentUser.email}_${classCode}`;
      await setDoc(doc(db, "accessRequests", reqId), {
        email: currentUser.email,
        name: currentUser.displayName || "",
        photoURL: currentUser.photoURL || "",
        classCode,
        className: pucClass,
        status: "pending",
        requestedAt: new Date().toISOString(),
      });
      setRequestStatus("pending");
      alert("✅ Access request sent! Dr. Devaraju will review it shortly.");
    } catch (e) { alert("Failed to send request: " + e.message); }
    setSubmittingRequest(false);
  };

  const fetchFiles = async () => {
    setLoading(true);
    try {
      const q = query(collection(db, "files"), where("class", "==", pucClass));
      const snap = await getDocs(q);
      setFiles(smartSortFiles(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
    } catch (e) {
      // Never leave the page stuck on "Loading..." — show the lock / request screen instead.
      console.error("fetchFiles error:", e);
      setFiles([]);
    }
    setLoading(false);
  };
  const toggleSection = s => setOpenSection(openSection === s ? null : s);
  const getYoutubeEmbedUrl = url => {
    if (url.includes("watch?v=")) return url.replace("watch?v=", "embed/");
    if (url.includes("youtu.be/")) return url.replace("youtu.be/", "www.youtube.com/embed/");
    return url;
  };
  if (loading) return <div className="loading">Loading...</div>;

  // ── LOCKED VIEW ──
  if (!hasAccess) {
    const lockSections = ["Reading Materials", "Assignments", "Question Papers", "Videos", "Simulations"];
    const sectionColor = { "Reading Materials": "#e3f2fd", "Assignments": "#fce4ec", "Question Papers": "#f3e5f5", "Videos": "#fff3e0", "Simulations": "#e8f5e9" };
    return (
      <div className="class-page">
        <h1 className="class-title">📚 {pucClass}</h1>
        <div style={{ position: "relative", borderRadius: 16, overflow: "hidden" }}>
          {/* Blurred preview */}
          <div style={{ filter: "blur(4px)", pointerEvents: "none", userSelect: "none", opacity: 0.7 }}>
            {lockSections.map(section => (
              <div key={section} className="chapter-card" style={{ background: sectionColor[section] || "#f5f5f5" }}>
                <div className="chapter-header" style={{ cursor: "default" }}>
                  <span>{sectionIcons[section]} {section}</span><span>▼</span>
                </div>
                <div className="chapter-content" style={{ padding: "16px 20px" }}>
                  {[1,2,3].map(n => (
                    <div key={n} style={{ background: "#e0e0e0", borderRadius: 8, height: 36, marginBottom: 8, width: `${70 + n*8}%` }} />
                  ))}
                </div>
              </div>
            ))}
          </div>
          {/* Lock overlay */}
          <div style={{
            position: "absolute", top: 0, left: 0, right: 0, bottom: 0,
            display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
            background: "rgba(255,255,255,0.6)", backdropFilter: "blur(2px)"
          }}>
            <div style={{ textAlign: "center", background: "#fff", borderRadius: 20, padding: "32px 40px", boxShadow: "0 8px 32px rgba(0,0,0,0.15)", border: "2px solid #e0e0e0", maxWidth: 420 }}>
              <div style={{ fontSize: "3.5rem", marginBottom: 12 }}>🔒</div>
              <h2 style={{ color: "#1a237e", margin: "0 0 10px", fontSize: "1.4rem" }}>Content Locked</h2>
              <p style={{ color: "#555", margin: "0 0 20px", lineHeight: 1.6 }}>
                {classSuspended ? <>Access to <strong>{pucClass}</strong> is currently closed.</> : <>You don't have access to <strong>{pucClass}</strong> content yet.</>}
              </p>
              {!classSuspended && requestStatus === "pending" && (
                <div style={{ background: "#fff8e1", border: "1.5px solid #fdd835", borderRadius: 12, padding: "12px 16px", marginBottom: 16 }}>
                  <p style={{ margin: 0, color: "#f57c00", fontWeight: 700, fontSize: "0.9rem" }}>⏳ Request Pending</p>
                  <p style={{ margin: "4px 0 0", color: "#888", fontSize: "0.82rem" }}>Dr. Devaraju will review your request soon.</p>
                </div>
              )}
              {!classSuspended && requestStatus === "rejected" && (
                <div style={{ background: "#fce4ec", border: "1.5px solid #f48fb1", borderRadius: 12, padding: "12px 16px", marginBottom: 16 }}>
                  <p style={{ margin: 0, color: "#c62828", fontWeight: 700, fontSize: "0.9rem" }}>❌ Request Not Approved</p>
                  <p style={{ margin: "4px 0 0", color: "#888", fontSize: "0.82rem" }}>Contact Dr. Devaraju directly for more info.</p>
                  <a href="mailto:g.devaraju@gmail.com" style={{ color: "#1565c0", fontSize: "0.82rem", fontWeight: 600 }}>g.devaraju@gmail.com</a>
                </div>
              )}
              {!classSuspended && !requestStatus && (
                <button
                  onClick={handleRequestAccess}
                  disabled={submittingRequest}
                  style={{ background: "linear-gradient(135deg,#1565c0,#1b5e20)", color: "#fff", border: "none", borderRadius: 12, padding: "12px 28px", fontWeight: 700, fontSize: "1rem", cursor: "pointer", marginBottom: 16, width: "100%" }}>
                  {submittingRequest ? "⏳ Sending..." : "🙋 Request Access"}
                </button>
              )}
              <p style={{ margin: 0, color: "#aaa", fontSize: "0.75rem" }}>
                Logged in as: {currentUser?.email}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="class-page">
      <h1 className="class-title">📚 {pucClass}</h1>

      {/* ── JNTU Question Bank (Admin Only, Engineering Physics) ── */}
      {isAdmin && pucClass === "Engineering Physics" && (
        <a
          href="/question-bank.html"
          target="_blank"
          rel="noreferrer"
          style={{
            display: "inline-block",
            background: "#8e44ad",
            color: "#fff",
            border: "none",
            padding: "10px 18px",
            borderRadius: 6,
            cursor: "pointer",
            fontWeight: 600,
            fontSize: "0.95rem",
            marginBottom: "12px",
            textDecoration: "none",
            transition: "all 0.3s ease"
          }}
          onMouseEnter={(e) => e.target.style.background = "#7a3a9d"}
          onMouseLeave={(e) => e.target.style.background = "#8e44ad"}
        >
          📚 View JNTU Question Bank (94 Questions)
        </a>
      )}

      {["Reading Materials", "Assignments", "Question Papers", "Videos"].map(section => {
        const sectionFiles = files.filter(f => f.section === section);
        return (
          <div key={section} className="chapter-card">
            <div className="chapter-header" onClick={() => toggleSection(section)}><span>{sectionIcons[section]} {section}</span><span>{openSection === section ? "▲" : "▼"}</span></div>
            {openSection === section && (
              <div className="chapter-content">
                {isAdmin && sectionFiles.length > 1 && reorderSection !== section && (
                  <div className="reorder-bar">
                    <button className="reorder-btn" onClick={() => startReorder(section)}>↕ Change order</button>
                  </div>
                )}
                {reorderSection === section ? (
                  <div className="reorder-panel">
                    <div className="reorder-bar">
                      <span className="reorder-hint">Drag rows, or use the arrows.</span>
                      <button className="reorder-btn" onClick={autoArrange} disabled={savingOrder}>🔢 Auto-arrange (Unit I → V)</button>
                      <button className="reorder-btn" onClick={resetToAuto} disabled={savingOrder} title="Always keep this section auto-sorted by unit number">♻ Always auto-sort</button>
                    </div>
                    <ul className="file-list">
                      {draftOrder.map((f, i) => (
                        <li key={f.id}
                          draggable
                          onDragStart={() => setDragIndex(i)}
                          onDragOver={e => { e.preventDefault(); if (overIndex !== i) setOverIndex(i); }}
                          onDragLeave={() => setOverIndex(null)}
                          onDrop={e => { e.preventDefault(); if (dragIndex !== null) moveItem(dragIndex, i); setDragIndex(null); setOverIndex(null); }}
                          onDragEnd={() => { setDragIndex(null); setOverIndex(null); }}
                          className={`reorder-row${dragIndex === i ? " dragging" : ""}${overIndex === i && dragIndex !== i ? " drag-over" : ""}`}>
                          <span className="drag-handle" title="Drag to move">⠿</span>
                          <span className="reorder-pos">{i + 1}</span>
                          <span className="file-name">{f.name}</span>
                          <span className="reorder-arrows">
                            <button onClick={() => moveItem(i, 0)} disabled={i === 0} title="Move to top">⤒</button>
                            <button onClick={() => moveItem(i, i - 1)} disabled={i === 0} title="Move up">▲</button>
                            <button onClick={() => moveItem(i, i + 1)} disabled={i === draftOrder.length - 1} title="Move down">▼</button>
                            <button onClick={() => moveItem(i, draftOrder.length - 1)} disabled={i === draftOrder.length - 1} title="Move to bottom">⤓</button>
                          </span>
                        </li>
                      ))}
                    </ul>
                    <div className="reorder-bar reorder-actions">
                      <button className="reorder-btn cancel" onClick={cancelReorder} disabled={savingOrder}>Cancel</button>
                      <button className="reorder-btn save" onClick={saveOrder} disabled={savingOrder}>{savingOrder ? "Saving…" : "💾 Save order"}</button>
                    </div>
                  </div>
                ) : sectionFiles.length === 0 ? <p className="empty-section">No {section.toLowerCase()} added yet.</p> : (
                  <ul className="file-list">
                    {sectionFiles.map(f => (
                      <li key={f.id}>
                        {section === "Videos"
                          ? <div className="video-container"><p className="video-title">🎥 {f.name}</p><iframe src={getYoutubeEmbedUrl(f.url)} title={f.name} allowFullScreen className="video-frame" /></div>
                          : (
                          <div className="file-row">
                            <div className="file-row-left">
                              <div className={`file-icon-wrap file-icon-${section === "Reading Materials" ? "read" : section === "Assignments" ? "assign" : "paper"}`}>
                                <svg width="17" height="17" viewBox="0 0 24 24" fill="none">
                                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                  <polyline points="14 2 14 8 20 8" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                  <line x1="16" y1="13" x2="8" y2="13" stroke="white" strokeWidth="1.8" strokeLinecap="round"/>
                                  <line x1="16" y1="17" x2="8" y2="17" stroke="white" strokeWidth="1.8" strokeLinecap="round"/>
                                </svg>
                              </div>
                              <span className="file-name">{f.name}</span>
                            </div>
                            <a href={f.url} target="_blank" rel="noreferrer" className="file-download-btn">
                              <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                                <polyline points="7 10 12 15 17 10" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                                <line x1="12" y1="15" x2="12" y2="3" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
                              </svg>
                              Download
                            </a>
                          </div>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        );
      })}
      <div className="chapter-card">
        <div className="chapter-header" onClick={() => toggleSection("Simulations")}><span>🔬 Simulations</span><span>{openSection === "Simulations" ? "▲" : "▼"}</span></div>
        {openSection === "Simulations" && (
          <div className="chapter-content">
            {files.filter(f => f.section === "Simulations").map(f => <a key={f.id} href={f.url} target="_blank" rel="noreferrer" className="sim-link">🔬 {f.name}</a>)}
            <div className="sim-grid">
              {(SIMULATIONS[pucClass] || []).filter(sim => isAdmin || sim.local || sim.internal).map((sim, i) => (
                sim.internal ?
                  <div key={i} className="sim-card-item" style={{ cursor: "pointer" }} onClick={() => window.dispatchEvent(new CustomEvent("navigate", { detail: sim.url }))}><div className="sim-icon">{sim.icon}</div><div className="sim-info"><strong>{sim.name}</strong><p>{sim.desc}</p></div><span className="sim-open">Open →</span></div> :
                  <a key={i} href={sim.url} target="_blank" rel="noreferrer" className="sim-card-item"><div className="sim-icon">{sim.icon}</div><div className="sim-info"><strong>{sim.name}</strong><p>{sim.desc}</p></div><span className="sim-open">Open →</span></a>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function SimulationsPage({ setPage, userAccess = [], currentUser = null, isAdmin = false, trackSimulationAccess = null }) {
  const [openClass, setOpenClass] = useState("PUC-I");
  const [iframeSim, setIframeSim] = useState(null);
  const [currentSimName, setCurrentSimName] = useState(null);

  // Listen for the in-sim "Back to Simulations" button (posted from the iframe).
  useEffect(() => {
    const onMessage = (e) => {
      if (e?.data?.type === "rgukt-back-to-sims") setIframeSim(null);
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, []);

  // Track simulation access when a simulation is opened
  const handleSimulationClick = (simName) => {
    setCurrentSimName(simName);
    if (trackSimulationAccess) {
      trackSimulationAccess(simName);
    }
  };

  // Admin sees everything; others only see custom/AI sims (no Colorado/PhET)
  const visibleSims = (cls) => (SIMULATIONS[cls] || []).filter(sim => isAdmin || sim.local || sim.internal);

  if (iframeSim) return (
    <div className="class-page">
      <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: 16 }}>
        <button onClick={() => { setIframeSim(null); setCurrentSimName(null); }} style={{ background: "#1a237e", color: "#fff", border: "none", borderRadius: 8, padding: "8px 20px", cursor: "pointer", fontWeight: 700 }}>← Back to Simulations</button>
        {currentSimName && (
          <span style={{ fontSize: "0.9rem", color: "#666", fontWeight: 600 }}>
            📍 Currently viewing: <strong>{currentSimName}</strong>
          </span>
        )}
      </div>
      <iframe src={iframeSim} width="100%" height="850px" style={{ border: "none", borderRadius: "12px", display: "block" }} title="Simulation" allow="microphone" />
    </div>
  );

  return (
    <div className="class-page">
      <h1 className="class-title">🔬 Physics Simulations</h1>
      <p className="sim-subtitle">Interactive physics simulations — for students and educators</p>
      <div className="sim-tabs">{CLASSES.map(cls => <button key={cls} className={openClass === cls ? "active" : ""} onClick={() => setOpenClass(cls)}>{cls}</button>)}</div>
      <div className="sim-grid-page">
        {visibleSims(openClass).map((sim, i) => (
          (sim.locked && !isAdmin) ?
            <div key={i} className="sim-card-big" style={{ position: "relative", cursor: "not-allowed", userSelect: "none" }}>
              <div style={{ opacity: 0.15, pointerEvents: "none" }}>
                <div className="sim-icon-big">{sim.icon}</div>
                <h3>{sim.name}</h3>
                <p>{sim.desc || "Interactive physics simulation"}</p>
                <button style={{ background: "linear-gradient(135deg,#1565c0,#7c4dff)" }}>🚀 Launch →</button>
              </div>
              <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 10 }}>
                <div style={{ fontSize: "2.2rem" }}>🔒</div>
                <div style={{ fontSize: "0.85rem", color: "rgba(255,255,255,0.55)", fontWeight: 700, textAlign: "center", padding: "0 16px" }}>Full Access Required</div>
                <div style={{ fontSize: "0.72rem", color: "rgba(255,255,255,0.30)", textAlign: "center", padding: "0 20px", lineHeight: 1.5 }}>Contact Dr. Devaraju for access</div>
              </div>
            </div> :
          sim.local ?
            <div key={i} className="sim-card-big" onClick={() => { handleSimulationClick(sim.name); setIframeSim(sim.url); }} style={{ cursor: "pointer" }}><div className="sim-icon-big">{sim.icon}</div><h3>{sim.name}</h3><p>{sim.desc}</p><button>🚀 Launch →</button></div> :
          sim.internal ?
            <div key={i} className="sim-card-big" onClick={() => { handleSimulationClick(sim.name); setPage(sim.url); }} style={{ cursor: "pointer" }}><div className="sim-icon-big">{sim.icon}</div><h3>{sim.name}</h3><p>{sim.desc}</p><button>Launch Simulation →</button></div> :
            <a key={i} href={sim.url} target="_blank" rel="noreferrer" className="sim-card-big" onClick={() => handleSimulationClick(sim.name)}><div className="sim-icon-big">{sim.icon}</div><h3>{sim.name}</h3><p>{sim.desc}</p><button>Launch Simulation →</button></a>
        ))}
      </div>
    </div>
  );
}

// ── REPLACE your entire AboutPage function with this ──────────────────────────

function AboutPage({ setPage, isAdmin }) {
  const LOCAL_CV_URL = "/Devaraju_CV_Latest.pdf";
  const MANUSCRIPT_URL = "/MnGaN_final_manuscript_28_04_26.pdf";
  const REVIEWER_URL = "/peer_review_GRAD-2026-0150.pdf";
  const [cvUrl, setCvUrl] = useState(LOCAL_CV_URL);
  const [usingCustom, setUsingCustom] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showPdf, setShowPdf] = useState(false);
  const [showManuscript, setShowManuscript] = useState(false);
  const [showReviewer, setShowReviewer] = useState(false);
  const [cvLoading, setCvLoading] = useState(true);
  const [resetting, setResetting] = useState(false);

  useEffect(() => {
    const loadCv = async () => {
      try {
        const snap = await getDoc(doc(db, "settings", "professorCV"));
        if (snap.exists() && snap.data().useCustom && snap.data().url) {
          setCvUrl(snap.data().url);
          setUsingCustom(true);
        }
      } catch(e) {}
      finally { setCvLoading(false); }
    };
    loadCv();
  }, []);

  const handleCvUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("upload_preset", CLOUDINARY_PRESET);
      const res = await fetch(`https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD}/auto/upload`, { method: "POST", body: formData });
      const data = await res.json();
      if (data.secure_url) {
        setCvUrl(data.secure_url);
        setUsingCustom(true);
        setShowPdf(true);
        await setDoc(doc(db, "settings", "professorCV"), { url: data.secure_url, useCustom: true, updatedAt: new Date().toISOString() });
        alert("✅ CV uploaded and now live for all students!");
      } else throw new Error("Upload failed");
    } catch (err) { alert("❌ CV upload failed: " + err.message); }
    finally { setUploading(false); }
  };

  const handleResetCv = async () => {
    if (!window.confirm("Reset to the default bundled CV?\n\nThis will hide any custom uploaded CV and show the version that came with the portal.")) return;
    setResetting(true);
    try {
      await setDoc(doc(db, "settings", "professorCV"), { useCustom: false, updatedAt: new Date().toISOString() }, { merge: true });
      setCvUrl(LOCAL_CV_URL);
      setUsingCustom(false);
      alert("✅ Reset to default CV. Students will now see the bundled version.");
    } catch (err) { alert("❌ Reset failed: " + err.message); }
    finally { setResetting(false); }
  };

  const sectionTitle = {
    fontSize: "1.05rem", fontWeight: 800, color: "#1a237e",
    marginBottom: 16, paddingBottom: 8,
    borderBottom: "2px solid #e8eaf6", letterSpacing: 0.3
  };

  return (
    <div style={{ maxWidth: 1000, margin: "0 auto", padding: "0 16px 40px" }}>

      {/* ── Hero Banner ── */}
      <div style={{
        background: "linear-gradient(135deg, #1a237e 0%, #283593 60%, #3949ab 100%)",
        borderRadius: 20, padding: "36px 40px", marginBottom: 28,
        display: "flex", alignItems: "center", gap: 32, flexWrap: "wrap",
        boxShadow: "0 8px 32px rgba(26,35,126,0.25)"
      }}>
        <img src="/professor1.png" alt="Dr G Devaraju" style={{
          width: 200, height: 200, borderRadius: "50%", objectFit: "cover",
          border: "4px solid #fdd835", boxShadow: "0 4px 16px rgba(0,0,0,0.3)", flexShrink: 0
        }} />
        <div style={{ flex: 1, minWidth: 220 }}>
          <h1 style={{ color: "#fff", fontSize: "2rem", fontWeight: 900, margin: "0 0 4px" }}>Dr. G. Devaraju</h1>
          <p style={{ color: "#fdd835", fontSize: "1rem", fontWeight: 700, margin: "0 0 4px" }}>
            Assistant Professor & Head of Department — Physics
          </p>
          <p style={{ color: "#c5cae9", fontSize: "0.85rem", margin: "0 0 6px", fontStyle: "italic" }}>
            Associate Dean · IQAC Coordinator · Nodal Officer, PM-USHA · Program Coordinator, Education Week 2026
          </p>
          <p style={{ color: "#c5cae9", fontSize: "0.88rem", margin: "0 0 16px" }}>
            Educator and Content Creator
          </p>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {["🎓 PhD · UoH", "🔬 Postdoc · Padova", "📚 Educator", "💻 EdTech Builder", "🌐 Portal Founder", "🏛️ HoD Physics"].map((t, i) => (
              <span key={i} style={{
                background: "rgba(255,255,255,0.15)", color: "#fff",
                borderRadius: 20, padding: "4px 14px", fontSize: "0.78rem", fontWeight: 600,
                border: "1px solid rgba(255,255,255,0.25)"
              }}>{t}</span>
            ))}
          </div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 8, minWidth: 200 }}>
          {[
            { icon: "📧", label: "g.devaraju@gmail.com",    href: "mailto:g.devaraju@gmail.com" },
            { icon: "📱", label: "+91 9440880309",           href: "tel:+919440880309" },
            { icon: "💼", label: "LinkedIn Profile",         href: "https://in.linkedin.com/in/dr-g-devaraju-ph-d-77276531" },
            { icon: "🌐", label: "Physics Hub Portal",       href: "https://physics-portal-app.web.app" },
          ].map((c, i) => (
            <a key={i} href={c.href} target="_blank" rel="noreferrer" style={{
              color: "#c5cae9", fontSize: "0.82rem", textDecoration: "none",
              display: "flex", alignItems: "center", gap: 6
            }}><span>{c.icon}</span> {c.label}</a>
          ))}
        </div>
      </div>

      {/* ── Row 1: Biography + Education ── */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 20 }}>

        <div style={{ background: "#fff", borderRadius: 16, padding: 24, boxShadow: "0 2px 12px rgba(0,0,0,0.07)", border: "1px solid #e8eaf6" }}>
          <h3 style={sectionTitle}>📖 Biography</h3>
          <p style={{ color: "#444", lineHeight: 1.8, fontSize: "0.92rem", margin: "0 0 12px" }}>
            Dr. G. Devaraju is an accomplished Physics educator with expertise in PUC-I, PUC-II, and Engineering Physics. His research focuses on <strong>GaN:Mn (Gallium Nitride doped with Manganese)</strong> systems and semiconductor spintronics. He holds a PhD from the University of
            Hyderabad and completed two postdoctoral fellowships at Dipartimento di Fisica e Astronomia "Galileo Galilei", Padova, Italy
            under Young Researcher Fellowships.
          </p>
          <p style={{ color: "#444", lineHeight: 1.8, fontSize: "0.92rem", margin: 0 }}>
            He is the creator of the <strong style={{ color: "#1a237e" }}>Physics Hub</strong> —
            a full-stack interactive learning portal featuring AI tutoring, PhET simulations, quizzes,
            assignments, and student analytics — recognised by institutional leadership.
          </p>
        </div>

        <div style={{ background: "#fff", borderRadius: 16, padding: 24, boxShadow: "0 2px 12px rgba(0,0,0,0.07)", border: "1px solid #e8eaf6" }}>
          <h3 style={sectionTitle}>🎓 Education & Career</h3>
          {[
            { dot: "#1565c0", year: "2012–Present", title: "Physics Educator & Department Head", sub: "PUC-I, PUC-II & Engineering Physics" },
            { dot: "#6a1b9a", year: "2013–14, 2015–16", title: "Postdoctoral Fellow, Dipartimento di Fisica e Astronomia Galileo Galilei,Univ. of Padova, Italy", sub: "Young Researcher Fellowship · Supervisor: Prof. G. Mattei" },
            { dot: "#2e7d32", year: "2012", title: "PhD in Physics — University of Hyderabad", sub: "SHI effects in III-V Semiconductors · Supervisor: Prof. A. P. Pathak" },
            { dot: "#e65100", year: "2003", title: "MSc in Physics — University of Hyderabad", sub: "8th Rank in National Level Entrance Test 2001" },
          ].map((item, i, arr) => (
            <div key={i} style={{ display: "flex", gap: 14, marginBottom: i < arr.length - 1 ? 14 : 0 }}>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                <div style={{ width: 12, height: 12, borderRadius: "50%", background: item.dot, marginTop: 4, flexShrink: 0 }} />
                {i < arr.length - 1 && <div style={{ width: 2, flex: 1, background: "#e8eaf6", margin: "4px 0" }} />}
              </div>
              <div>
                <span style={{ fontSize: "0.75rem", color: item.dot, fontWeight: 700 }}>{item.year}</span>
                <p style={{ margin: "2px 0 0", fontWeight: 700, color: "#222", fontSize: "0.88rem" }}>{item.title}</p>
                <p style={{ margin: "2px 0 0", color: "#777", fontSize: "0.8rem" }}>{item.sub}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Research Interests (full width) ── */}
      <div style={{ background: "#fff", borderRadius: 16, padding: 24, boxShadow: "0 2px 12px rgba(0,0,0,0.07)", border: "1px solid #e8eaf6", marginBottom: 20 }}>
        <h3 style={sectionTitle}>🔬 Research Interests & Expertise</h3>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 14 }}>
          {[
            { icon: "⚛️", bg: "#e3f2fd", tc: "#1565c0",
              title: "Swift Heavy Ion (SHI) Physics",
              desc: "Ion-beam irradiation effects in III-V semiconductors (GaN, AlGaN, InGaAs/InP) — structural, optical & electronic property modifications" },
            { icon: "🔭", bg: "#f3e5f5", tc: "#6a1b9a",
              title: "Nanophotonics & Plasmonics",
              desc: "Energy transfer from Au nanoclusters to Er³⁺ ions; optical properties of metal nanostructures; plasmonic thin films (Padova)" },
            { icon: "💎", bg: "#e8f5e9", tc: "#2e7d32",
              title: "Nanocrystal Synthesis & Characterization",
              desc: "Ge nanocrystals via RF sputtering, microwave & RTA; HRXRD, Raman, PL, AFM, TEM and RBS characterization techniques" },
            { icon: "🌀", bg: "#fff3e0", tc: "#e65100",
              title: "Topological Materials (Current)",
              desc: "Berry curvature, Weyl semimetals, nonlinear Hall transport, Fermi surface topology — tight-binding & DFT computational approaches" },
            { icon: "📐", bg: "#e0f2f1", tc: "#004d40",
              title: "Computational Physics (Emerging)",
              desc: "Tight-binding band structure calculations, Fermi surface visualization — transitioning from experimental to computational/theory" },
            { icon: "🖥️", bg: "#fce4ec", tc: "#880e4f",
              title: "Physics Education Technology",
              desc: "Interactive HTML5 simulations, AI-powered tutoring systems, web-based learning portals — creator of Physics Hub" },
          ].map((r, i) => (
            <div key={i} style={{ background: r.bg, borderRadius: 12, padding: "14px 16px" }}>
              <p style={{ margin: "0 0 6px", fontSize: "1.4rem" }}>{r.icon}</p>
              <p style={{ margin: "0 0 4px", fontWeight: 800, color: r.tc, fontSize: "0.88rem" }}>{r.title}</p>
              <p style={{ margin: 0, color: "#555", fontSize: "0.8rem", lineHeight: 1.6 }}>{r.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* ── Row 3: Awards + Admin ── */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 20 }}>

        <div style={{ background: "#fff", borderRadius: 16, padding: 24, boxShadow: "0 2px 12px rgba(0,0,0,0.07)", border: "1px solid #e8eaf6" }}>
          <h3 style={sectionTitle}>🏅 Awards, Grants & Fellowships</h3>
          {[
            { badge: "🥇", bg: "#fff8e1", text: "8th Rank — National Level Test 2001", sub: "M.Sc. Physics, University of Hyderabad" },
            { badge: "🥈", bg: "#fff8e1", text: "7th Rank — National Level Test 2001", sub: "M.Sc. Tech, NIT Warangal" },
            { badge: "🌟", bg: "#e8f5e9", text: "Young Researcher Fellowship ×2", sub: "University of Padova, Italy (2013–14 & 2015–16)" },
            { badge: "✈️", bg: "#e3f2fd", text: "DST Young Scientist Travel Grant", sub: "MRS Spring Meeting 2011, San Francisco, USA" },
            { badge: "✈️", bg: "#e3f2fd", text: "DST-PURSE Travel Grant", sub: "ICACS-24, Krakow, Poland (2010)" },
            { badge: "🔬", bg: "#f3e5f5", text: "CSIR–Senior Research Fellowship (SRF)", sub: "2010–2012" },
            { badge: "📚", bg: "#f3e5f5", text: "RFSMS Fellowship — UGC", sub: "Centre for Advanced Studies, UoH" },
            { badge: "🎤", bg: "#e0f2f1", text: "Invited Talk — WINS Workshop 2025", sub: "Ion/Laser Beams in Nano-Science, Feb 18–20" },
          ].map((a, i) => (
            <div key={i} style={{
              display: "flex", gap: 10, alignItems: "flex-start",
              background: a.bg, borderRadius: 8, padding: "8px 12px", marginBottom: 8
            }}>
              <span style={{ fontSize: "1.1rem", flexShrink: 0 }}>{a.badge}</span>
              <div>
                <p style={{ margin: 0, fontWeight: 700, color: "#222", fontSize: "0.85rem" }}>{a.text}</p>
                <p style={{ margin: 0, color: "#777", fontSize: "0.78rem" }}>{a.sub}</p>
              </div>
            </div>
          ))}
        </div>

        <div style={{ background: "#fff", borderRadius: 16, padding: 24, boxShadow: "0 2px 12px rgba(0,0,0,0.07)", border: "1px solid #e8eaf6" }}>
          <h3 style={sectionTitle}>🏛️ Administrative Positions</h3>
          {[
            { period: "May 2026",         role: "Program Coordinator, Education Week-2026 (11–17 May 2026)", active: true  },
            { period: "2026",             role: "Convener, National Science Day Celebrations",                active: true  },
            { period: "2024–Present",     role: "Head of Department, Physics (since 10 Aug 2024)",            active: true  },
            { period: "2023–Present",     role: "Coordinator, External Funding & Linkages (since 9 Apr 2023)",active: true  },
            { period: "2023–Present",     role: "Nodal Officer, PM-USHA",                                     active: true  },
            { period: "2025",             role: "Co-Convener, UG-Admissions 2025",                            active: false },
            { period: "2022–2023",        role: "Academic Coordinator (1 Aug 2022 – 8 Apr 2023)",             active: false },
            { period: "2019–2022",        role: "Associate Dean, Science & Humanities (26 Aug 2019 – 31 Jul 2022)", active: false },
            { period: "2016–2020",        role: "Grievance Redressal Cell (23 Nov 2016 – 1 Aug 2020)",        active: false },
            { period: "2017",             role: "Co-Convener, Intl. Conf. on Advanced Functional Materials",  active: false },
            { period: "2012–2013",        role: "Physics Coordinator",       active: false },
          ].map((r, i, arr) => (
            <div key={i} style={{
              display: "flex", alignItems: "center", gap: 10,
              padding: "8px 0", borderBottom: i < arr.length - 1 ? "1px solid #f0f0f0" : "none"
            }}>
              <span style={{
                minWidth: 88, fontSize: "0.72rem", fontWeight: 700, textAlign: "center",
                color: r.active ? "#2e7d32" : "#1565c0",
                background: r.active ? "#e8f5e9" : "#e3f2fd",
                borderRadius: 12, padding: "3px 8px"
              }}>{r.period}</span>
              <span style={{ color: "#333", fontSize: "0.84rem", lineHeight: 1.4 }}>{r.role}</span>
            </div>
          ))}
        </div>
      </div>

      {/* ── Oral Presentations & Invited Talks (full width) ── */}
      <div style={{ background: "#fff", borderRadius: 16, padding: 24, boxShadow: "0 2px 12px rgba(0,0,0,0.07)", border: "1px solid #e8eaf6", marginBottom: 20 }}>
        <h3 style={sectionTitle}>🎤 Oral Presentations & Invited Talks</h3>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 12 }}>
          {[
            { date: "16 May 2026", title: "AI in Physics Teaching", venue: "Education Week-2026", type: "Lecture", color: "#2e7d32", bg: "#e8f5e9" },
            { date: "18–20 Feb 2025", title: "Invited Talk — Ion/Laser Beams in Nano-Science (WINS)", venue: "Workshop on WINS", type: "Invited", color: "#6a1b9a", bg: "#f3e5f5" },
            { date: "1 Jul 2023", title: "Preparation of Research Proposal", venue: "Research & Development Workshop", type: "Lecture", color: "#1565c0", bg: "#e3f2fd" },
            { date: "30 Jan 2023", title: "Recent Trends in Emerging Materials", venue: "National Science Day 2023", type: "Lecture", color: "#1565c0", bg: "#e3f2fd" },
            { date: "15–16 Mar 2019", title: "Advances in Mech. Engg. & Materials Characterization", venue: "PVP Siddhartha Inst. of Technology, Vijayawada", type: "Conf.", color: "#e65100", bg: "#fff3e0" },
            { date: "Apr 2011", title: "MRS Spring Meeting 2011", venue: "San Francisco, USA", type: "Conf.", color: "#880e4f", bg: "#fce4ec" },
            { date: "Jul 2010", title: "ICACS-24, Atomic Collisions in Solids", venue: "Krakow, Poland", type: "Conf.", color: "#880e4f", bg: "#fce4ec" },
          ].map((t, i) => (
            <div key={i} style={{ background: t.bg, borderRadius: 10, padding: "12px 14px", borderLeft: `3px solid ${t.color}` }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                <span style={{ fontSize: "0.74rem", color: t.color, fontWeight: 700 }}>{t.date}</span>
                <span style={{ background: t.color, color: "#fff", borderRadius: 12, padding: "2px 8px", fontSize: "0.7rem", fontWeight: 700 }}>{t.type}</span>
              </div>
              <p style={{ margin: "0 0 3px", fontWeight: 700, color: "#1a237e", fontSize: "0.85rem", lineHeight: 1.4 }}>{t.title}</p>
              <p style={{ margin: 0, color: "#666", fontSize: "0.78rem" }}>{t.venue}</p>
            </div>
          ))}
        </div>
      </div>

      {/* ── Technical Achievement Spotlight ── */}
      <div style={{
        background: "linear-gradient(135deg, #1a237e 0%, #283593 60%, #3949ab 100%)",
        borderRadius: 16, padding: "22px 26px", marginBottom: 20,
        boxShadow: "0 4px 20px rgba(26,35,126,0.20)", color: "#fff"
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 10 }}>
          <span style={{ fontSize: "2rem" }}>🚀</span>
          <h3 style={{ margin: 0, fontSize: "1.05rem", fontWeight: 800, color: "#fdd835", letterSpacing: 0.3 }}>
            Technical Achievement — Physics Hub
          </h3>
        </div>
        <p style={{ margin: "0 0 10px", color: "#e8eaf6", lineHeight: 1.7, fontSize: "0.92rem" }}>
          Designed, developed and deployed the <strong style={{ color: "#fff" }}>Physics Hub</strong> —
          a full-stack web application (React + Firebase) serving <strong style={{ color: "#fdd835" }}>1,500+ students</strong> across
          PUC-I, PUC-II and Engineering Physics. Includes interactive simulations, AI tutoring, quizzes, attendance,
          and analytics. Launched <strong style={{ color: "#fdd835" }}>April 2026</strong> with Android TWA implementation.
        </p>
        <a href="https://physics-portal-app.web.app" target="_blank" rel="noreferrer" style={{
          display: "inline-block", background: "#fdd835", color: "#1a237e",
          borderRadius: 8, padding: "8px 16px", fontWeight: 800, fontSize: "0.85rem",
          textDecoration: "none"
        }}>🌐 Visit physics-portal-app.web.app</a>
      </div>

      {/* ── Publications (full width) ── */}
      <div style={{ background: "#fff", borderRadius: 16, padding: 24, boxShadow: "0 2px 12px rgba(0,0,0,0.07)", border: "1px solid #e8eaf6", marginBottom: 20 }}>
        <h3 style={sectionTitle}>
          📄 Selected Publications
          <span style={{ fontWeight: 400, fontSize: "0.82rem", color: "#999", marginLeft: 8 }}>
            19 journal papers · 7 conference proceedings · 1 book
          </span>
        </h3>

        {/* ── Admin-Only: Recent GaN:Mn Manuscript ── */}
        {isAdmin && (
          <div style={{
            background: "linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%)",
            borderRadius: 10,
            padding: "16px",
            marginBottom: "16px",
            border: "2px solid #ffb74d",
            position: "relative"
          }}>
            <div style={{ position: "absolute", top: 8, right: 8, background: "#ff6f00", color: "#fff", padding: "4px 10px", borderRadius: 20, fontSize: "0.7rem", fontWeight: 700 }}>🔒 PRIVATE</div>
            <div style={{ display: "flex", alignItems: "flex-start", gap: 14 }}>
              <span style={{ fontSize: "2rem" }}>📄</span>
              <div style={{ flex: 1 }}>
                <p style={{ margin: "0 0 6px", color: "#e65100", fontWeight: 800, fontSize: "0.9rem" }}>🔬 RECENT MANUSCRIPT (2026)</p>
                <p style={{ margin: "0 0 6px", color: "#bf360c", fontWeight: 700, fontSize: "0.95rem", lineHeight: 1.4 }}>GaN:Mn Semiconductor Spintronics: Electronic Structure & Magnetic Properties</p>
                <p style={{ margin: "0 0 12px", color: "#666", fontSize: "0.85rem" }}>G. Devaraju, A. P. Pathak et al. | April 2026 | Under Review</p>
                <button
                  onClick={() => setShowManuscript(!showManuscript)}
                  style={{
                    background: "#ff6f00",
                    color: "#fff",
                    border: "none",
                    padding: "8px 16px",
                    borderRadius: 6,
                    fontWeight: 700,
                    fontSize: "0.85rem",
                    cursor: "pointer"
                  }}
                >
                  {showManuscript ? "🔼 Hide Manuscript" : "👁️ View Manuscript"}
                </button>
              </div>
            </div>
            {showManuscript && (
              <div style={{
                background: "#fff",
                borderRadius: 10,
                padding: "16px",
                marginTop: "12px",
                boxShadow: "0 2px 12px rgba(0,0,0,0.1)",
                border: "2px solid #ff6f00"
              }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
                  <p style={{ margin: 0, color: "#ff6f00", fontWeight: 700, fontSize: "0.9rem" }}>📄 GaN:Mn Manuscript — April 2026</p>
                  <a
                    href="/MnGaN_final_manuscript_28_04_26.pdf"
                    download="MnGaN_final_manuscript_28_04_26.pdf"
                    style={{
                      background: "#ff6f00",
                      color: "#fff",
                      padding: "6px 14px",
                      borderRadius: 6,
                      textDecoration: "none",
                      fontWeight: 600,
                      fontSize: "0.8rem",
                      cursor: "pointer",
                      transition: "all 0.3s ease"
                    }}
                    onMouseEnter={(e) => e.target.style.background = "#e65100"}
                    onMouseLeave={(e) => e.target.style.background = "#ff6f00"}
                  >
                    📥 Download PDF
                  </a>
                </div>
                <iframe
                  src="/MnGaN_final_manuscript_28_04_26.pdf"
                  width="100%"
                  height="600px"
                  style={{ border: "1px solid #ddd", borderRadius: 8 }}
                  title="GaN:Mn Manuscript"
                />
              </div>
            )}
          </div>
        )}

        {/* ── Peer Review Section (Admin Only) ── */}
        {isAdmin && (
          <>
            <button
              onClick={() => setShowReviewer(!showReviewer)}
              style={{
                background: "#2196f3",
                color: "#fff",
                border: "none",
                padding: "10px 18px",
                borderRadius: 6,
                cursor: "pointer",
                fontWeight: 600,
                fontSize: "0.95rem",
                marginBottom: "12px"
              }}
            >
              {showReviewer ? "🔼 Hide Comments" : "📋 View Comments"}
            </button>
            {showReviewer && (
              <div style={{
                background: "#fff",
                borderRadius: 10,
                padding: "16px",
                marginBottom: "16px",
                boxShadow: "0 2px 12px rgba(0,0,0,0.1)",
                border: "2px solid #2196f3"
              }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
                  <p style={{ margin: 0, color: "#2196f3", fontWeight: 700, fontSize: "0.9rem" }}>📋 Peer Review — GRAD-2026-0150</p>
                  <a
                    href={REVIEWER_URL}
                    download="peer_review_GRAD-2026-0150.pdf"
                    style={{
                      background: "#2196f3",
                      color: "#fff",
                      padding: "6px 14px",
                      borderRadius: 6,
                      textDecoration: "none",
                      fontWeight: 600,
                      fontSize: "0.8rem",
                      cursor: "pointer",
                      transition: "all 0.3s ease"
                    }}
                    onMouseEnter={(e) => e.target.style.background = "#1565c0"}
                    onMouseLeave={(e) => e.target.style.background = "#2196f3"}
                  >
                    📥 Download PDF
                  </a>
                </div>
                <iframe
                  src={REVIEWER_URL}
                  width="100%"
                  height="600px"
                  style={{ border: "1px solid #ddd", borderRadius: 8 }}
                  title="Peer Review"
                />
              </div>
            )}
          </>
        )}

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          {[
            { authors: "T. Cesca, K. Boris, N. Michieli, G. Devaraju et al.",
              title: "Energy-transfer from ultra-small Au nanoclusters to Er³⁺ ions: a short-range mechanism",
              journal: "Phys. Chem. Chem. Phys.", info: "16, 15158 (2014)" },
            { authors: "G. Devaraju, A. P. Pathak et al.",
              title: "Ion beam treated strained AlGaN/GaN multi quantum wells: HAADF-STEM, HRTEM, Raman and HRXRD",
              journal: "Radiation Effects & Defects in Solids", info: "167, 612 (2012)" },
            { authors: "G. Devaraju, A. P. Pathak, N. Sathish et al.",
              title: "Effects of swift heavy ion irradiation on band gap of strained AlGaN/GaN multi quantum wells",
              journal: "Nucl. Instr. and Meth. B", info: "268, 3001 (2010)" },
            { authors: "G. Devaraju, A. P. Pathak, N. Srinivasa Rao, V. Saikiran et al.",
              title: "Photoluminescence studies in 80 MeV Ni ions irradiated MOCVD grown GaN",
              journal: "Nucl. Instr. and Meth. B", info: "269, 1925 (2011)" },
            { authors: "G. Devaraju, S. Dhamodaran, A. P. Pathak et al.",
              title: "Ion beam modification of strained InGaAs/InP characterized by HRXRD, PL and AFM",
              journal: "Nucl. Instr. and Meth. B", info: "266, 3552 (2008)" },
            { authors: "G. Devaraju, A. P. Pathak et al.",
              title: "Synthesis and tailoring of GaN nanoparticles by RF magnetron sputtering at room temperature",
              journal: "Radiation Effects & Defects in Solids", info: "167, 659 (2012)" },
          ].map((p, i) => (
            <div key={i} style={{ background: "#f8f9ff", borderRadius: 10, padding: "12px 14px", borderLeft: "3px solid #3949ab" }}>
              <p style={{ margin: "0 0 3px", color: "#888", fontSize: "0.78rem" }}>{p.authors}</p>
              <p style={{ margin: "0 0 4px", color: "#1a237e", fontWeight: 700, fontSize: "0.85rem", lineHeight: 1.4 }}>{p.title}</p>
              <p style={{ margin: 0, color: "#888", fontSize: "0.78rem" }}><em>{p.journal}</em> · {p.info}</p>
            </div>
          ))}
        </div>
        <div style={{ marginTop: 14, background: "linear-gradient(90deg,#e3f2fd,#e8eaf6)", borderRadius: 10, padding: "12px 16px", display: "flex", alignItems: "center", gap: 14 }}>
          <span style={{ fontSize: "2rem" }}>📗</span>
          <div>
            <p style={{ margin: "0 0 2px", fontWeight: 800, color: "#1a237e", fontSize: "0.9rem" }}>Book — Lambert Academic Publishing (2016)</p>
            <p style={{ margin: 0, color: "#555", fontSize: "0.85rem" }}>
              <em>Swift Heavy Ion Induced Effects in III-V Compound Semiconductors</em> — Devaraju Gurram & Anand Pathak
            </p>
          </div>
        </div>
      </div>

      {/* ── Memberships + CV ── */}
      <div style={{ display: "grid", gridTemplateColumns: "300px 1fr", gap: 20, marginBottom: 20 }}>

        <div style={{ background: "#fff", borderRadius: 16, padding: 24, boxShadow: "0 2px 12px rgba(0,0,0,0.07)", border: "1px solid #e8eaf6" }}>
          <h3 style={sectionTitle}>🤝 Memberships</h3>
          {[
            { flag: "🇮🇹", name: "Società Italiana di Ottica e Fotonica", sub: "Italian Branch, European Optical Society" },
            { flag: "🇮🇳", name: "Ion Beam Society of India", sub: "National research society" },
          ].map((m, i) => (
            <div key={i} style={{ display: "flex", gap: 10, marginBottom: 14 }}>
              <span style={{ fontSize: "1.5rem" }}>{m.flag}</span>
              <div>
                <p style={{ margin: 0, fontWeight: 700, color: "#222", fontSize: "0.85rem" }}>{m.name}</p>
                <p style={{ margin: 0, color: "#888", fontSize: "0.78rem" }}>{m.sub}</p>
              </div>
            </div>
          ))}
          <div style={{ marginTop: 8, paddingTop: 14, borderTop: "1px solid #e8eaf6" }}>
            <p style={{ margin: "0 0 8px", fontWeight: 700, color: "#1a237e", fontSize: "0.88rem" }}>🌍 Countries Visited</p>
            {["🇮🇹 Italy", "🇵🇱 Poland", "🇺🇸 USA"].map((c, i) => (
              <span key={i} style={{ display: "inline-block", background: "#e3f2fd", color: "#1565c0", borderRadius: 20, padding: "3px 12px", fontSize: "0.82rem", fontWeight: 600, marginRight: 6, marginBottom: 4 }}>{c}</span>
            ))}
          </div>
        </div>

        <div style={{ background: "#fff", borderRadius: 16, padding: 24, boxShadow: "0 2px 12px rgba(0,0,0,0.07)", border: "1px solid #e8eaf6" }}>
          <h3 style={sectionTitle}>📁 Curriculum Vitae</h3>
          {cvLoading ? (
            <p style={{ color: "#aaa" }}>⏳ Loading...</p>
          ) : (
            <div>
              {isAdmin && (
                <div style={{
                  background: usingCustom ? "#fff3e0" : "#e8f5e9",
                  border: `1px solid ${usingCustom ? "#ffb74d" : "#a5d6a7"}`,
                  borderRadius: 8, padding: "8px 12px", marginBottom: 12,
                  fontSize: "0.78rem", color: usingCustom ? "#e65100" : "#2e7d32", fontWeight: 600
                }}>
                  {usingCustom
                    ? "🔶 Showing custom uploaded CV (overrides bundled version)"
                    : "✅ Showing the bundled CV that came with the portal"}
                </div>
              )}
              <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
                <button onClick={() => setShowPdf(!showPdf)} style={{
                  background: showPdf ? "#e8eaf6" : "#1a237e", color: showPdf ? "#1a237e" : "#fff",
                  border: "none", borderRadius: 10, padding: "10px 20px", fontWeight: 700, cursor: "pointer"
                }}>{showPdf ? "🔼 Hide CV" : "👁 View CV"}</button>
                <a href={cvUrl} target="_blank" rel="noreferrer" style={{
                  background: "#2e7d32", color: "#fff", borderRadius: 10, padding: "10px 20px",
                  fontWeight: 700, textDecoration: "none"
                }}>⬇️ Download CV</a>
                {isAdmin && (
                  <label style={{ background: "#e65100", color: "#fff", borderRadius: 10, padding: "10px 16px", fontWeight: 700, cursor: uploading ? "wait" : "pointer", opacity: uploading ? 0.7 : 1 }}>
                    {uploading ? "⏳ Uploading..." : (usingCustom ? "🔄 Replace CV" : "📤 Upload New CV")}
                    <input type="file" accept=".pdf" onChange={handleCvUpload} style={{ display: "none" }} disabled={uploading} />
                  </label>
                )}
                {isAdmin && usingCustom && (
                  <button onClick={handleResetCv} disabled={resetting} style={{
                    background: "#fff", color: "#c62828", border: "1.5px solid #c62828",
                    borderRadius: 10, padding: "10px 16px", fontWeight: 700, cursor: resetting ? "wait" : "pointer"
                  }}>{resetting ? "⏳" : "↺ Reset to Default"}</button>
                )}
              </div>
              {showPdf && (
                <iframe src={cvUrl} width="100%" height="520px"
                  style={{ border: "1px solid #e8eaf6", borderRadius: 12 }} title="CV" />
              )}
            </div>
          )}
        </div>
      </div>

      <div style={{ textAlign: "center" }}>
        <button className="back-btn" onClick={() => setPage("home")}>← Back to Home</button>
      </div>

    </div>
  );
}
function DesmosPage() {
  const [tab, setTab] = useState("plotter");  // "plotter" | "desmos"
  return (
    <div className="class-page">
      <div style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap" }}>
        <button onClick={() => setTab("plotter")} style={{
          background: tab === "plotter" ? "#1a237e" : "#fff",
          color: tab === "plotter" ? "#fff" : "#1a237e",
          border: "1.5px solid #1a237e", borderRadius: 8,
          padding: "8px 16px", fontWeight: 700, cursor: "pointer", fontSize: "0.9rem"
        }}>⚛️ Physics Plotter <span style={{ background: "#fdd835", color: "#1a237e", borderRadius: 8, padding: "1px 6px", fontSize: "0.65rem", marginLeft: 5 }}>NEW</span></button>
        <button onClick={() => setTab("desmos")} style={{
          background: tab === "desmos" ? "#1a237e" : "#fff",
          color: tab === "desmos" ? "#fff" : "#1a237e",
          border: "1.5px solid #1a237e", borderRadius: 8,
          padding: "8px 16px", fontWeight: 700, cursor: "pointer", fontSize: "0.9rem"
        }}>📈 Desmos</button>
      </div>

      {tab === "plotter" && <PhysicsPlotterPage />}
      {tab === "desmos" && (
        <>
          <h1 className="class-title">📈 Desmos Graphing Calculator</h1>
          <div className="desmos-tips"><span>💡 Try: y = A·sin(Bx)</span><span>💡 Try: y = ut + 0.5at²</span><span>💡 Try: x² + y² = r²</span></div>
          <div className="desmos-container"><iframe src="https://www.desmos.com/calculator" title="Desmos" className="desmos-frame" allowFullScreen /></div>
        </>
      )}
    </div>
  );
}

// ── RESEARCH PAGE ───────────────────────────────────────────────────────────
function ResearchPage() {
  const [activeTab, setActiveTab] = useState("xrr");

  const tools = [
    {
      id: "xrr",
      icon: "🌐",
      name: "XRR Simulation",
      fullName: "X-Ray Reflectivity (XRR)",
      desc: "Simulate X-ray reflectivity from thin film multilayer systems. Adjust layer thickness, density and roughness to match experimental data.",
      url: "/simulations/plugplay/xrr_simulator.html",
      tag: "Thin Films · Synchrotron",
    },
  ];

  const active = tools.find(t => t.id === activeTab);

  return (
    <div className="class-page">
      <h1 className="class-title">🔭 Research Tools</h1>
      <p style={{ color: "#546e7a", marginBottom: 20, fontSize: "0.95rem" }}>
        Advanced computational tools for research-grade physics experiments and data analysis.
      </p>

      {/* Tool tabs */}
      <div style={{ display: "flex", gap: 12, marginBottom: 20, flexWrap: "wrap" }}>
        {tools.map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)} style={{
            background: activeTab === t.id ? "linear-gradient(135deg,#1a237e,#283593)" : "#f5f7ff",
            color: activeTab === t.id ? "#fff" : "#1a237e",
            border: `2px solid ${activeTab === t.id ? "#1a237e" : "#c5cae9"}`,
            borderRadius: 10, padding: "10px 20px",
            cursor: "pointer", fontWeight: 700, fontSize: "0.9rem",
            display: "flex", alignItems: "center", gap: 8,
            transition: "all 0.18s",
          }}>
            <span>{t.icon}</span>{t.name}
          </button>
        ))}
      </div>

      {/* Active tool info bar */}
      {active && (
        <div style={{
          background: "linear-gradient(135deg,#e8eaf6,#f3e5f5)",
          border: "1.5px solid #9fa8da",
          borderRadius: 12, padding: "14px 20px",
          marginBottom: 16, display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap",
        }}>
          <span style={{ fontSize: "2rem" }}>{active.icon}</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, color: "#1a237e", fontSize: "1rem" }}>{active.fullName}</div>
            <div style={{ color: "#546e7a", fontSize: "0.85rem", marginTop: 3 }}>{active.desc}</div>
          </div>
          <span style={{
            background: "#1a237e", color: "#fff",
            borderRadius: 20, padding: "4px 14px",
            fontSize: "0.75rem", fontWeight: 600, whiteSpace: "nowrap",
          }}>{active.tag}</span>
        </div>
      )}

      {/* Embedded simulation */}
      {active && (
        <div style={{ borderRadius: 14, overflow: "hidden", border: "1.5px solid #c5cae9", boxShadow: "0 4px 24px rgba(26,35,126,0.10)" }}>
          <iframe
            key={active.id}
            src={active.url}
            title={active.fullName}
            width="100%"
            height="860px"
            style={{ border: "none", display: "block" }}
            allow="microphone"
          />
        </div>
      )}
    </div>
  );
}

// ── ADMIN PAGE ──────────────────────────────────────────────────────────────
function AdminPage({ user }) {
  const [activeTab, setActiveTab] = useState("add");
  const [selectedClass, setSelectedClass] = useState("PUC-I");
  const [selectedSection, setSelectedSection] = useState("Reading Materials");
  const [fileName, setFileName] = useState("");
  const [fileUrl, setFileUrl] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadMode, setUploadMode] = useState("upload");
  const [files, setFiles] = useState([]);
  const [saving, setSaving] = useState(false);
  const [editFileId, setEditFileId] = useState(null);
  const [editFileName, setEditFileName] = useState("");
  const [editFileUrl, setEditFileUrl] = useState("");
  const [editFileSaving, setEditFileSaving] = useState(false);
  const [uploadProgress, setUploadProgress] = useState("");
  const [quizClass, setQuizClass] = useState("PUC-I");
  const [quizTitle, setQuizTitle] = useState("");
  const [quizTime, setQuizTime] = useState(10);
  const [quizBatch, setQuizBatch] = useState("Both");
  const [quizOpenFrom, setQuizOpenFrom] = useState("");
  const [quizOpenUntil, setQuizOpenUntil] = useState("");
  const [quizShareWithTeachers, setQuizShareWithTeachers] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [quizList, setQuizList] = useState([]);
  const [savingQuiz, setSavingQuiz] = useState(false);
  const [editingQuizId, setEditingQuizId] = useState(null); // null = create mode
  const quizCreateRef = useRef(null);
  // Crop modal state
  const [cropModal, setCropModal] = useState({ open: false, idx: null, src: null, originalFile: null, uploading: false });
  const [previewIdx, setPreviewIdx] = useState(null);
  const [quizStep, setQuizStep] = useState(1);
  const [showQuizPreview, setShowQuizPreview] = useState(false);
  const [previewAnswers, setPreviewAnswers] = useState(false);
  const [importMode, setImportMode] = useState(false);
  const [importText, setImportText] = useState("");
  const [importError, setImportError] = useState("");
  const [results, setResults] = useState([]);
  const [resultsLoading, setResultsLoading] = useState(false);
  const [filterQuiz, setFilterQuiz] = useState("all");
  const [filterBatch, setFilterBatch] = useState("all");
  const [exporting, setExporting] = useState(false);
  const [clearingResults, setClearingResults] = useState(false);
  const [roster, setRoster] = useState([]);
  const [rosterLoading, setRosterLoading] = useState(false);
  const [rosterFile, setRosterFile] = useState(null);
  const [uploadingRoster, setUploadingRoster] = useState(false);
  const [rosterPreview, setRosterPreview] = useState([]);
  const [rosterAccessLevels, setRosterAccessLevels] = useState({ E1: false, P1: false, P2: false, S1: false });
  // ── Semesters (e.g. AY_26_27_SEM_I) ──
  const [semesters, setSemesters] = useState([]);          // all known semesters
  const [activeSemester, setActiveSemester] = useState(""); // the current semester
  const [rosterSemester, setRosterSemester] = useState(""); // semester being viewed / uploaded to
  const [newSemester, setNewSemester] = useState("");
  const semRoster = roster.filter(s => !rosterSemester || (s.semesters || (s.semester ? [s.semester] : [])).includes(rosterSemester));
  const [rosterBranch, setRosterBranch] = useState("all");
  const branchCounts = semRoster.reduce((m, s) => { const b = s.branch || "—"; m[b] = (m[b] || 0) + 1; return m; }, {});
  const branchRoster = semRoster.filter(s => rosterBranch === "all" || (s.branch || "—") === rosterBranch);
  const [users, setUsers] = useState([]);
  const rosterInputRef = useRef(null);
  // ── Student Access ──
  const [accessEmail, setAccessEmail] = useState("");
  const [accessLevels, setAccessLevels] = useState({ E1: false, P1: false, P2: false, S1: false });
  const [accessList, setAccessList] = useState([]);
  const [suspendedCodes, setSuspendedCodes] = useState([]);
  const [accessLoading, setAccessLoading] = useState(false);
  const [savingAccess, setSavingAccess] = useState(false);
  // ── Bulk Access by Batch ──
  const [bulkBatch, setBulkBatch] = useState("C1");
  const [bulkAccessLevels, setBulkAccessLevels] = useState({ E1: false, P1: false, P2: false, S1: false });
  const [bulkGranting, setBulkGranting] = useState(false);
  // ── Access Requests ──
  const [accessRequests, setAccessRequests] = useState([]);
  const [requestsLoading, setRequestsLoading] = useState(false);
  // ── Visit Log ──
  const [visitLog, setVisitLog] = useState([]);
  const [visitLogLoading, setVisitLogLoading] = useState(false);
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [visitedToday, setVisitedToday] = useState(0);
  // ── Teacher Access Requests ──
  const [teacherAccessRequests, setTeacherAccessRequests] = useState([]);
  const [teacherRequestsLoading, setTeacherRequestsLoading] = useState(false);
  const [selectedTeacherRequest, setSelectedTeacherRequest] = useState(null);
  const [selectedTeacherClasses, setSelectedTeacherClasses] = useState([]);
  const [approvingTeacher, setApprovingTeacher] = useState(false);
  // ── App Analytics ──
  const [appInstallations, setAppInstallations] = useState([]);
  const [appAnalyticsLoading, setAppAnalyticsLoading] = useState(false);

  useEffect(() => { fetchAllFiles(); fetchAllUsers(); fetchAllQuizzes(); fetchAccessList(); fetchAccessRequests(); }, []);

  // Real-time presence listener
  useEffect(() => {
    const unsub = onSnapshot(collection(db, "presence"), snap => {
      const now = Date.now();
      setOnlineUsers(snap.docs.map(d => d.data()).filter(u =>
        u.online && u.lastSeen && (now - new Date(u.lastSeen).getTime()) < 720000
      ));
    });
    return () => unsub();
  }, []);

  const fetchAllFiles = async () => { const snap = await getDocs(collection(db, "files")); setFiles(smartSortFiles(snap.docs.map(d => ({ id: d.id, ...d.data() })))); };
  // Move a file up/down within its section and save the new order immediately.
  const moveAdminFile = async (sectionFiles, idx, dir) => {
    const to = idx + dir;
    if (to < 0 || to >= sectionFiles.length) return;
    const next = [...sectionFiles];
    const [item] = next.splice(idx, 1);
    next.splice(to, 0, item);
    const orderMap = Object.fromEntries(next.map((f, i) => [f.id, i]));
    setFiles(prev => smartSortFiles(prev.map(f => (f.id in orderMap ? { ...f, order: orderMap[f.id] } : f))));
    try {
      const batch = writeBatch(db);
      next.forEach((f, i) => batch.update(doc(db, "files", f.id), { order: i }));
      await batch.commit();
    } catch (e) { alert("Could not save order: " + e.message); fetchAllFiles(); }
  };
  const autoSortAdminSection = async (sectionFiles) => {
    const ids = new Set(sectionFiles.map(f => f.id));
    setFiles(prev => smartSortFiles(prev.map(f => { if (!ids.has(f.id)) return f; const { order, ...rest } = f; return rest; })));
    try {
      const batch = writeBatch(db);
      sectionFiles.forEach(f => batch.update(doc(db, "files", f.id), { order: deleteField() }));
      await batch.commit();
    } catch (e) { alert("Could not auto-sort: " + e.message); fetchAllFiles(); }
  };
  const fetchAllUsers = async () => { const snap = await getDocs(collection(db, "users")); const list = snap.docs.map(d => d.data()).sort((a, b) => new Date(b.lastLogin) - new Date(a.lastLogin)); setUsers(list); };
  const fetchVisitLog = async () => {
    setVisitLogLoading(true);
    try {
      let snap;
      try {
        // Try ordered query first
        snap = await getDocs(query(collection(db, "visitLog"), orderBy("loginAt", "desc"), limit(500)));
      } catch (e) {
        console.log("Ordered query failed, trying unordered:", e);
        // Fallback: unordered if index not ready
        snap = await getDocs(collection(db, "visitLog"));
      }
      console.log("Visit log count:", snap.docs.length);
      const list = snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (b.loginAt || "").localeCompare(a.loginAt || ""));
      setVisitLog(list);
      const today = todayStr();
      setVisitedToday(list.filter(v => v.date === today).length);
    } catch(e) {
      console.error("fetchVisitLog error:", e);
      alert("Could not load visit log: " + e.message);
    }
    finally { setVisitLogLoading(false); }
  };
  const fetchAllQuizzes = async () => { const snap = await getDocs(collection(db, "quizzes")); setQuizList(snap.docs.map(d => ({ id: d.id, ...d.data() }))); };
  const fetchRoster = async () => {
    setRosterLoading(true);
    try {
      const [snap, cfg] = await Promise.all([getDocs(collection(db, "studentRoster")), getDoc(doc(db, "settings", "roster"))]);
      const list = snap.docs.map(d => ({ id: d.id, ...d.data() })).sort((a, b) => (a.batch || "").localeCompare(b.batch || "") || (a.studentId || "").localeCompare(b.studentId || ""));
      setRoster(list);
      const c = cfg.exists() ? cfg.data() : {};
      const known = [...new Set([...(c.semesters || []), ...list.flatMap(s => s.semesters || (s.semester ? [s.semester] : []))])].sort().reverse();
      setSemesters(known);
      setActiveSemester(c.activeSemester || "");
      setRosterSemester(prev => prev || c.activeSemester || known[0] || "");
    } catch (e) { console.error("fetchRoster error:", e); }
    setRosterLoading(false);
  };
  const saveSemesterSettings = async (list, active) => {
    await setDoc(doc(db, "settings", "roster"), { semesters: list, activeSemester: active, updatedAt: new Date().toISOString() }, { merge: true });
    setSemesters([...list].sort().reverse()); setActiveSemester(active);
  };
  const handleAddSemester = async () => {
    const name = newSemester.trim().toUpperCase().replace(/\s+/g, "_");
    if (!/^[A-Z0-9_\-]+$/.test(name)) return alert("Use a name like AY_26_27_SEM_I (letters, numbers, _ only).");
    if (semesters.includes(name)) { setRosterSemester(name); setNewSemester(""); return; }
    const makeCurrent = window.confirm(`Create semester "${name}".\n\nMake it the CURRENT semester? (Attendance, quizzes batch lookup and bulk access will use its students.)`);
    try {
      await saveSemesterSettings([...semesters, name], makeCurrent ? name : activeSemester);
      setRosterSemester(name); setNewSemester("");
    } catch (e) { alert("Error: " + e.message); }
  };
  const handleSetActiveSemester = async (name) => {
    if (!window.confirm(`Make "${name}" the current semester?`)) return;
    try { await saveSemesterSettings(semesters, name); } catch (e) { alert("Error: " + e.message); }
  };
  const fetchResults = async () => {
    setResultsLoading(true);
    const snap = await getDocs(collection(db, "quizResults"));
    // Keep the Firestore doc ref alongside data so we can batch-delete by ref
    const list = snap.docs
      .map(d => ({ id: d.id, ref: d.ref, ...d.data() }))
      .sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
    setResults(list);
    setResultsLoading(false);
  };
  const fetchAccessList = async () => { loadSuspended().then(setSuspendedCodes); setAccessLoading(true); try { const snap = await getDocs(collection(db, "studentAccess")); setAccessList(snap.docs.map(d => ({ email: d.id, ...d.data() }))); } catch(e) { console.error("fetchAccessList error:", e); } finally { setAccessLoading(false); } };

  // ── App Analytics Functions ──
  const fetchAppInstallations = async () => {
    setAppAnalyticsLoading(true);
    try {
      const snap = await getDocs(query(collection(db, "appInstallations"), orderBy("installedAt", "desc")));
      const installations = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setAppInstallations(installations);
    } catch(e) {
      console.error("fetchAppInstallations error:", e);
    } finally {
      setAppAnalyticsLoading(false);
    }
  };

  // Track app installation when app is opened in standalone mode
  const trackAppInstallation = async (userEmail, userName) => {
    try {
      // Check if app is running in standalone mode (installed as PWA)
      const isStandalone = window.navigator.standalone === true ||
                           window.matchMedia("(display-mode: standalone)").matches ||
                           window.matchMedia("(display-mode: fullscreen)").matches;

      if (!isStandalone) return; // Only track if installed as app

      const userAgent = navigator.userAgent;
      const now = new Date().toISOString();

      // Check if user already has an installation record
      const existingSnap = await getDocs(
        query(collection(db, "appInstallations"), where("email", "==", userEmail))
      );

      if (existingSnap.empty) {
        // New installation
        await addDoc(collection(db, "appInstallations"), {
          email: userEmail,
          name: userName || "",
          installedAt: now,
          lastOpenedAt: now,
          openCount: 1,
          userAgent,
          deviceType: /mobile|android|iphone/i.test(userAgent) ? "Mobile" : "Desktop",
        });
      } else {
        // Existing installation - update last opened and open count
        const docRef = existingSnap.docs[0].ref;
        const existingData = existingSnap.docs[0].data();
        await updateDoc(docRef, {
          lastOpenedAt: now,
          openCount: (existingData.openCount || 0) + 1,
        });
      }

      // Log to Firebase Analytics
      logEvent(analytics, "app_installed", {
        email: userEmail,
        name: userName || "",
        device_type: /mobile|android|iphone/i.test(userAgent) ? "Mobile" : "Desktop",
      });
    } catch(e) {
      console.error("trackAppInstallation error:", e);
    }
  };
  const handleAddAccess = async () => {
    const email = accessEmail.trim().toLowerCase();
    if (!email) return alert("Please enter student email!");
    const access = Object.keys(accessLevels).filter(k => accessLevels[k]);
    if (access.length === 0) return alert("Please select at least one access level!");
    setSavingAccess(true);
    try {
      await setDoc(doc(db, "studentAccess", email), { email, access, updatedAt: new Date().toISOString() });
      alert(`✅ Access granted to ${email}!`);
      setAccessEmail(""); setAccessLevels({ E1: false, P1: false, P2: false, S1: false }); fetchAccessList();
    } catch (e) { alert("Error: " + e.message); }
    setSavingAccess(false);
  };
  const handleDeleteAccess = async (email) => {
    if (!window.confirm(`Remove access for ${email}?`)) return;
    await deleteDoc(doc(db, "studentAccess", email)); fetchAccessList();
  };
  const handleBulkGrant = async () => {
    const access = Object.keys(bulkAccessLevels).filter(k => bulkAccessLevels[k]);
    if (access.length === 0) return alert("Please select at least one access level!");
    const batchesToQuery = bulkBatch === "Both" ? ["C1", "C2"] : [bulkBatch];
    setBulkGranting(true);
    try {
      let students = [];
      for (const b of batchesToQuery) {
        const snap = await getDocs(query(collection(db, "studentRoster"), where("batch", "==", b)));
        snap.docs.forEach(d => students.push({ email: d.id, ...d.data() }));
      }
      if (activeSemester) students = students.filter(s => (s.semesters || [s.semester]).includes(activeSemester));
      if (students.length === 0) { alert("No students found in the selected batch(es)" + (activeSemester ? ` for ${activeSemester}` : "") + "!"); setBulkGranting(false); return; }
      // Merge access — don't overwrite existing codes the student already has
      const existingSnap = await getDocs(collection(db, "studentAccess"));
      const existingMap = {};
      existingSnap.docs.forEach(d => { existingMap[d.id] = d.data().access || []; });
      const now = new Date().toISOString();
      for (let i = 0; i < students.length; i += 500) {
        const wb = writeBatch(db);
        students.slice(i, i + 500).forEach(s => {
          const merged = Array.from(new Set([...(existingMap[s.email] || []), ...access]));
          wb.set(doc(db, "studentAccess", s.email), { email: s.email, access: merged, updatedAt: now }, { merge: true });
        });
        await wb.commit();
      }
      alert(`✅ Access ${access.join(", ")} granted to ${students.length} students in ${bulkBatch}!`);
      setBulkAccessLevels({ E1: false, P1: false, P2: false, S1: false });
      fetchAccessList();
    } catch (e) { alert("Error: " + e.message); }
    setBulkGranting(false);
  };
  const fetchAccessRequests = async () => {
    setRequestsLoading(true);
    try {
      const snap = await getDocs(collection(db, "accessRequests"));
      const list = snap.docs.map(d => ({ id: d.id, ...d.data() })).sort((a, b) => {
        if (a.status === "pending" && b.status !== "pending") return -1;
        if (b.status === "pending" && a.status !== "pending") return 1;
        return (b.requestedAt || "").localeCompare(a.requestedAt || "");
      });
      setAccessRequests(list);
    } catch(e) { console.error("fetchAccessRequests error:", e); setAccessRequests([]); }
    finally { setRequestsLoading(false); }
  };
  const handleApproveRequest = async (req) => {
    if ((await loadSuspended()).includes(req.classCode)) return alert(`${req.className || req.classCode} is suspended. Restore it in 🔑 Student Access before approving.`);
    try {
      // Merge with existing access if any
      const existingDoc = await getDoc(doc(db, "studentAccess", req.email));
      const existingAccess = existingDoc.exists() ? (existingDoc.data().access || []) : [];
      const newAccess = [...new Set([...existingAccess, req.classCode])];
      await setDoc(doc(db, "studentAccess", req.email), { email: req.email, access: newAccess, updatedAt: new Date().toISOString() });
      await setDoc(doc(db, "accessRequests", req.id), { ...req, status: "approved", reviewedAt: new Date().toISOString() });
      alert(`✅ Access approved for ${req.email}!`); fetchAccessRequests(); fetchAccessList();
    } catch (e) { alert("Error: " + e.message); }
  };
  const handleRejectRequest = async (req) => {
    if (!window.confirm(`Reject access request from ${req.email}?`)) return;
    try {
      await setDoc(doc(db, "accessRequests", req.id), { ...req, status: "rejected", reviewedAt: new Date().toISOString() });
      fetchAccessRequests();
    } catch (e) { alert("Error: " + e.message); }
  };

  // ── Bulk approve / reject ──
  const [selectedReqIds, setSelectedReqIds] = useState([]);
  const [reqClassFilter, setReqClassFilter] = useState("all");
  const [bulkBusy, setBulkBusy] = useState(false);
  const toggleReqSelect = (id) => setSelectedReqIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const commitInChunks = async (ops) => {
    for (let i = 0; i < ops.length; i += 400) {
      const b = writeBatch(db);
      ops.slice(i, i + 400).forEach(fn => fn(b));
      await b.commit();
    }
  };
  const handleBulkApprove = async (reqsIn) => {
    const suspendedNow = await loadSuspended();
    const reqs = reqsIn.filter(r => !suspendedNow.includes(r.classCode));
    if (reqs.length < reqsIn.length) alert(`${reqsIn.length - reqs.length} request(s) skipped: class suspended.`);
    if (reqs.length === 0) return;
    if (!window.confirm(`Approve access for ${reqs.length} request(s)?`)) return;
    setBulkBusy(true);
    try {
      const now = new Date().toISOString();
      // Group class codes per student (a student may request more than one class)
      const byEmail = {};
      reqs.forEach(r => { (byEmail[r.email] = byEmail[r.email] || []).push(r.classCode); });
      const emails = Object.keys(byEmail);
      const existing = await Promise.all(emails.map(e => getDoc(doc(db, "studentAccess", e))));
      const ops = [];
      emails.forEach((email, i) => {
        const had = existing[i].exists() ? (existing[i].data().access || []) : [];
        const access = [...new Set([...had, ...byEmail[email]])];
        ops.push(b => b.set(doc(db, "studentAccess", email), { email, access, updatedAt: now }));
      });
      reqs.forEach(r => ops.push(b => b.update(doc(db, "accessRequests", r.id), { status: "approved", reviewedAt: now })));
      await commitInChunks(ops);
      alert(`✅ Approved ${reqs.length} request(s). Ask students to log out and log in again.`);
      setSelectedReqIds([]); fetchAccessRequests(); fetchAccessList();
    } catch (e) { alert("Error: " + e.message); }
    setBulkBusy(false);
  };
  const handleBulkReject = async (reqs) => {
    if (reqs.length === 0) return;
    if (!window.confirm(`Reject ${reqs.length} request(s)?`)) return;
    setBulkBusy(true);
    try {
      const now = new Date().toISOString();
      await commitInChunks(reqs.map(r => b => b.update(doc(db, "accessRequests", r.id), { status: "rejected", reviewedAt: now })));
      setSelectedReqIds([]); fetchAccessRequests();
    } catch (e) { alert("Error: " + e.message); }
    setBulkBusy(false);
  };

  // ── Teacher Access Requests ──
  const fetchTeacherAccessRequests = async () => {
    setTeacherRequestsLoading(true);
    try {
      const { getTeacherAccessRequests } = await import("./utils/rbac");
      const list = await getTeacherAccessRequests(db);
      setTeacherAccessRequests(list);
    } catch (e) {
      console.error("Error fetching teacher requests:", e);
      setTeacherAccessRequests([]);
    } finally {
      setTeacherRequestsLoading(false);
    }
  };

  const handleApproveTeacherRequest = async () => {
    if (!selectedTeacherRequest || selectedTeacherClasses.length === 0) {
      return alert("Please select at least one class.");
    }
    try {
      setApprovingTeacher(true);
      const { approveTeacherRequest } = await import("./utils/rbac");
      await approveTeacherRequest(db, selectedTeacherRequest.email, selectedTeacherClasses, user.email);
      alert("✅ Teacher request approved!");
      setSelectedTeacherRequest(null);
      setSelectedTeacherClasses([]);
      fetchTeacherAccessRequests();
    } catch (e) {
      alert("Error approving request: " + e.message);
    } finally {
      setApprovingTeacher(false);
    }
  };

  const handleRejectTeacherRequest = async (request) => {
    if (!window.confirm(`Reject ${request.name}'s teacher request?`)) return;
    try {
      const { rejectTeacherRequest } = await import("./utils/rbac");
      await rejectTeacherRequest(db, request.email, user.email, "Request was reviewed and rejected");
      alert("✅ Request rejected");
      fetchTeacherAccessRequests();
    } catch (e) {
      alert("Error rejecting request: " + e.message);
    }
  };

  const handleRosterFileChange = (e) => {
    const file = e.target.files[0]; if (!file) return; setRosterFile(file);
    const reader = new FileReader();
    reader.onload = async (evt) => {
      try {
        const ExcelJSLib = await import("exceljs"); const wb = new ExcelJSLib.default.Workbook(); await wb.xlsx.load(evt.target.result);
        const ws = wb.worksheets[0]; const rows = [];
        ws.eachRow((row, rowNum) => { if (rowNum === 1) return; const studentId = String(row.getCell(1).value || "").trim(); const name = String(row.getCell(2).value || "").trim(); const email = String(row.getCell(3).value || "").trim().toLowerCase(); const batch = String(row.getCell(4).value || "").trim().toUpperCase(); const branch = String(row.getCell(5).value || "").trim().toUpperCase(); if (email) rows.push({ studentId, name, email, batch, branch }); });
        setRosterPreview(rows.slice(0, 5));
      } catch { alert("Could not read file. Use .xlsx format."); }
    };
    reader.readAsArrayBuffer(file);
  };

  const handleUploadRoster = async () => {
    if (!rosterFile) return alert("Please select an Excel file!");
    if (!rosterSemester) return alert("Please choose or create a semester first (e.g. AY_26_27_SEM_I).");
    const selectedAccess = Object.keys(rosterAccessLevels).filter(k => rosterAccessLevels[k]);
    setUploadingRoster(true);
    try {
      const reader = new FileReader();
      reader.onload = async (evt) => {
        const ExcelJSLib = await import("exceljs"); const wb = new ExcelJSLib.default.Workbook(); await wb.xlsx.load(evt.target.result);
        const ws = wb.worksheets[0]; const rows = [];
        ws.eachRow((row, rowNum) => { if (rowNum === 1) return; const studentId = String(row.getCell(1).value || "").trim(); const name = String(row.getCell(2).value || "").trim(); const email = String(row.getCell(3).value || "").trim().toLowerCase(); const batch = String(row.getCell(4).value || "").trim().toUpperCase(); const branch = String(row.getCell(5).value || "").trim().toUpperCase(); if (email && batch) rows.push({ studentId, name, email, batch, ...(branch ? { branch } : {}) }); });
        if (rows.length === 0) { alert("No valid rows found!"); setUploadingRoster(false); return; }
        // Upload to studentRoster
        for (let i = 0; i < rows.length; i += 500) {
          const chunk = rows.slice(i, i + 500); const batch = writeBatch(db);
          chunk.forEach(s => { const ref = doc(db, "studentRoster", s.email); batch.set(ref, { ...s, semester: rosterSemester, semesters: arrayUnion(rosterSemester), updatedAt: new Date().toISOString() }, { merge: true }); });
          await batch.commit();
        }
        if (!semesters.includes(rosterSemester)) await saveSemesterSettings([...semesters, rosterSemester], activeSemester || rosterSemester);
        // Also write studentAccess if any access levels were selected
        if (selectedAccess.length > 0) {
          for (let i = 0; i < rows.length; i += 500) {
            const chunk = rows.slice(i, i + 500); const accessBatch = writeBatch(db);
            chunk.forEach(s => {
              const ref = doc(db, "studentAccess", s.email);
              accessBatch.set(ref, { email: s.email, access: selectedAccess, updatedAt: new Date().toISOString() }, { merge: true });
            });
            await accessBatch.commit();
          }
          alert(`✅ ${rows.length} students uploaded to ${rosterSemester} with access: ${selectedAccess.join(", ")}!`);
        } else {
          alert(`✅ ${rows.length} students uploaded to ${rosterSemester}! (No access levels assigned — add later from Student Access tab)`);
        }
        setRosterFile(null); setRosterPreview([]); setRosterAccessLevels({ E1: false, P1: false, P2: false, S1: false });
        if (rosterInputRef.current) rosterInputRef.current.value = ""; fetchRoster(); fetchAccessList();
      };
      reader.readAsArrayBuffer(rosterFile);
    } catch (e) { alert("Upload failed: " + e.message); }
    setUploadingRoster(false);
  };

  const downloadTemplate = async () => {
    const wb = new ExcelJS.Workbook(); const ws = wb.addWorksheet("Students");
    ws.columns = [{ header: "Student ID", key: "studentId", width: 15 }, { header: "Name", key: "name", width: 25 }, { header: "Email (@rgukt.ac.in)", key: "email", width: 35 }, { header: "Batch (C1 or C2)", key: "batch", width: 18 }, { header: "Branch (CSE/ECE/EEE/ME/CE/CHE/MME)", key: "branch", width: 34 }];
    ws.getRow(1).eachCell(cell => { cell.font = { bold: true, color: { argb: "FFFFFFFF" } }; cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF1A237E" } }; cell.alignment = { horizontal: "center" }; });
    ws.addRow({ studentId: "R200001", name: "Ravi Kumar", email: "r200001@rgukt.ac.in", batch: "C1", branch: "CSE" });
    ws.addRow({ studentId: "R200002", name: "Priya Sharma", email: "r200002@rgukt.ac.in", batch: "C2", branch: "ECE" });
    const buffer = await wb.xlsx.writeBuffer();
    saveAs(new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }), "Student_Roster_Template.xlsx");
  };

  const handleSave = async () => {
    if (!fileName.trim()) return alert("Please enter file name!");
    if (uploadMode === "upload" && !selectedFile) return alert("Please select a file!");
    if (uploadMode === "link" && !fileUrl.trim()) return alert("Please enter a link!");
    setSaving(true);
    try {
      let url = fileUrl;
      if (uploadMode === "upload" && selectedFile) {
        setUploadProgress("Uploading to Cloudinary...");
        const formData = new FormData(); formData.append("file", selectedFile); formData.append("upload_preset", CLOUDINARY_PRESET); formData.append("resource_type", "raw");
        const res = await fetch(`https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD}/raw/upload`, { method: "POST", body: formData });
        const data = await res.json(); if (!data.secure_url) throw new Error("Upload failed: " + (data.error?.message || res.status)); url = data.secure_url; setUploadProgress("");
      }
      await addDoc(collection(db, "files"), { class: selectedClass, section: selectedSection, name: fileName, url, createdAt: Date.now() });
      alert("✅ File added!"); setFileName(""); setFileUrl(""); setSelectedFile(null); fetchAllFiles();
    } catch (e) { alert("Error: " + e.message); }
    setSaving(false); setUploadProgress("");
  };

  const handleDelete = async (id) => { if (!window.confirm("Delete this file?")) return; await deleteDoc(doc(db, "files", id)); fetchAllFiles(); };
  const startEditFile = (f) => { setEditFileId(f.id); setEditFileName(f.name); setEditFileUrl(f.url); };
  const cancelEditFile = () => { setEditFileId(null); setEditFileName(""); setEditFileUrl(""); };
  const saveEditFile = async () => {
    if (!editFileName.trim() || !editFileUrl.trim()) { alert("Name and URL are required"); return; }
    setEditFileSaving(true);
    try {
      await updateDoc(doc(db, "files", editFileId), { name: editFileName.trim(), url: editFileUrl.trim() });
      cancelEditFile(); fetchAllFiles();
    } catch (e) { alert("Error: " + e.message); }
    setEditFileSaving(false);
  };

  // ── Quiz question helpers ──
  const addQuestion = (type) => {
    if (type === "mcq") setQuestions([...questions, { type, question: "", options: ["", "", "", ""], answer: 0, imageUrl: "", imageMode: "none" }]);
    else setQuestions([...questions, { type, question: "", answer: "true", imageUrl: "", imageMode: "none" }]);
  };
  const updateQuestion = (idx, field, val) => { const u = [...questions]; u[idx] = { ...u[idx], [field]: val }; setQuestions(u); };
  const updateOption = (qIdx, oIdx, val) => { const u = [...questions]; u[qIdx].options[oIdx] = val; setQuestions(u); };
  const removeQuestion = (idx) => setQuestions(questions.filter((_, i) => i !== idx));

  // ── Bulk Import Parser ──
  const parseImportedQuestions = () => {
    setImportError("");
    if (!importText.trim()) { setImportError("Please paste some questions first."); return; }
    // Split into blocks by blank lines
    const blocks = importText.trim().split(/\n\s*\n/);
    const parsed = [];
    let errorCount = 0;
    for (const block of blocks) {
      const lines = block.trim().split('\n').map(l => l.trim()).filter(Boolean);
      if (lines.length < 2) continue;
      // Question line: starts with Q:, Q., 1., 1), or just text
      const qLine = lines.find(l => /^(Q\s*[:.)]?\s*|[\d]+\s*[.)]\s*)/i.test(l)) || lines[0];
      const question = qLine.replace(/^(Q\s*[:.)]?\s*|[\d]+\s*[.)]\s*)/i, '').trim();
      if (!question) continue;
      // Answer line
      const ansLine = lines.find(l => /^(ans|answer)\s*[:.)]?\s*/i.test(l));
      const ansText = ansLine ? ansLine.replace(/^(ans|answer)\s*[:.)]?\s*/i, '').trim().toLowerCase() : '';
      // Option lines: a) / a. / a: / (a) / A)
      const optionLines = lines.filter(l => /^\(?[a-dA-D]\s*[:.)][\s)]/i.test(l) || /^\(?[a-dA-D]\s*\)\s*.+/i.test(l));
      if (optionLines.length === 0) {
        // True/False
        const ans = /^(true|t|yes)/i.test(ansText) ? 'true' : /^(false|f|no)/i.test(ansText) ? 'false' : 'true';
        parsed.push({ type: 'truefalse', question, answer: ans, imageUrl: '', imageMode: 'none' });
      } else {
        // MCQ
        const options = optionLines.map(l => l.replace(/^\(?[a-dA-D]\s*[:.)][\s)]*/, '').replace(/^\(?[a-dA-D]\s*\)\s*/, '').trim());
        while (options.length < 4) options.push('');
        // Find correct answer index
        const firstChar = ansText.replace(/[^a-d]/gi, '').charAt(0).toLowerCase();
        const ansIdx = ['a','b','c','d'].indexOf(firstChar);
        const answer = ansIdx >= 0 ? ansIdx : 0;
        if (ansIdx < 0) errorCount++;
        parsed.push({ type: 'mcq', question, options: options.slice(0, 4), answer, imageUrl: '', imageMode: 'none' });
      }
    }
    if (parsed.length === 0) { setImportError("Could not parse any questions. Check the format below."); return; }
    setQuestions(prev => [...prev, ...parsed]);
    setImportText("");
    setImportMode(false);
    if (errorCount > 0) alert(`✅ ${parsed.length} questions imported! (${errorCount} had missing answers — defaulted to option A. Please review.)`);
    else alert(`✅ ${parsed.length} questions imported successfully!`);
  };

  // Opens the crop modal; actual upload happens after the user confirms the crop
  const handleQuestionImageUpload = (idx, file) => {
    if (!file) return;
    const src = URL.createObjectURL(file);
    setCropModal({ open: true, idx, src, originalFile: file, uploading: false });
  };

  // Called when user clicks "Crop & Use" — uploads the cropped blob
  const handleCropConfirm = async (croppedBlob) => {
    const { idx, src } = cropModal;
    setCropModal(prev => ({ ...prev, uploading: true }));
    updateQuestion(idx, "imageUploading", true);
    try {
      const ext = croppedBlob.type === "image/png" ? "png" : "jpg";
      const croppedFile = new File([croppedBlob], `cropped.${ext}`, { type: croppedBlob.type || "image/jpeg" });
      console.log("Uploading cropped image:", croppedFile.name, croppedFile.size, "bytes");
      const url = await uploadImageToCloudinary(croppedFile);
      console.log("Cloudinary upload success, url:", url);
      // Update both fields in ONE setQuestions call to avoid stale-closure overwrite
      setQuestions(prev => prev.map((q, i) => i === idx ? { ...q, imageUrl: url, imageUploading: false } : q));
      URL.revokeObjectURL(src);
      setCropModal({ open: false, idx: null, src: null, originalFile: null, uploading: false });
    } catch (e) {
      alert("Image upload failed: " + e.message);
      setQuestions(prev => prev.map((q, i) => i === idx ? { ...q, imageUploading: false } : q));
      setCropModal(prev => ({ ...prev, uploading: false }));
    }
  };

  // Called when user clicks "Use Full Image" — skips cropping, uploads original
  const handleCropSkip = async () => {
    const { idx, src, originalFile } = cropModal;
    setCropModal(prev => ({ ...prev, uploading: true }));
    updateQuestion(idx, "imageUploading", true);
    try {
      const url = await uploadImageToCloudinary(originalFile);
      console.log("Full image upload success, url:", url);
      // Update both fields in ONE setQuestions call to avoid stale-closure overwrite
      setQuestions(prev => prev.map((q, i) => i === idx ? { ...q, imageUrl: url, imageUploading: false } : q));
      URL.revokeObjectURL(src);
      setCropModal({ open: false, idx: null, src: null, originalFile: null, uploading: false });
    } catch (e) {
      alert("Image upload failed: " + e.message);
      setQuestions(prev => prev.map((q, i) => i === idx ? { ...q, imageUploading: false } : q));
      setCropModal(prev => ({ ...prev, uploading: false }));
    }
  };

  const handleCropCancel = () => {
    if (cropModal.src) URL.revokeObjectURL(cropModal.src);
    setCropModal({ open: false, idx: null, src: null, originalFile: null, uploading: false });
  };

  const handleSaveQuiz = async () => {
    if (!quizTitle.trim()) return alert("Please enter quiz title!");
    if (questions.length === 0) return alert("Add at least one question!");
    const incomplete = questions.some(q => !q.question.trim() || (q.type === "mcq" && q.options.some(o => !o.trim())));
    if (incomplete) return alert("Please fill in all question fields!");
    setSavingQuiz(true);
    try {
      const cleanQuestions = questions.map(({ imageUploading, imageMode, ...q }) => q);
      const quizData = { class: quizClass, title: quizTitle, timeLimit: Number(quizTime), assignedBatch: quizBatch, questions: cleanQuestions, openFrom: quizOpenFrom || null, openUntil: quizOpenUntil || null, maxAttempts: 1, createdBy: user?.email, createdByName: user?.displayName, sharedWithTeachers: !!quizShareWithTeachers };
      if (editingQuizId) {
        // ── UPDATE existing quiz ──
        await updateDoc(doc(db, "quizzes", editingQuizId), { ...quizData, updatedAt: Date.now() });
        alert("✅ Quiz updated!");
        setEditingQuizId(null);
      } else {
        // ── CREATE new quiz ──
        await addDoc(collection(db, "quizzes"), { ...quizData, createdAt: Date.now() });
        alert("✅ Quiz saved!");
      }
      setQuizTitle(""); setQuizTime(10); setQuizBatch("Both"); setQuestions([]); setQuizOpenFrom(""); setQuizOpenUntil(""); setQuizShareWithTeachers(false); setQuizStep(1);
      fetchAllQuizzes();
    } catch (e) { alert("Error: " + e.message); }
    setSavingQuiz(false);
  };

  const handleDeleteQuiz = async (id) => { if (!window.confirm("Delete this quiz?")) return; await deleteDoc(doc(db, "quizzes", id)); if (editingQuizId === id) handleCancelEdit(); fetchAllQuizzes(); };

  const handleEditQuiz = (qz) => {
    setEditingQuizId(qz.id);
    setQuizClass(qz.class || "PUC-I");
    setQuizTitle(qz.title || "");
    setQuizTime(qz.timeLimit || 10);
    setQuizBatch(qz.assignedBatch || "Both");
    setQuizOpenFrom(qz.openFrom || "");
    setQuizOpenUntil(qz.openUntil || "");
    setQuizShareWithTeachers(!!qz.sharedWithTeachers);
    setQuizStep(2);
    // Restore questions — ensure each has all expected fields
    setQuestions((qz.questions || []).map(q => ({
      type: q.type || "mcq",
      question: q.question || "",
      options: q.options || ["", "", "", ""],
      answer: q.answer ?? 0,
      imageUrl: q.imageUrl || "",
      imageMode: q.imageUrl ? "url" : "none",
    })));
    // Scroll to the create/edit form
    setTimeout(() => quizCreateRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
  };

  const handleCancelEdit = () => {
    setEditingQuizId(null);
    setQuizTitle(""); setQuizTime(10); setQuizBatch("Both");
    setQuestions([]); setQuizOpenFrom(""); setQuizOpenUntil(""); setQuizShareWithTeachers(false); setQuizStep(1);
  };

  // Delete documents in batches of 490 (Firestore limit is 500 per batch)
  const deleteInBatches = async (docs) => {
    const BATCH_SIZE = 490;
    for (let i = 0; i < docs.length; i += BATCH_SIZE) {
      const batch = writeBatch(db);
      docs.slice(i, i + BATCH_SIZE).forEach(d => batch.delete(d.ref));
      await batch.commit();
    }
  };

  // Clear quiz results — optionally scoped to the currently selected quiz filter
  const handleClearResults = async (scopeLabel, docsToDelete) => {
    if (docsToDelete.length === 0) { alert("No results to delete."); return; }
    const confirmed = window.confirm(
      `⚠️ Delete ${docsToDelete.length} result(s) for ${scopeLabel}?\n\nStudents will be able to retake those quizzes.\n\nThis CANNOT be undone.`
    );
    if (!confirmed) return;
    // Second confirmation for safety
    const reconfirmed = window.confirm(`Are you sure? This will permanently delete ${docsToDelete.length} result record(s).`);
    if (!reconfirmed) return;
    setClearingResults(true);
    try {
      await deleteInBatches(docsToDelete);
      alert(`✅ Deleted ${docsToDelete.length} result(s). Students can now retake the quiz.`);
      fetchResults(); // refresh table
    } catch (e) { alert("Error deleting results: " + e.message); }
    setClearingResults(false);
  };

  const exportToExcel = async () => {
    setExporting(true);
    try {
      let filtered = results.filter(r => filterQuiz === "all" || r.quizTitle === filterQuiz).filter(r => filterBatch === "all" || r.studentBatch === filterBatch);
      const wb = new ExcelJS.Workbook(); const ws = wb.addWorksheet("Quiz Results");
      ws.columns = [{ header: "#", key: "no", width: 5 }, { header: "Student Name", key: "name", width: 22 }, { header: "Email", key: "email", width: 30 }, { header: "Batch", key: "batch", width: 8 }, { header: "Class", key: "cls", width: 20 }, { header: "Quiz Title", key: "quiz", width: 35 }, { header: "Score", key: "score", width: 8 }, { header: "Total", key: "total", width: 8 }, { header: "Percentage (%)", key: "percent", width: 15 }, { header: "Grade", key: "grade", width: 15 }, { header: "Submitted At", key: "date", width: 24 }];
      ws.getRow(1).eachCell(cell => { cell.font = { bold: true, color: { argb: "FFFFFFFF" } }; cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF1A237E" } }; cell.alignment = { horizontal: "center", vertical: "middle" }; });
      filtered.forEach((r, i) => {
        const grade = r.percent >= 90 ? "Excellent" : r.percent >= 75 ? "Good" : r.percent >= 50 ? "Average" : "Below Average";
        const row = ws.addRow({ no: i + 1, name: r.studentName, email: r.studentEmail, batch: r.studentBatch || "—", cls: r.class, quiz: r.quizTitle, score: r.score, total: r.total, percent: r.percent, grade, date: r.submittedAt ? new Date(r.submittedAt).toLocaleString("en-IN") : "—" });
        const color = r.percent >= 90 ? "FF2E7D32" : r.percent >= 75 ? "FF1565C0" : r.percent >= 50 ? "FFF57C00" : "FFC62828";
        row.getCell("percent").font = { bold: true, color: { argb: color } }; row.getCell("grade").font = { bold: true, color: { argb: color } };
        if (i % 2 === 0) row.eachCell(cell => { cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFF5F5F5" } }; });
      });
      const buffer = await wb.xlsx.writeBuffer();
      saveAs(new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }), `Quiz_Results_${new Date().toLocaleDateString("en-IN").replace(/\//g, "-")}.xlsx`);
    } catch (e) { alert("Export failed: " + e.message); }
    setExporting(false);
  };

  const formatDate = (iso) => { if (!iso) return "—"; return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); };
  const uniqueQuizTitles = [...new Set(results.map(r => r.quizTitle))];
  const filteredResults = results.filter(r => filterQuiz === "all" || r.quizTitle === filterQuiz).filter(r => filterBatch === "all" || r.studentBatch === filterBatch);

  return (
    <div className="admin-page">
      <h1>🔐 Admin Panel</h1>
      <div className="admin-tabs">
        <button className={activeTab === "add" ? "active" : ""} onClick={() => setActiveTab("add")}>➕ Add File</button>
        <button className={activeTab === "manage" ? "active" : ""} onClick={() => setActiveTab("manage")}>📋 Manage Files</button>
        <button className={activeTab === "quiz" ? "active" : ""} onClick={() => setActiveTab("quiz")}>📝 Quizzes</button>
        <button className={activeTab === "results" ? "active" : ""} onClick={() => { setActiveTab("results"); fetchResults(); }}>📊 Results</button>
        <button className={activeTab === "attendance" ? "active" : ""} onClick={() => setActiveTab("attendance")} style={{ background: activeTab === "attendance" ? "#e65100" : "" }}>📅 Attendance</button>
        <button className={activeTab === "roster" ? "active" : ""} onClick={() => { setActiveTab("roster"); fetchRoster(); }}>🎓 Student Roster</button>
        <button className={activeTab === "classmap" ? "active" : ""} onClick={() => setActiveTab("classmap")} style={{ background: activeTab === "classmap" ? "#0d1b5e" : "" }}>📚 Class Mapping</button>
        <button className={activeTab === "broadcast" ? "active" : ""} onClick={() => setActiveTab("broadcast")} style={{ background: activeTab === "broadcast" ? "#01579b" : "" }}>📡 Broadcast</button>
        <button className={activeTab === "users" ? "active" : ""} onClick={() => { setActiveTab("users"); fetchAllUsers(); }}>👥 Students ({users.length})</button>
        <button className={activeTab === "access" ? "active" : ""} onClick={() => { setActiveTab("access"); fetchAccessList(); }} style={{ background: activeTab === "access" ? "#1b5e20" : "" }}>🔑 Student Access</button>
        <button className={activeTab === "teachers" ? "active" : ""} onClick={() => { setActiveTab("teachers"); fetchTeacherAccessRequests(); }} style={{ background: activeTab === "teachers" ? "#6a1b9a" : "", position: "relative" }}>
          👨‍🏫 Teacher Requests {teacherAccessRequests.filter(r => r.status === "pending").length > 0 && <span style={{ background: "#f44336", color: "#fff", borderRadius: "50%", padding: "1px 6px", fontSize: "0.72rem", fontWeight: 900, marginLeft: 4 }}>{teacherAccessRequests.filter(r => r.status === "pending").length}</span>}
        </button>
        <button className={activeTab === "requests" ? "active" : ""} onClick={() => { setActiveTab("requests"); fetchAccessRequests(); }} style={{ background: activeTab === "requests" ? "#b71c1c" : "", position: "relative" }}>
          🔔 Requests {accessRequests.filter(r => r.status === "pending").length > 0 && <span style={{ background: "#f44336", color: "#fff", borderRadius: "50%", padding: "1px 6px", fontSize: "0.72rem", fontWeight: 900, marginLeft: 4 }}>{accessRequests.filter(r => r.status === "pending").length}</span>}
        </button>
        <button className={activeTab === "activity" ? "active" : ""} onClick={() => setActiveTab("activity")} style={{ background: activeTab === "activity" ? "#0288d1" : "", position: "relative" }}>
          📊 Student Activity
        </button>
        <button className={activeTab === "visitors" ? "active" : ""} onClick={() => { setActiveTab("visitors"); fetchVisitLog(); }} style={{ background: activeTab === "visitors" ? "#00897b" : "" }}>👀 Visitors</button>
        <button className={activeTab === "appanalytics" ? "active" : ""} onClick={() => { setActiveTab("appanalytics"); fetchAppInstallations(); }} style={{ background: activeTab === "appanalytics" ? "#c0ca33" : "", position: "relative" }}>
          📱 App Analytics {appInstallations.length > 0 && <span style={{ background: "#ff6f00", color: "#fff", borderRadius: "50%", padding: "1px 6px", fontSize: "0.72rem", fontWeight: 900, marginLeft: 4 }}>{appInstallations.length}</span>}
        </button>
        <button className={activeTab === "aboutprof" ? "active" : ""} onClick={() => setActiveTab("aboutprof")} style={{ background: activeTab === "aboutprof" ? "#9c27b0" : "", position: "relative" }}>
          👨‍🎓 About Prof
        </button>
        <button className={activeTab === "notices" ? "active" : ""} onClick={() => setActiveTab("notices")}>📣 Notices</button>
        <button className={activeTab === "messages" ? "active" : ""} onClick={() => setActiveTab("messages")} style={{ background: activeTab === "messages" ? "#1565c0" : "" }}>💬 Messages</button>
      </div>

      {activeTab === "notices" && <NoticeManager />}
      {activeTab === "messages" && <MessagesInbox />}

      {activeTab === "add" && (
        <div className="add-form">
          <div className="form-row"><label>Class:</label><select value={selectedClass} onChange={e => setSelectedClass(e.target.value)}>{CLASSES.map(c => <option key={c}>{c}</option>)}</select></div>
          <div className="form-row"><label>Section:</label><select value={selectedSection} onChange={e => setSelectedSection(e.target.value)}>{SECTIONS.map(s => <option key={s}>{s}</option>)}</select></div>
          <div className="form-row"><label>File Name:</label><input type="text" placeholder="e.g. Chapter 1 - Motion Notes" value={fileName} onChange={e => setFileName(e.target.value)} /></div>
          <div className="upload-toggle">
            <button className={uploadMode === "upload" ? "active" : ""} onClick={() => setUploadMode("upload")}>📤 Upload PDF</button>
            <button className={uploadMode === "link" ? "active" : ""} onClick={() => setUploadMode("link")}>🔗 Paste Link</button>
          </div>
          {uploadMode === "upload" && <div className="form-row"><label>Select File:</label><input type="file" accept=".pdf,.doc,.docx,.ppt,.pptx" onChange={e => setSelectedFile(e.target.files[0])} />{selectedFile && <p className="file-selected">✅ {selectedFile.name}</p>}</div>}
          {uploadMode === "link" && <div className="form-row"><label>Link:</label><input type="text" placeholder="https://..." value={fileUrl} onChange={e => setFileUrl(e.target.value)} /></div>}
          {uploadProgress && <p className="upload-progress">⏳ {uploadProgress}</p>}
          <button className="save-btn" onClick={handleSave} disabled={saving}>{saving ? "Uploading..." : "💾 Save File"}</button>
        </div>
      )}

      {activeTab === "manage" && (
        <div className="manage-section">
          {files.length === 0 ? <p>No files yet.</p> : CLASSES.map(cls => {
            const classFiles = files.filter(f => f.class === cls); if (classFiles.length === 0) return null;
            return (<div key={cls}><h3 className="manage-class-title">{cls}</h3>{SECTIONS.map(sec => { const sectionFiles = classFiles.filter(f => f.section === sec); if (sectionFiles.length === 0) return null; return (<div key={sec}><p className="manage-section-title" style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}><span>{sec}</span>{sectionFiles.length > 1 && <button className="admin-autosort-btn" onClick={() => autoSortAdminSection(sectionFiles)} title="Clear manual order and sort by unit / number">🔢 Auto-sort</button>}</p>{sectionFiles.map((f, idx) => (<div key={f.id} className="manage-row">{editFileId === f.id ? (<><input value={editFileName} onChange={e => setEditFileName(e.target.value)} style={{ flex: 1, padding: "6px 10px", borderRadius: 6, border: "1.5px solid #555", background: "#1a2340", color: "#fff", marginRight: 6 }} /><input value={editFileUrl} onChange={e => setEditFileUrl(e.target.value)} style={{ flex: 2, padding: "6px 10px", borderRadius: 6, border: "1.5px solid #555", background: "#1a2340", color: "#fff", marginRight: 6 }} /><button className="delete-btn" style={{ background: "#1a6e3a", borderColor: "#2ecc71", color: "#fff", marginRight: 4 }} onClick={saveEditFile} disabled={editFileSaving}>{editFileSaving ? "…" : "💾 Save"}</button><button className="delete-btn" onClick={cancelEditFile}>✕ Cancel</button></>) : (<><span style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}><span className="admin-move-btns"><button onClick={() => moveAdminFile(sectionFiles, idx, -1)} disabled={idx === 0} title="Move up">▲</button><button onClick={() => moveAdminFile(sectionFiles, idx, 1)} disabled={idx === sectionFiles.length - 1} title="Move down">▼</button></span>{f.name}</span><div style={{ display: "flex", gap: 6 }}><button className="delete-btn" style={{ borderColor: "#888", color: "#ccc" }} onClick={() => startEditFile(f)}>✏️ Edit</button><button className="delete-btn" onClick={() => handleDelete(f.id)}>🗑 Delete</button></div></>)}</div>))}</div>); })}</div>);
          })}
        </div>
      )}

      {activeTab === "quiz" && (
        <div className="quiz-admin">
          <div className="quiz-create-box" ref={quizCreateRef}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10, marginBottom: 14 }}>
              <h3 style={{ margin: 0 }}>{editingQuizId ? "✏️ Edit Quiz" : "➕ Create New Quiz"}</h3>
              {editingQuizId && (
                <button onClick={handleCancelEdit} style={{ background: "rgba(239,68,68,0.12)", color: "#fca5a5", border: "1.5px solid rgba(239,68,68,0.4)", borderRadius: 8, padding: "6px 16px", fontWeight: 700, cursor: "pointer", fontSize: "0.85rem" }}>
                  ✕ Cancel Edit
                </button>
              )}
            </div>
            {/* ── Step indicator ── */}
            <div className="quiz-steps">
              {[["1", "Details"], ["2", `Questions${questions.length ? ` (${questions.length})` : ""}`], ["3", "Schedule & Save"]].map(([n, label]) => (
                <button key={n} type="button" className={`quiz-step ${quizStep === Number(n) ? "active" : ""} ${quizStep > Number(n) ? "done" : ""}`} onClick={() => setQuizStep(Number(n))}>
                  <span className="quiz-step-num">{quizStep > Number(n) ? "✓" : n}</span>{label}
                </button>
              ))}
            </div>
            {quizStep === 1 && (<>
            <div className="form-row"><label>Class:</label><select value={quizClass} onChange={e => setQuizClass(e.target.value)}>{CLASSES.map(c => <option key={c}>{c}</option>)}</select></div>
            <div className="form-row"><label>Quiz Title:</label><input type="text" placeholder="e.g. Chapter 2 - Laws of Motion" value={quizTitle} onChange={e => setQuizTitle(e.target.value)} /></div>
            <div className="form-row">
              <label>Assign to Batch:</label>
              <div style={{ display: "flex", gap: 10, marginTop: 4 }}>
                {BATCHES.map(b => <button key={b} className={`batch-select-btn ${quizBatch === b ? "active" : ""}`} onClick={() => setQuizBatch(b)}>{b === "Both" ? "🌐 Both" : `🏷 Batch ${b}`}</button>)}
              </div>
            </div>
            <div className="quiz-step-nav">
              <span />
              <button type="button" className="quiz-nav-btn next" onClick={() => { if (!quizTitle.trim()) return alert("Please enter quiz title!"); setQuizStep(2); }}>Next →</button>
            </div>
            </>)}
            {quizStep === 2 && (<>
            <div className="latex-tip"><strong>💡 LaTeX:</strong> <code>$...$</code> inline, <code>$$...$$</code> block, <code>\(...\)</code> inline, <code>\[...\]</code> block</div>
            <div className="questions-list">
              {questions.map((q, idx) => (
                <div key={idx} className="question-block">
                  <div className="question-block-header">
                    <span className="q-badge">{q.type === "mcq" ? "MCQ" : "T/F"}</span>
                    <span>Q{idx + 1}</span>
                    <button className="preview-toggle-btn" onClick={() => setPreviewIdx(previewIdx === idx ? null : idx)}>{previewIdx === idx ? "✏️ Edit" : "👁 Preview"}</button>
                    <button className="delete-btn" onClick={() => removeQuestion(idx)}>🗑</button>
                  </div>

                  {previewIdx === idx ? (
                    <div className="latex-preview-block">
                      <p className="latex-preview-label">Preview:</p>
                      <p className="latex-preview-text"><strong>Q{idx + 1}.</strong> <LatexRenderer text={q.question || "No question entered"} /></p>
                      {q.imageUrl && <img src={q.imageUrl} alt="question" className="question-img" />}
                      {q.type === "mcq" && (
                        <div className="latex-preview-options">
                          {q.options.map((opt, oi) => (
                            <div key={oi} className={`latex-preview-option ${Number(q.answer) === oi ? "correct-preview" : ""}`}>
                              <span className="option-letter">{["A","B","C","D"][oi]}</span>
                              <LatexRenderer text={opt || `Option ${["A","B","C","D"][oi]}`} />
                              {Number(q.answer) === oi && <span className="correct-tick">✅</span>}
                            </div>
                          ))}
                        </div>
                      )}
                      {q.type === "truefalse" && <p style={{ marginTop: 8, color: "#2e7d32", fontWeight: 600 }}>✅ Correct: {q.answer === "true" ? "True" : "False"}</p>}
                    </div>
                  ) : (
                    <>
                      <input type="text" placeholder="Type question — use $...$ for LaTeX" value={q.question} onChange={e => updateQuestion(idx, "question", e.target.value)} className="question-input" />

                      {/* ── Image section ── */}
                      <div className="question-img-section">
                        {!q.imageUrl && !q.imageUploading && (
                          <div style={{ display: "flex", gap: 10, marginTop: 10, flexWrap: "wrap" }}>
                            {/* Hidden file input — triggered by the button below */}
                            <input
                              id={`img-upload-${idx}`}
                              type="file" accept="image/*"
                              style={{ display: "none" }}
                              onChange={e => { if (e.target.files[0]) handleQuestionImageUpload(idx, e.target.files[0]); e.target.value = ""; }}
                            />
                            {/* Primary: photo/upload from device with crop */}
                            <label htmlFor={`img-upload-${idx}`}
                              style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "linear-gradient(135deg,#1565c0,#1a237e)", color: "#fff", border: "none", borderRadius: 10, padding: "9px 18px", fontWeight: 700, cursor: "pointer", fontSize: "0.88rem" }}>
                              📷 Add Image &amp; Crop
                            </label>
                            {/* Secondary: paste URL */}
                            <button onClick={() => updateQuestion(idx, "imageMode", q.imageMode === "url" ? "none" : "url")}
                              style={{ background: q.imageMode === "url" ? "#e3f2fd" : "#fff", color: "#1565c0", border: "1.5px solid #90caf9", borderRadius: 10, padding: "9px 16px", fontWeight: 600, cursor: "pointer", fontSize: "0.88rem" }}>
                              🔗 {q.imageMode === "url" ? "Hide URL" : "Paste URL"}
                            </button>
                          </div>
                        )}

                        {/* URL input (shown when toggled) */}
                        {q.imageMode === "url" && !q.imageUrl && (
                          <input type="text" placeholder="Paste image URL (https://...)" className="question-input"
                            style={{ marginTop: 8 }} value={q.imageUrl}
                            onChange={e => updateQuestion(idx, "imageUrl", e.target.value)} />
                        )}

                        {/* Uploading spinner */}
                        {q.imageUploading && (
                          <div style={{ marginTop: 10, color: "#f57c00", fontSize: "0.85rem", fontWeight: 600 }}>
                            ⏳ Uploading image…
                          </div>
                        )}

                        {/* Preview + remove */}
                        {q.imageUrl && !q.imageUploading && (
                          <div style={{ marginTop: 10, position: "relative", display: "inline-block" }}>
                            <img src={q.imageUrl} alt="preview" className="question-img-preview"
                              style={{ maxWidth: "100%", maxHeight: 200, borderRadius: 10, border: "2px solid #90caf9", display: "block" }} />
                            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                              {/* Replace image */}
                              <label htmlFor={`img-replace-${idx}`}
                                style={{ background: "#e3f2fd", color: "#1565c0", border: "1.5px solid #90caf9", borderRadius: 8, padding: "5px 14px", fontWeight: 600, cursor: "pointer", fontSize: "0.82rem" }}>
                                📷 Replace
                              </label>
                              <input id={`img-replace-${idx}`} type="file" accept="image/*" style={{ display: "none" }}
                                onChange={e => { if (e.target.files[0]) handleQuestionImageUpload(idx, e.target.files[0]); e.target.value = ""; }} />
                              {/* Remove image */}
                              <button onClick={() => { updateQuestion(idx, "imageUrl", ""); updateQuestion(idx, "imageMode", "none"); }}
                                style={{ background: "#fce4ec", color: "#c62828", border: "1.5px solid #ef9a9a", borderRadius: 8, padding: "5px 14px", fontWeight: 600, cursor: "pointer", fontSize: "0.82rem" }}>
                                ✕ Remove
                              </button>
                            </div>
                          </div>
                        )}
                      </div>

                      {q.type === "mcq" ? (
                        <div className="mcq-options">
                          {q.options.map((opt, oi) => (
                            <div key={oi} className="mcq-option-row">
                              <input type="radio" name={`correct-${idx}`} checked={Number(q.answer) === oi} onChange={() => updateQuestion(idx, "answer", oi)} />
                              <input type="text" placeholder={`Option ${["A","B","C","D"][oi]} — use $...$ for math`} value={opt} onChange={e => updateOption(idx, oi, e.target.value)} className="option-input" />
                            </div>
                          ))}
                          <p className="option-hint">🔵 Select radio button next to the correct answer</p>
                        </div>
                      ) : (
                        <div className="tf-options">
                          <label className={`tf-btn ${q.answer === "true" ? "selected" : ""}`}><input type="radio" name={`tf-${idx}`} checked={q.answer === "true"} onChange={() => updateQuestion(idx, "answer", "true")} /> ✅ True</label>
                          <label className={`tf-btn ${q.answer === "false" ? "selected" : ""}`}><input type="radio" name={`tf-${idx}`} checked={q.answer === "false"} onChange={() => updateQuestion(idx, "answer", "false")} /> ❌ False</label>
                        </div>
                      )}
                    </>
                  )}
                </div>
              ))}
            </div>
            {/* ── Import Mode Toggle ── */}
            <div style={{ margin: "16px 0 8px", display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
              <button className="add-q-btn mcq" onClick={() => addQuestion("mcq")}>＋ Add MCQ</button>
              <button className="add-q-btn tf" onClick={() => addQuestion("truefalse")}>＋ Add True/False</button>
              <button onClick={() => { setImportMode(!importMode); setImportError(""); }} style={{ background: importMode ? "#e8f5e9" : "linear-gradient(135deg,#1b5e20,#2e7d32)", color: importMode ? "#1b5e20" : "#fff", border: importMode ? "2px solid #a5d6a7" : "none", borderRadius: 8, padding: "8px 18px", fontWeight: 700, cursor: "pointer", fontSize: "0.9rem" }}>
                {importMode ? "✕ Close Import" : "📋 Paste All Questions at Once"}
              </button>
            </div>

            {/* ── Import Panel ── */}
            {importMode && (
              <div style={{ background: "#f0fdf4", border: "1.5px solid #a5d6a7", borderRadius: 14, padding: "20px 22px", marginBottom: 16 }}>
                <h4 style={{ margin: "0 0 6px", color: "#1b5e20" }}>📋 Paste Questions — Bulk Import</h4>
                <p style={{ margin: "0 0 12px", color: "#555", fontSize: "0.83rem" }}>Paste all your MCQ or True/False questions at once. Separate each question with a blank line.</p>

                {/* Format guide */}
                <details style={{ marginBottom: 14 }}>
                  <summary style={{ cursor: "pointer", color: "#1565c0", fontWeight: 600, fontSize: "0.85rem" }}>📖 Show format guide</summary>
                  <div style={{ background: "#1a1a2e", color: "#a5d6a7", borderRadius: 10, padding: "14px 18px", marginTop: 10, fontFamily: "monospace", fontSize: "0.82rem", lineHeight: 1.8, whiteSpace: "pre" }}>{`Q: What is the Poynting vector?
a) S = E × H
b) S = (1/μ₀)(E × B)
c) S = ε₀(E × B)
d) S = E · B
Answer: b

Q: Maxwell's equations apply in free space.
Answer: True

1. The boundary condition for tangential E is?
a) E1t ≠ E2t
b) E1t = E2t
c) E1t + E2t = 0
d) None
Ans: b`}</div>
                  <p style={{ margin: "8px 0 0", color: "#888", fontSize: "0.8rem" }}>
                    ✅ <strong>Q:</strong> or <strong>1.</strong> for question &nbsp;|&nbsp; <strong>a) b) c) d)</strong> for options &nbsp;|&nbsp; <strong>Answer: b</strong> or <strong>Ans: b</strong> for correct answer
                  </p>
                </details>

                <textarea
                  value={importText}
                  onChange={e => setImportText(e.target.value)}
                  placeholder={`Q: What is the Poynting vector?\na) S = E × H\nb) S = (1/μ₀)(E × B)\nc) S = ε₀(E × B)\nd) S = E · B\nAnswer: b\n\nQ: Displacement current exists in a conductor.\nAnswer: False`}
                  style={{ width: "100%", minHeight: 260, borderRadius: 10, border: "1.5px solid #a5d6a7", padding: "12px 14px", fontFamily: "monospace", fontSize: "0.85rem", lineHeight: 1.7, resize: "vertical", boxSizing: "border-box", background: "#fff" }}
                />
                {importError && <p style={{ color: "#c62828", fontWeight: 600, margin: "6px 0 0" }}>⚠️ {importError}</p>}
                <div style={{ display: "flex", gap: 10, marginTop: 12 }}>
                  <button onClick={parseImportedQuestions} style={{ background: "linear-gradient(135deg,#1565c0,#1b5e20)", color: "#fff", border: "none", borderRadius: 10, padding: "10px 24px", fontWeight: 700, cursor: "pointer", fontSize: "0.95rem" }}>
                    ⚡ Parse & Add All Questions
                  </button>
                  <button onClick={() => setImportText("")} style={{ background: "#fff", color: "#888", border: "1.5px solid #ddd", borderRadius: 10, padding: "10px 18px", fontWeight: 600, cursor: "pointer" }}>Clear</button>
                </div>
              </div>
            )}
            <div className="quiz-step-nav">
              <button type="button" className="quiz-nav-btn back" onClick={() => setQuizStep(1)}>← Back</button>
              <button type="button" className="quiz-nav-btn next" onClick={() => { if (questions.length === 0) return alert("Add at least one question."); setQuizStep(3); }}>Next →</button>
            </div>
            </>)}
            {quizStep === 3 && (<>
            <div className="quiz-summary">
              <span><strong>{quizTitle || "Untitled quiz"}</strong></span>
              <span>📚 {quizClass}</span>
              <span>🏷 {quizBatch === "Both" ? "Both batches" : `Batch ${quizBatch}`}</span>
              <span>❓ {questions.length} question{questions.length === 1 ? "" : "s"}</span>
            </div>
            <div className="form-row">
              <label>Time Limit (minutes):</label>
              <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginTop: 4 }}>
                <input type="number" min="1" max="180" value={quizTime} onChange={e => setQuizTime(e.target.value)} style={{ width: "100px" }} />
                {questions.length > 0 && (
                  <button type="button"
                    onClick={() => setQuizTime(Math.max(1, questions.length * 2))}
                    style={{ background: "rgba(59,130,246,0.18)", color: "#93c5fd", border: "1.5px solid rgba(59,130,246,0.4)", borderRadius: 8, padding: "5px 14px", fontSize: "0.82rem", cursor: "pointer", fontWeight: 600 }}>
                    ⚡ Auto-set ({questions.length * 2} min for {questions.length} Q{questions.length > 1 ? "s" : ""})
                  </button>
                )}
                {questions.length === 0 && (
                  <span style={{ color: "rgba(255,255,255,0.4)", fontSize: "0.8rem" }}>💡 Add questions first to use Auto-set</span>
                )}
              </div>
            </div>
            {/* ── Access Time Window ── */}
            {(() => {
              // Helpers to split/combine "YYYY-MM-DDTHH:mm" strings
              const getDate = val => val ? val.substring(0, 10) : "";
              const getTime = val => val ? val.substring(11, 16) : "";
              const combine = (date, time) => date ? `${date}T${time || "00:00"}` : "";

              const fromDate = getDate(quizOpenFrom);
              const fromTime = getTime(quizOpenFrom);
              const untilDate = getDate(quizOpenUntil);
              const untilTime = getTime(quizOpenUntil);

              const inputStyle = { padding: "8px 10px", borderRadius: 8, fontSize: "0.88rem", boxSizing: "border-box", width: "100%", background: "rgba(255,255,255,0.07)", color: "#fff" };

              return (
                <div style={{ background: "rgba(59,130,246,0.08)", border: "1.5px solid rgba(59,130,246,0.3)", borderRadius: 12, padding: "14px 18px", marginBottom: 12 }}>
                  <p style={{ margin: "0 0 6px", fontWeight: 700, color: "#93c5fd", fontSize: "0.92rem" }}>⏰ Quiz Access Window (optional)</p>
                  <p style={{ margin: "0 0 14px", color: "rgba(255,255,255,0.5)", fontSize: "0.81rem" }}>Set when students can access this quiz. Leave blank to keep it always open.</p>

                  {/* Opens From row */}
                  <div style={{ marginBottom: 12 }}>
                    <label style={{ display: "block", fontWeight: 700, color: "#93c5fd", fontSize: "0.85rem", marginBottom: 6 }}>📅 Opens From</label>
                    <div style={{ display: "flex", gap: 10 }}>
                      <input type="date" value={fromDate}
                        onChange={e => setQuizOpenFrom(combine(e.target.value, fromTime))}
                        style={{ ...inputStyle, flex: 2, border: "1.5px solid rgba(59,130,246,0.4)" }} />
                      <input type="time" value={fromTime}
                        onChange={e => setQuizOpenFrom(combine(fromDate, e.target.value))}
                        style={{ ...inputStyle, flex: 1, border: "1.5px solid rgba(59,130,246,0.4)" }}
                        disabled={!fromDate} />
                    </div>
                    {fromDate && <p style={{ margin: "4px 0 0", color: "#93c5fd", fontSize: "0.78rem" }}>
                      Opens: {new Date(quizOpenFrom).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
                    </p>}
                  </div>

                  {/* Closes At row */}
                  <div style={{ marginBottom: 10 }}>
                    <label style={{ display: "block", fontWeight: 700, color: "#fca5a5", fontSize: "0.85rem", marginBottom: 6 }}>🔒 Closes At</label>
                    <div style={{ display: "flex", gap: 10 }}>
                      <input type="date" value={untilDate}
                        onChange={e => setQuizOpenUntil(combine(e.target.value, untilTime))}
                        style={{ ...inputStyle, flex: 2, border: "1.5px solid rgba(239,68,68,0.4)" }} />
                      <input type="time" value={untilTime}
                        onChange={e => setQuizOpenUntil(combine(untilDate, e.target.value))}
                        style={{ ...inputStyle, flex: 1, border: "1.5px solid rgba(239,68,68,0.4)" }}
                        disabled={!untilDate} />
                    </div>
                    {untilDate && <p style={{ margin: "4px 0 0", color: "#fca5a5", fontSize: "0.78rem" }}>
                      Closes: {new Date(quizOpenUntil).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
                    </p>}
                  </div>

                  {/* Summary */}
                  {quizOpenFrom && quizOpenUntil && new Date(quizOpenUntil) > new Date(quizOpenFrom) && (
                    <p style={{ margin: "8px 0 0", color: "#86efac", fontSize: "0.82rem", fontWeight: 600 }}>
                      ✅ Quiz active for {Math.round((new Date(quizOpenUntil) - new Date(quizOpenFrom)) / 3600000 * 10) / 10} hours
                    </p>
                  )}
                  {quizOpenFrom && quizOpenUntil && new Date(quizOpenUntil) <= new Date(quizOpenFrom) && (
                    <p style={{ margin: "8px 0 0", color: "#fca5a5", fontSize: "0.82rem", fontWeight: 600 }}>
                      ⚠️ Close time must be after open time
                    </p>
                  )}
                  {(quizOpenFrom || quizOpenUntil) && (
                    <button onClick={() => { setQuizOpenFrom(""); setQuizOpenUntil(""); }}
                      style={{ marginTop: 10, background: "none", border: "none", color: "rgba(255,255,255,0.4)", cursor: "pointer", fontSize: "0.8rem" }}>
                      ✕ Clear time window
                    </button>
                  )}
                </div>
              );
            })()}

            <details className="quiz-more">
              <summary>⚙️ More options (share with teachers, rules)</summary>
            {/* ── Share with Teachers ── */}
            <div style={{ background: "#e8f5e9", border: "1.5px solid #a5d6a7", borderRadius: 10, padding: "10px 16px", marginBottom: 12 }}>
              <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", fontSize: "0.9rem", color: "#1b5e20", fontWeight: 600 }}>
                <input
                  type="checkbox"
                  checked={quizShareWithTeachers}
                  onChange={e => setQuizShareWithTeachers(e.target.checked)}
                  style={{ width: 18, height: 18, cursor: "pointer" }}
                />
                <span>🤝 Share this quiz with all teachers</span>
              </label>
              <p style={{ margin: "6px 0 0 28px", fontSize: "0.78rem", color: "#2e7d32" }}>
                When ON, teachers can find this quiz in their <strong>📚 My Classroom → Quizzes → 📥 Shared Library</strong> and clone it for their own class.
              </p>
            </div>
            {/* ── Exam Rules Summary ── */}
            <div style={{ background: "#f3e5f5", border: "1.5px solid #ce93d8", borderRadius: 10, padding: "10px 16px", marginBottom: 12, fontSize: "0.83rem", color: "#4a148c" }}>
              <strong>📋 Enforced Rules:</strong> &nbsp;
              🔒 <strong>1 attempt per student</strong> &nbsp;|&nbsp;
              ⏰ <strong>Access window</strong> blocks quiz before/after set dates &nbsp;|&nbsp;
              ⚡ <strong>Auto-time</strong> = 2 min × number of questions
            </div>
            </details>
            <div className="quiz-step-nav">
              <button type="button" className="quiz-nav-btn back" onClick={() => setQuizStep(2)}>← Back</button>

            </div>
            <button type="button" className="quiz-preview-btn" onClick={() => { setPreviewAnswers(false); setShowQuizPreview(true); }} disabled={questions.length === 0}>👁 Preview full quiz (as students see it)</button>
            {showQuizPreview && (
              <div className="qp-overlay" onClick={() => setShowQuizPreview(false)}>
                <div className="qp-card" onClick={e => e.stopPropagation()}>
                  <div className="qp-head">
                    <div>
                      <h3>{quizTitle || "Untitled quiz"}</h3>
                      <p>📚 {quizClass} · 🏷 {quizBatch === "Both" ? "Both batches" : `Batch ${quizBatch}`} · ⏱ {quizTime} min · ❓ {questions.length} questions</p>
                    </div>
                    <button className="qp-close" onClick={() => setShowQuizPreview(false)}>✕</button>
                  </div>
                  <label className="qp-toggle"><input type="checkbox" checked={previewAnswers} onChange={e => setPreviewAnswers(e.target.checked)} /> Show correct answers</label>
                  {questions.map((q, i) => (
                    <div key={i} className="qp-q">
                      <p className="qp-qtext"><strong>Q{i + 1}.</strong> <LatexRenderer text={q.question || "(empty question)"} /></p>
                      {q.imageUrl && <img src={q.imageUrl} alt="" className="question-img" style={{ maxWidth: "100%", borderRadius: 8, margin: "6px 0" }} />}
                      <div className="quiz-options">
                        {(q.type === "truefalse" ? ["True", "False"] : q.options).map((opt, oi) => {
                          const correct = q.type === "truefalse" ? (q.answer === (oi === 0 ? "true" : "false")) : Number(q.answer) === oi;
                          return (
                            <div key={oi} className={`quiz-option${previewAnswers && correct ? " qp-correct" : ""}`} style={{ cursor: "default" }}>
                              <span className="option-letter">{q.type === "truefalse" ? (oi === 0 ? "T" : "F") : ["A", "B", "C", "D"][oi]}</span>
                              <LatexRenderer text={opt || `(empty option ${["A","B","C","D"][oi]})`} />
                              {previewAnswers && correct && <span style={{ marginLeft: "auto" }}>✅</span>}
                            </div>
                          );
                        })}
                      </div>
                      {!q.question?.trim() && <p className="qp-warn">⚠️ Question text is empty</p>}
                      {q.type === "mcq" && q.options.some(o => !String(o || "").trim()) && <p className="qp-warn">⚠️ Some options are empty</p>}
                    </div>
                  ))}
                  <div className="qp-foot">
                    <button onClick={() => setShowQuizPreview(false)} style={{ background: "#fff", color: "#1a237e", border: "2px solid #1a237e", borderRadius: 10, padding: "10px 18px", fontWeight: 700, cursor: "pointer" }}>✏️ Keep editing</button>
                    <button className="save-btn" style={{ width: "auto", margin: 0 }} onClick={() => { setShowQuizPreview(false); handleSaveQuiz(); }} disabled={savingQuiz}>{editingQuizId ? "💾 Looks good — Update Quiz" : "💾 Looks good — Save Quiz"}</button>
                  </div>
                </div>
              </div>
            )}
            <button className="save-btn" onClick={handleSaveQuiz} disabled={savingQuiz}
              style={editingQuizId ? { background: "linear-gradient(135deg,#e65100,#f57c00)" } : {}}>
              {savingQuiz ? (editingQuizId ? "Updating..." : "Saving...") : (editingQuizId ? "💾 Update Quiz" : "💾 Save Quiz")}
            </button>
            </>)}
          </div>

          <div className="quiz-existing">
            <h3>📋 Existing Quizzes ({quizList.length})</h3>
            {quizList.length === 0 ? <p style={{ color: "#999" }}>No quizzes yet.</p> : quizList.map(qz => (
              <div key={qz.id} className="manage-row">
                <div>
                  <strong>{qz.title}</strong>
                  <span className="quiz-meta-small"> — {qz.class} | ⏱ {qz.timeLimit} min | ❓ {qz.questions?.length} Qs</span>
                  <span className={`quiz-batch-tag ${qz.assignedBatch === "C1" ? "c1" : qz.assignedBatch === "C2" ? "c2" : "both"}`}>{qz.assignedBatch === "Both" ? "🌐 Both" : `🏷 ${qz.assignedBatch}`}</span>
                  {(qz.openFrom || qz.openUntil) && (
                    <span style={{ marginLeft: 8, fontSize: "0.78rem", color: "#1565c0", background: "#e3f2fd", borderRadius: 8, padding: "2px 8px" }}>
                      ⏰ {qz.openFrom ? new Date(qz.openFrom).toLocaleString("en-IN", { dateStyle: "short", timeStyle: "short" }) : "∞"} → {qz.openUntil ? new Date(qz.openUntil).toLocaleString("en-IN", { dateStyle: "short", timeStyle: "short" }) : "∞"}
                    </span>
                  )}
                  <span style={{ marginLeft: 8, fontSize: "0.78rem", color: "#7b1fa2", background: "#f3e5f5", borderRadius: 8, padding: "2px 8px" }}>🔒 1 attempt</span>
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <button onClick={() => handleEditQuiz(qz)}
                    style={{ background: "#e3f2fd", color: "#1565c0", border: "1.5px solid #90caf9", borderRadius: 8, padding: "6px 16px", fontWeight: 700, cursor: "pointer", fontSize: "0.85rem" }}>
                    ✏️ Edit
                  </button>
                  <button className="delete-btn" onClick={() => handleDeleteQuiz(qz.id)}>🗑 Delete</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {activeTab === "results" && (
        <div className="results-section">
          <div className="results-header">
            <div><h3>📊 Student Quiz Results</h3><span className="user-count">{filteredResults.length} submissions</span></div>
            <div className="results-controls">
              <select className="results-filter" value={filterBatch} onChange={e => setFilterBatch(e.target.value)}><option value="all">All Batches</option><option value="C1">Batch C1</option><option value="C2">Batch C2</option></select>
              <select className="results-filter" value={filterQuiz} onChange={e => setFilterQuiz(e.target.value)}><option value="all">All Quizzes</option>{uniqueQuizTitles.map(t => <option key={t} value={t}>{t}</option>)}</select>
              <button className="export-btn" onClick={exportToExcel} disabled={exporting || results.length === 0}>{exporting ? "⏳ Exporting..." : "📥 Export to Excel"}</button>
            </div>
          </div>
          {/* ── Clear Results Controls ── */}
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 14, padding: "12px 16px", background: "#fff3e0", border: "1.5px solid #ffcc80", borderRadius: 10 }}>
            <span style={{ fontSize: "0.85rem", color: "#e65100", fontWeight: 700, alignSelf: "center" }}>🔄 Reset Attempts:</span>
            {filterQuiz !== "all" && (
              <button
                onClick={() => handleClearResults(
                  `"${filterQuiz}"`,
                  filteredResults
                )}
                disabled={clearingResults || filteredResults.length === 0}
                style={{ background: "#fff8e1", color: "#f57c00", border: "1.5px solid #ffb300", borderRadius: 8, padding: "6px 16px", fontWeight: 700, cursor: "pointer", fontSize: "0.85rem" }}
              >
                {clearingResults ? "⏳ Deleting..." : `🗑 Clear "${filterQuiz}" (${filteredResults.length})`}
              </button>
            )}
            <button
              onClick={() => handleClearResults("ALL quizzes", results)}
              disabled={clearingResults || results.length === 0}
              style={{ background: "#fce4ec", color: "#c62828", border: "1.5px solid #ef9a9a", borderRadius: 8, padding: "6px 16px", fontWeight: 700, cursor: "pointer", fontSize: "0.85rem" }}
            >
              {clearingResults ? "⏳ Deleting..." : `🗑 Clear ALL Results (${results.length})`}
            </button>
            <span style={{ fontSize: "0.78rem", color: "#888", alignSelf: "center" }}>Students can retake after clearing.</span>
          </div>
          {resultsLoading ? <div className="loading">Loading...</div> : filteredResults.length === 0 ? <p style={{ color: "#999" }}>No results found.</p> : (
            <div style={{ overflowX: "auto" }}>
              <table className="users-table">
                <thead><tr><th>#</th><th>Student</th><th>Email</th><th>Batch</th><th>Quiz</th><th>Score</th><th>%</th><th>Grade</th><th>Submitted</th></tr></thead>
                <tbody>{filteredResults.map((r, i) => {
                  const grade = r.percent >= 90 ? { label: "Excellent", color: "#2e7d32" } : r.percent >= 75 ? { label: "Good", color: "#1565c0" } : r.percent >= 50 ? { label: "Average", color: "#f57c00" } : { label: "Below Avg", color: "#c62828" };
                  return <tr key={r.id}><td>{i + 1}</td><td>{r.studentName}</td><td style={{ fontSize: "0.82rem" }}>{r.studentEmail}</td><td><span className={`batch-chip ${r.studentBatch === "C1" ? "c1" : "c2"}`}>{r.studentBatch || "—"}</span></td><td style={{ fontSize: "0.82rem" }}>{r.quizTitle}</td><td style={{ fontWeight: 700 }}>{r.score}/{r.total}</td><td style={{ fontWeight: 700, color: grade.color }}>{r.percent}%</td><td style={{ color: grade.color, fontWeight: 600 }}>{grade.label}</td><td style={{ fontSize: "0.8rem" }}>{r.status === "in_progress" ? <span style={{ color: "#f57c00", fontWeight: 700 }}>⏳ In progress</span> : formatDate(r.submittedAt)}{r.violations > 0 && <div style={{ color: "#e65100", fontWeight: 600 }}>⚠️ Left screen {r.violations}×</div>}{r.autoSubmitted && <div style={{ color: "#c62828" }}>{r.autoSubmitted}</div>}</td></tr>;
                })}</tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {activeTab === "attendance" && <AttendanceTab />}
      {activeTab === "classmap" && <ClassMappingPanel user={user} />}
      {activeTab === "broadcast" && <BroadcastPanel user={user} />}

      {/* ── Image Crop Modal (rendered at admin panel level so it covers everything) ── */}
      {cropModal.open && (
        <ImageCropModal
          src={cropModal.src}
          uploading={cropModal.uploading}
          onConfirm={handleCropConfirm}
          onSkip={handleCropSkip}
          onCancel={handleCropCancel}
        />
      )}

      {activeTab === "roster" && (
        <div className="roster-section">
          <div className="roster-upload-box">
            {/* ── Semester selector ── */}
            <div className="sem-bar">
              <div className="sem-row">
                <label className="sem-label">📅 Semester</label>
                <select value={rosterSemester} onChange={e => setRosterSemester(e.target.value)} className="sem-select">
                  {semesters.length === 0 && <option value="">— create one below —</option>}
                  {semesters.map(sm => <option key={sm} value={sm}>{sm}{sm === activeSemester ? "  (current)" : ""}</option>)}
                </select>
                {rosterSemester && rosterSemester !== activeSemester && <button className="sem-btn" onClick={() => handleSetActiveSemester(rosterSemester)}>⭐ Make current</button>}
                {rosterSemester && rosterSemester === activeSemester && <span className="sem-current">⭐ Current semester</span>}
              </div>
              <div className="sem-row">
                <input value={newSemester} onChange={e => setNewSemester(e.target.value)} placeholder="New semester, e.g. AY_26_27_SEM_I" className="sem-input" onKeyDown={e => e.key === "Enter" && handleAddSemester()} />
                <button className="sem-btn primary" onClick={handleAddSemester}>➕ Add semester</button>
              </div>
              <p className="sem-hint">Uploads go into the selected semester. The <strong>current</strong> semester's students are used for Attendance and bulk access.</p>
            </div>

            <h3>📤 Upload Student Roster{rosterSemester ? ` → ${rosterSemester}` : ""}</h3>
            <p className="roster-desc">Upload Excel: <strong>Student ID, Name, Email, Batch (C1 or C2), Branch (CSE, ECE, EEE, ME, CE, CHE, MME)</strong></p>
            <button className="template-btn" onClick={downloadTemplate}>📥 Download Template</button>
            <div className="form-row" style={{ marginTop: 16 }}><label>Select Excel (.xlsx):</label><input ref={rosterInputRef} type="file" accept=".xlsx" onChange={handleRosterFileChange} /></div>

            {/* ── Access Level Selection ── */}
            <div style={{ background: "#e8f5e9", border: "1.5px solid #a5d6a7", borderRadius: 12, padding: "14px 18px", marginTop: 16 }}>
              <p style={{ margin: "0 0 10px", fontWeight: 700, color: "#1b5e20", fontSize: "0.92rem" }}>🔑 Grant Access While Uploading (optional)</p>
              <p style={{ margin: "0 0 12px", color: "#555", fontSize: "0.82rem" }}>Students in this list will automatically get access to selected content. You can change this later from the Student Access tab.</p>
              <div style={{ display: "flex", gap: 20, flexWrap: "wrap" }}>
                {[["E1","⚡ Engineering Physics","#1565c0","#e3f2fd"],["P1","📘 PUC-I","#2e7d32","#e8f5e9"],["P2","📗 PUC-II","#e65100","#fff3e0"],["S1","🔬 Simulations","#6a1b9a","#f3e5f5"]].map(([code, label, color, bg]) => (
                  <label key={code} style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", background: rosterAccessLevels[code] ? bg : "#fff", border: `1.5px solid ${rosterAccessLevels[code] ? color : "#e0e0e0"}`, borderRadius: 10, padding: "8px 16px", fontWeight: 700, color: rosterAccessLevels[code] ? color : "#888", transition: "all 0.15s" }}>
                    <input type="checkbox" checked={rosterAccessLevels[code]} onChange={e => setRosterAccessLevels(prev => ({ ...prev, [code]: e.target.checked }))} style={{ width: 16, height: 16 }} />
                    {label}
                  </label>
                ))}
              </div>
              {Object.values(rosterAccessLevels).every(v => !v) && (
                <p style={{ margin: "10px 0 0", color: "#999", fontSize: "0.78rem" }}>⚠️ No access selected — students will be added to roster only, without content access.</p>
              )}
            </div>

            {rosterPreview.length > 0 && (
              <div className="roster-preview">
                <p className="roster-preview-label">📋 Preview (first 5 rows):</p>
                <table className="users-table"><thead><tr><th>ID</th><th>Name</th><th>Email</th><th>Batch</th><th>Branch</th></tr></thead><tbody>{rosterPreview.map((s, i) => <tr key={i}><td>{s.studentId}</td><td>{s.name}</td><td>{s.email}</td><td><span className={`batch-chip ${s.batch === "C1" ? "c1" : "c2"}`}>{s.batch}</span></td><td>{s.branch || "—"}</td></tr>)}</tbody></table>
                <div style={{ marginTop: 14, display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                  <button className="save-btn" onClick={handleUploadRoster} disabled={uploadingRoster}>{uploadingRoster ? "⏳ Uploading..." : "☁️ Upload to Firebase"}</button>
                  {Object.keys(rosterAccessLevels).filter(k => rosterAccessLevels[k]).length > 0 && (
                    <span style={{ color: "#2e7d32", fontWeight: 600, fontSize: "0.85rem" }}>
                      ✅ Will grant: {Object.keys(rosterAccessLevels).filter(k => rosterAccessLevels[k]).join(", ")}
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
          <div className="roster-list">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
              <h3>🎓 {rosterSemester || "Roster"} ({branchRoster.length}{rosterBranch !== "all" ? ` of ${semRoster.length}` : ""})</h3>
              <div style={{ display: "flex", gap: 8 }}><span className="batch-chip c1">C1: {semRoster.filter(s => s.batch === "C1").length}</span><span className="batch-chip c2">C2: {semRoster.filter(s => s.batch === "C2").length}</span></div>
            </div>
            {Object.keys(branchCounts).length > 1 && (
              <div className="bulk-filters" style={{ marginBottom: 12 }}>
                {[["all", `All (${semRoster.length})`], ...Object.keys(branchCounts).sort().map(b => [b, `${b} (${branchCounts[b]})`])].map(([k, lbl]) => (
                  <button key={k} className={`bulk-chip ${rosterBranch === k ? "active" : ""}`} onClick={() => setRosterBranch(k)}>{lbl}</button>
                ))}
              </div>
            )}
            {rosterLoading ? <div className="loading">Loading...</div> : semRoster.length === 0 ? <p style={{ color: "rgba(255,255,255,0.45)" }}>No students in {rosterSemester || "this semester"} yet.</p> : (
              <div style={{ overflowX: "auto" }}><table className="users-table"><thead><tr><th>#</th><th>ID</th><th>Name</th><th>Email</th><th>Batch</th><th>Branch</th></tr></thead><tbody>{branchRoster.map((s, i) => <tr key={s.id}><td>{i + 1}</td><td>{s.studentId}</td><td>{s.name}</td><td style={{ fontSize: "0.82rem" }}>{s.email}</td><td><span className={`batch-chip ${s.batch === "C1" ? "c1" : "c2"}`}>{s.batch}</span></td><td>{s.branch || "—"}</td></tr>)}</tbody></table></div>
            )}
          </div>
        </div>
      )}

      {activeTab === "users" && (
        <div className="users-section">
          <div className="users-header"><h3>👥 Registered Students</h3><span className="user-count">Total: {users.length}</span></div>
          {users.length === 0 ? <p>No students yet.</p> : <table className="users-table"><thead><tr><th>#</th><th>Name</th><th>Email</th><th>Last Login</th></tr></thead><tbody>{users.map((u, i) => <tr key={u.email}><td>{i + 1}</td><td>{u.name}</td><td>{u.email}</td><td>{formatDate(u.lastLogin)}</td></tr>)}</tbody></table>}
        </div>
      )}

      {activeTab === "requests" && (
        <div className="users-section">
          <div className="users-header">
            <h3>🔔 Access Requests</h3>
            <span className="user-count">{accessRequests.filter(r => r.status === "pending").length} pending</span>
          </div>
          {requestsLoading ? <div className="loading">Loading...</div> : accessRequests.length === 0 ? (
            <p style={{ color: "rgba(255,255,255,0.45)" }}>No access requests yet.</p>
          ) : (
            <div>
              {/* Pending requests first */}
              {accessRequests.filter(r => r.status === "pending").length > 0 && (
                <div style={{ marginBottom: 24 }}>
                  <h4 style={{ color: "#f5c518", marginBottom: 12 }}>⏳ Pending ({accessRequests.filter(r => r.status === "pending").length})</h4>
                  {(() => {
                    const pending = accessRequests.filter(r => r.status === "pending");
                    const counts = pending.reduce((m, r) => { m[r.classCode] = (m[r.classCode] || 0) + 1; return m; }, {});
                    const visible = pending.filter(r => reqClassFilter === "all" || r.classCode === reqClassFilter);
                    const visibleIds = visible.map(r => r.id);
                    const selected = visible.filter(r => selectedReqIds.includes(r.id));
                    const allChecked = visible.length > 0 && selected.length === visible.length;
                    const labels = { E1: "Engg. Physics", P1: "PUC-I", P2: "PUC-II" };
                    return (
                      <div className="bulk-bar">
                        <div className="bulk-filters">
                          {[["all", `All (${pending.length})`], ...Object.keys(counts).sort().map(c => [c, `${labels[c] || c} (${counts[c]})`])].map(([k, lbl]) => (
                            <button key={k} className={`bulk-chip ${reqClassFilter === k ? "active" : ""}`} onClick={() => { setReqClassFilter(k); setSelectedReqIds([]); }}>{lbl}</button>
                          ))}
                        </div>
                        <div className="bulk-actions">
                          <label className="bulk-selectall">
                            <input type="checkbox" checked={allChecked} onChange={() => setSelectedReqIds(allChecked ? [] : visibleIds)} />
                            Select all ({visible.length})
                          </label>
                          <span className="bulk-count">{selected.length} selected</span>
                          <button className="bulk-btn approve" disabled={bulkBusy || selected.length === 0} onClick={() => handleBulkApprove(selected)}>{bulkBusy ? "Working…" : `✅ Approve selected (${selected.length})`}</button>
                          <button className="bulk-btn reject" disabled={bulkBusy || selected.length === 0} onClick={() => handleBulkReject(selected)}>❌ Reject selected</button>
                        </div>
                      </div>
                    );
                  })()}
                  {accessRequests.filter(r => r.status === "pending" && (reqClassFilter === "all" || r.classCode === reqClassFilter)).map(req => (
                    <div key={req.id} onClick={e => { if (e.target.tagName !== "BUTTON" && e.target.tagName !== "INPUT") toggleReqSelect(req.id); }} style={{ background: selectedReqIds.includes(req.id) ? "rgba(46,125,50,0.18)" : "rgba(245,197,24,0.07)", border: selectedReqIds.includes(req.id) ? "1.5px solid #43a047" : "1.5px solid rgba(245,197,24,0.35)", borderRadius: 12, padding: "16px 20px", marginBottom: 12, display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap", cursor: "pointer" }}>
                      <input type="checkbox" checked={selectedReqIds.includes(req.id)} onChange={() => toggleReqSelect(req.id)} style={{ width: 20, height: 20, cursor: "pointer", accentColor: "#43a047" }} />
                      {req.photoURL && <img src={req.photoURL} alt="" style={{ width: 42, height: 42, borderRadius: "50%", objectFit: "cover" }} />}
                      <div style={{ flex: 1, minWidth: 200 }}>
                        <p style={{ margin: "0 0 2px", fontWeight: 700, color: "#fde68a" }}>{req.name || req.email}</p>
                        <p style={{ margin: "0 0 2px", color: "rgba(255,255,255,0.6)", fontSize: "0.84rem" }}>{req.email}</p>
                        <p style={{ margin: 0, color: "rgba(255,255,255,0.5)", fontSize: "0.8rem" }}>
                          Requesting: <strong style={{ color: "#93c5fd" }}>{req.className}</strong> ({req.classCode}) · {req.requestedAt ? new Date(req.requestedAt).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}
                        </p>
                      </div>
                      <div style={{ display: "flex", gap: 8 }}>
                        <button onClick={() => handleApproveRequest(req)} style={{ background: "#2e7d32", color: "#fff", border: "none", borderRadius: 8, padding: "8px 18px", fontWeight: 700, cursor: "pointer", fontSize: "0.88rem" }}>✅ Approve</button>
                        <button onClick={() => handleRejectRequest(req)} style={{ background: "#c62828", color: "#fff", border: "none", borderRadius: 8, padding: "8px 18px", fontWeight: 700, cursor: "pointer", fontSize: "0.88rem" }}>❌ Reject</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {/* Reviewed requests */}
              {accessRequests.filter(r => r.status !== "pending").length > 0 && (
                <div>
                  <h4 style={{ color: "rgba(255,255,255,0.55)", marginBottom: 12 }}>📋 Previously Reviewed</h4>
                  <table className="users-table">
                    <thead><tr><th>#</th><th>Name</th><th>Email</th><th>Class</th><th>Status</th><th>Date</th></tr></thead>
                    <tbody>
                      {accessRequests.filter(r => r.status !== "pending").map((req, i) => (
                        <tr key={req.id}>
                          <td>{i + 1}</td>
                          <td>{req.name || "—"}</td>
                          <td style={{ fontSize: "0.82rem" }}>{req.email}</td>
                          <td><span style={{ background: "rgba(59,130,246,0.22)", color: "#93c5fd", border: "1px solid rgba(59,130,246,0.4)", borderRadius: 20, padding: "2px 10px", fontSize: "0.78rem", fontWeight: 700 }}>{req.classCode}</span></td>
                          <td>
                            <span style={{ background: req.status === "approved" ? "rgba(34,197,94,0.18)" : "rgba(239,68,68,0.18)", color: req.status === "approved" ? "#86efac" : "#fca5a5", border: `1px solid ${req.status === "approved" ? "rgba(34,197,94,0.4)" : "rgba(239,68,68,0.4)"}`, borderRadius: 20, padding: "2px 10px", fontSize: "0.78rem", fontWeight: 700 }}>
                              {req.status === "approved" ? "✅ Approved" : "❌ Rejected"}
                            </span>
                          </td>
                          <td style={{ fontSize: "0.78rem", color: "rgba(255,255,255,0.45)" }}>{req.reviewedAt ? new Date(req.reviewedAt).toLocaleDateString("en-IN") : "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {activeTab === "activity" && (
        <StudentActivityTracker isAdmin={true} />
      )}

      {activeTab === "visitors" && <VisitorsPanel log={visitLog} loading={visitLogLoading} onRefresh={fetchVisitLog} />}

      {activeTab === "appanalytics" && (
        <div className="users-section">
          {/* Summary cards */}
          <div style={{ display: "flex", gap: 16, marginBottom: 24, flexWrap: "wrap" }}>
            <div style={{ background: "#fff3e0", border: "1.5px solid #ffb74d", borderRadius: 12, padding: "16px 24px", flex: 1, minWidth: 150, textAlign: "center" }}>
              <div style={{ fontSize: "2.2rem", fontWeight: 800, color: "#ff6f00" }}>{appInstallations.length}</div>
              <div style={{ fontSize: "0.82rem", color: "#e65100", fontWeight: 600 }}>📱 App Installations</div>
            </div>
            <div style={{ background: "#e8f5e9", border: "1.5px solid #a5d6a7", borderRadius: 12, padding: "16px 24px", flex: 1, minWidth: 150, textAlign: "center" }}>
              <div style={{ fontSize: "2.2rem", fontWeight: 800, color: "#2e7d32" }}>
                {appInstallations.reduce((sum, app) => sum + (app.openCount || 1), 0)}
              </div>
              <div style={{ fontSize: "0.82rem", color: "#388e3c", fontWeight: 600 }}>🔄 Total Opens</div>
            </div>
            <div style={{ background: "#e3f2fd", border: "1.5px solid #90caf9", borderRadius: 12, padding: "16px 24px", flex: 1, minWidth: 150, textAlign: "center" }}>
              <div style={{ fontSize: "2.2rem", fontWeight: 800, color: "#1565c0" }}>
                {appInstallations.filter(app => /mobile|android|iphone/i.test(app.userAgent || "")).length}
              </div>
              <div style={{ fontSize: "0.82rem", color: "#1976d2", fontWeight: 600 }}>📲 Mobile Users</div>
            </div>
          </div>

          {/* App Installations Table */}
          <div className="users-header">
            <h3>📱 App Installations & Usage</h3>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span className="user-count">{appInstallations.length} users with installed app</span>
              <button onClick={fetchAppInstallations} disabled={appAnalyticsLoading} style={{ background: "#ff6f00", color: "#fff", border: "none", borderRadius: 8, padding: "5px 14px", fontSize: "0.82rem", fontWeight: 700, cursor: "pointer" }}>
                🔄 Refresh
              </button>
            </div>
          </div>

          {appAnalyticsLoading ? (
            <div className="loading">Loading...</div>
          ) : appInstallations.length === 0 ? (
            <p style={{ color: "#999" }}>No app installations yet. Users who open the app from their home screen will appear here.</p>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table className="users-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Device</th>
                    <th>Installed</th>
                    <th>Last Opened</th>
                    <th>Opens</th>
                  </tr>
                </thead>
                <tbody>
                  {appInstallations.map((app, i) => (
                    <tr key={app.id}>
                      <td>{i + 1}</td>
                      <td style={{ fontWeight: 600 }}>{app.name || "—"}</td>
                      <td style={{ fontSize: "0.82rem", color: "#546e7a" }}>{app.email}</td>
                      <td>
                        <span style={{
                          background: app.deviceType === "Mobile" ? "rgba(33,150,243,0.18)" : "rgba(76,175,80,0.18)",
                          color: app.deviceType === "Mobile" ? "#42a5f5" : "#66bb6a",
                          border: app.deviceType === "Mobile" ? "1px solid rgba(33,150,243,0.4)" : "1px solid rgba(76,175,80,0.4)",
                          borderRadius: 20,
                          padding: "2px 10px",
                          fontSize: "0.78rem",
                          fontWeight: 700,
                        }}>
                          {app.deviceType === "Mobile" ? "📱" : "💻"} {app.deviceType}
                        </span>
                      </td>
                      <td style={{ fontSize: "0.78rem", color: "rgba(255,255,255,0.45)" }}>
                        {app.installedAt ? new Date(app.installedAt).toLocaleDateString("en-IN") : "—"}
                      </td>
                      <td style={{ fontSize: "0.78rem", color: "rgba(255,255,255,0.45)" }}>
                        {app.lastOpenedAt ? new Date(app.lastOpenedAt).toLocaleDateString("en-IN") : "—"}
                      </td>
                      <td style={{ fontWeight: 700, color: "#ff6f00" }}>
                        {app.openCount || 1} {(app.openCount || 1) === 1 ? "open" : "opens"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Analytics info box */}
          <div style={{
            marginTop: 24,
            padding: "16px 20px",
            background: "rgba(255,152,0,0.08)",
            border: "1.5px solid rgba(255,152,0,0.3)",
            borderRadius: 12,
          }}>
            <p style={{ margin: "0 0 8px", fontWeight: 700, color: "#ff6f00" }}>ℹ️ How This Works</p>
            <ul style={{ margin: 0, paddingLeft: 20, color: "rgba(255,255,255,0.65)", fontSize: "0.88rem", lineHeight: 1.8 }}>
              <li>Users who install Physics Hub to their home screen (iOS Safari or Android Chrome) are tracked</li>
              <li><strong>Device Type:</strong> Automatically detected from user agent</li>
              <li><strong>Opens:</strong> Incremented each time they access the app</li>
              <li><strong>Last Opened:</strong> Updated whenever they access the app in installed mode</li>
              <li>Data is collected from Firebase Analytics and stored in Firestore database</li>
            </ul>
          </div>
        </div>
      )}

      {activeTab === "aboutprof" && (
        <AboutProf userEmail={user?.email} isAdmin={user?.email === ADMIN_EMAIL} />
      )}

      {activeTab === "access" && (
        <div className="users-section">
          <div className="users-header"><h3>🔑 Student Access Management</h3><span className="user-count">{accessList.length} students granted</span></div>
          <SuspendManager onChange={setSuspendedCodes} />
          <div className="add-form" style={{ maxWidth: 560, background: "rgba(34,197,94,0.07)", border: "1.5px solid rgba(34,197,94,0.3)", borderRadius: 14, padding: "20px 24px", marginBottom: 24 }}>
            <h4 style={{ margin: "0 0 14px", color: "#86efac" }}>➕ Grant Access to Student</h4>
            <div className="form-row">
              <label>Student Email:</label>
              <input type="email" placeholder="student@gmail.com or id@rgukt.ac.in" value={accessEmail} onChange={e => setAccessEmail(e.target.value)} style={{ width: "100%", padding: "8px 12px", borderRadius: 8, border: "1.5px solid rgba(34,197,94,0.35)", background: "rgba(255,255,255,0.05)", color: "#fff" }} />
            </div>
            <div className="form-row" style={{ alignItems: "flex-start" }}>
              <label style={{ paddingTop: 4 }}>Grant Access To:</label>
              <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 4 }}>
                {[["E1","⚡ Engineering Physics"],["P1","📘 PUC-I"],["P2","📗 PUC-II"],["S1","🔬 Simulations"]].map(([code, label]) => (
                  <label key={code} style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontWeight: 600, color: "#86efac" }}>
                    <input type="checkbox" checked={accessLevels[code]} onChange={e => setAccessLevels(prev => ({ ...prev, [code]: e.target.checked }))} style={{ width: 18, height: 18 }} />
                    {label}
                  </label>
                ))}
              </div>
            </div>
            <button className="save-btn" style={{ background: "linear-gradient(135deg,#f5c518,#d97706)", color: "#081428", marginTop: 8 }} onClick={handleAddAccess} disabled={savingAccess}>{savingAccess ? "Saving..." : "💾 Save Access"}</button>
          </div>

          {/* Bulk grant by batch */}
          <div className="add-form" style={{ maxWidth: 560, background: "rgba(245,197,24,0.07)", border: "1.5px solid rgba(245,197,24,0.3)", borderRadius: 14, padding: "20px 24px", marginBottom: 24 }}>
            <h4 style={{ margin: "0 0 14px", color: "#f5c518" }}>⚡ Bulk Grant — Entire Batch</h4>
            <div className="form-row">
              <label>Batch:</label>
              <div style={{ display: "flex", gap: 10 }}>
                {["C1", "C2", "Both"].map(b => (
                  <button key={b} onClick={() => setBulkBatch(b)}
                    style={{ padding: "6px 18px", borderRadius: 20, border: "1.5px solid rgba(245,197,24,0.4)", fontWeight: 700, fontSize: "0.9rem", cursor: "pointer",
                      background: bulkBatch === b ? "linear-gradient(135deg,#f5c518,#d97706)" : "rgba(255,255,255,0.05)", color: bulkBatch === b ? "#081428" : "#fde68a" }}>
                    {b}
                  </button>
                ))}
              </div>
            </div>
            <div className="form-row" style={{ alignItems: "flex-start" }}>
              <label style={{ paddingTop: 4 }}>Grant Access To:</label>
              <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 4 }}>
                {[["E1","⚡ Engineering Physics"],["P1","📘 PUC-I"],["P2","📗 PUC-II"],["S1","🔬 Simulations"]].map(([code, label]) => (
                  <label key={code} style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontWeight: 600, color: "#fde68a" }}>
                    <input type="checkbox" checked={bulkAccessLevels[code]} onChange={e => setBulkAccessLevels(prev => ({ ...prev, [code]: e.target.checked }))} style={{ width: 18, height: 18 }} />
                    {label}
                  </label>
                ))}
              </div>
            </div>
            <p style={{ fontSize: "0.8rem", color: "rgba(255,255,255,0.45)", margin: "8px 0 12px" }}>
              Grants access to all students in the selected batch who are in the roster. Existing access is preserved — only new codes are added.
            </p>
            <button className="save-btn" style={{ background: "linear-gradient(135deg,#f5c518,#d97706)", color: "#081428", marginTop: 4 }} onClick={handleBulkGrant} disabled={bulkGranting}>
              {bulkGranting ? "Granting..." : `⚡ Grant to All ${bulkBatch} Students`}
            </button>
          </div>

          {accessLoading ? <div className="loading">Loading...</div> : accessList.length === 0 ? (
            <p style={{ color: "rgba(255,255,255,0.45)" }}>No students have been granted access yet.</p>
          ) : (
            <table className="users-table">
              <thead><tr><th>#</th><th>Email</th><th>Access Granted</th><th>Updated</th><th>Action</th></tr></thead>
              <tbody>
                {accessList.map((s, i) => (
                  <tr key={s.email}>
                    <td>{i + 1}</td>
                    <td style={{ fontSize: "0.85rem" }}>{s.email}</td>
                    <td>
                      {(s.access || []).map(code => (
                        <span key={code} style={{ background: code === "E1" ? "rgba(59,130,246,0.22)" : code === "P1" ? "rgba(34,197,94,0.18)" : code === "P2" ? "rgba(245,197,24,0.18)" : code === "S1" ? "rgba(167,139,250,0.18)" : "rgba(255,255,255,0.08)", color: code === "E1" ? "#93c5fd" : code === "P1" ? "#86efac" : code === "P2" ? "#fde68a" : code === "S1" ? "#c4b5fd" : "rgba(255,255,255,0.7)", border: `1px solid ${code === "E1" ? "rgba(59,130,246,0.4)" : code === "P1" ? "rgba(34,197,94,0.35)" : code === "P2" ? "rgba(245,197,24,0.35)" : code === "S1" ? "rgba(167,139,250,0.35)" : "rgba(255,255,255,0.15)"}`, borderRadius: 20, padding: "2px 10px", fontSize: "0.78rem", fontWeight: 700, marginRight: 4, ...(suspendedCodes.includes(code) ? { textDecoration: "line-through", opacity: 0.55 } : {}) }} title={suspendedCodes.includes(code) ? "Suspended — no access while the class is closed" : ""}>{suspendedCodes.includes(code) ? "⛔ " : ""}{code}</span>
                      ))}
                    </td>
                    <td style={{ fontSize: "0.78rem", color: "rgba(255,255,255,0.45)" }}>{s.updatedAt ? new Date(s.updatedAt).toLocaleDateString("en-IN") : "—"}</td>
                    <td><button className="delete-btn" onClick={() => handleDeleteAccess(s.email)}>🗑 Remove</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {activeTab === "teachers" && (
        <div className="users-section">
          <div className="users-header"><h3>👨‍🏫 Teacher Access Requests</h3><span className="user-count">{teacherAccessRequests.filter(r => r.status === "pending").length} pending</span></div>

          {teacherRequestsLoading ? (
            <div className="loading">Loading...</div>
          ) : teacherAccessRequests.length === 0 ? (
            <p style={{ color: "rgba(255,255,255,0.45)" }}>No teacher access requests yet.</p>
          ) : (
            <div>
              {teacherAccessRequests.map(request => (
                <div key={request.email} style={{ background: "rgba(106,27,154,0.07)", border: "1.5px solid rgba(106,27,154,0.3)", borderRadius: 14, padding: "20px 24px", marginBottom: 20 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 15 }}>
                    <div>
                      <h4 style={{ margin: "0 0 5px", color: "#e1bee7" }}>{request.name}</h4>
                      <p style={{ margin: 0, fontSize: "0.9rem", color: "rgba(255,255,255,0.7)" }}>{request.email}</p>
                    </div>
                    <span style={{ background: request.status === "pending" ? "#ff9800" : request.status === "approved" ? "#4caf50" : "#f44336", color: "#fff", padding: "5px 15px", borderRadius: 20, fontWeight: "bold", fontSize: "0.85rem" }}>
                      {request.status === "pending" ? "⏳ Pending" : request.status === "approved" ? "✅ Approved" : "❌ Rejected"}
                    </span>
                  </div>

                  {request.status === "pending" && (
                    <>
                      <div style={{ background: "rgba(255,255,255,0.05)", padding: "15px", borderRadius: "8px", marginBottom: "15px" }}>
                        <label style={{ display: "block", marginBottom: "10px", fontWeight: "bold", color: "#e1bee7" }}>Assign to Classes:</label>
                        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                          {CLASSES.map(cls => (
                            <label key={cls} style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                              <input
                                type="checkbox"
                                checked={selectedTeacherRequest?.email === request.email && selectedTeacherClasses.includes(cls)}
                                onChange={e => {
                                  setSelectedTeacherRequest(request);
                                  if (e.target.checked) {
                                    setSelectedTeacherClasses([...selectedTeacherClasses, cls]);
                                  } else {
                                    setSelectedTeacherClasses(selectedTeacherClasses.filter(c => c !== cls));
                                  }
                                }}
                                style={{ width: 18, height: 18 }}
                              />
                              <span>{cls}</span>
                            </label>
                          ))}
                        </div>
                      </div>

                      <div style={{ display: "flex", gap: 10 }}>
                        <button
                          onClick={() => {
                            setSelectedTeacherRequest(request);
                            handleApproveTeacherRequest();
                          }}
                          disabled={approvingTeacher}
                          style={{ flex: 1, padding: "10px", background: "#4caf50", color: "#fff", border: "none", borderRadius: "8px", cursor: "pointer", fontWeight: "bold" }}
                        >
                          {approvingTeacher ? "⏳ Approving..." : "✅ Approve"}
                        </button>
                        <button
                          onClick={() => handleRejectTeacherRequest(request)}
                          style={{ flex: 1, padding: "10px", background: "#f44336", color: "#fff", border: "none", borderRadius: "8px", cursor: "pointer", fontWeight: "bold" }}
                        >
                          ❌ Reject
                        </button>
                      </div>
                    </>
                  )}

                  {request.status === "approved" && (
                    <div style={{ color: "#4caf50" }}>
                      <strong>✅ Approved Classes:</strong> {(request.assignedClasses || []).join(", ")}
                    </div>
                  )}

                  {request.status === "rejected" && (
                    <div style={{ color: "#f44336" }}>
                      <strong>❌ Reason:</strong> {request.rejectionReason || "No reason provided"}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── VISITORS (who opened the portal) ─────────────────────────────────────────
function VisitorsPanel({ log, loading, onRefresh }) {
  const [range, setRange] = useState("today");
  const [q, setQ] = useState("");
  const now = Date.now();
  const inRange = v => {
    const t = new Date(v.loginAt).getTime();
    if (range === "today") return v.date === todayStr();
    if (range === "7d") return now - t < 7 * 864e5;
    if (range === "30d") return now - t < 30 * 864e5;
    return true;
  };
  const rows = log.filter(inRange).filter(v => !q.trim() || `${v.name} ${v.email}`.toLowerCase().includes(q.toLowerCase()));
  const people = {};
  rows.forEach(v => { const p = people[v.email] || (people[v.email] = { email: v.email, name: v.name, photoURL: v.photoURL, visits: 0, last: v.loginAt, devices: new Set() }); p.visits++; if (v.loginAt > p.last) p.last = v.loginAt; if (v.device) p.devices.add(v.device); });
  const list = Object.values(people).sort((a, b) => b.last.localeCompare(a.last));
  const fmt = iso => new Date(iso).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" });
  return (
    <div className="users-section">
      <div className="users-header"><h3>👀 Who opened the portal</h3><button className="vis-refresh" onClick={onRefresh}>🔄 Refresh</button></div>
      <div className="vis-stats">
        <div><b>{list.length}</b><span>people</span></div>
        <div><b>{rows.length}</b><span>visits</span></div>
        <div><b>{log.filter(v => v.date === todayStr()).length}</b><span>visits today</span></div>
      </div>
      <div className="vis-controls">
        {[["today", "Today"], ["7d", "Last 7 days"], ["30d", "Last 30 days"], ["all", "All (last 500)"]].map(([k, l]) => (
          <button key={k} className={range === k ? "active" : ""} onClick={() => setRange(k)}>{l}</button>
        ))}
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="🔍 Search name or email" />
      </div>
      {loading ? <div className="loading">Loading…</div> : list.length === 0 ? <p style={{ color: "rgba(255,255,255,0.6)" }}>No visits in this period.</p> : (
        <div style={{ overflowX: "auto" }}>
          <table className="users-table">
            <thead><tr><th>#</th><th>Name</th><th>Email</th><th>Visits</th><th>Last opened</th><th>Device</th></tr></thead>
            <tbody>{list.map((p, i) => (
              <tr key={p.email}><td>{i + 1}</td><td>{p.name || "—"}</td><td style={{ fontSize: "0.82rem" }}>{p.email}</td><td style={{ fontWeight: 700 }}>{p.visits}</td><td>{fmt(p.last)}</td><td style={{ fontSize: "0.8rem" }}>{[...p.devices].join(", ") || "—"}</td></tr>
            ))}</tbody>
          </table>
        </div>
      )}
      <p className="vis-note">Shows people who opened the portal while signed in with Google (one entry per browser session). Visitors who only see the welcome page without signing in can't be identified — Google Analytics counts them as anonymous totals.</p>
    </div>
  );
}

// ── ATTENDANCE TAB ──────────────────────────────────────────────────────────
const ATT_HOURS = [
  { h: 1, t: "9:00–10:00" }, { h: 2, t: "10:00–11:00" }, { h: 3, t: "11:00–12:00" },
  { h: 4, t: "1:30–2:30" }, { h: 5, t: "2:30–3:30" }, { h: 6, t: "3:30–4:30" }, { h: 7, t: "4:30–5:30" },
];
const hourLabel = h => { const x = ATT_HOURS.find(a => a.h === Number(h)); return x ? `Hour ${x.h} (${x.t})` : ""; };

function AttendanceTab() {
  const [date, setDate] = useState(todayStr());
  const [batchFilter, setBatchFilter] = useState("C1");
  const [hour, setHour] = useState(1);
  const attDocId = `${date}_${batchFilter}_H${hour}`;
  const [students, setStudents] = useState([]);
  const [attendance, setAttendance] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [viewMode, setViewMode] = useState("mark");
  const [pastDates, setPastDates] = useState([]);
  const [selectedReport, setSelectedReport] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  useEffect(() => { fetchStudents(); }, [batchFilter]);
  useEffect(() => { loadExistingAttendance(); }, [date, batchFilter, hour, students.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchStudents = async () => {
    setLoading(true);
    const q = query(collection(db, "studentRoster"), where("batch", "==", batchFilter));
    const [snap, cfg] = await Promise.all([getDocs(q), getDoc(doc(db, "settings", "roster")).catch(() => null)]);
    const current = cfg && cfg.exists() ? cfg.data().activeSemester : "";
    const list = snap.docs.map(d => ({ id: d.id, ...d.data() }))
      .filter(s => !current || (s.semesters || (s.semester ? [s.semester] : [])).includes(current))
      .sort((a, b) => (a.studentId || "").localeCompare(b.studentId || ""));
    setStudents(list);
    const defaultAtt = {}; list.forEach(s => { defaultAtt[s.email] = true; }); setAttendance(defaultAtt);
    setLoading(false);
  };

  const loadExistingAttendance = async () => {
    try {
      const snap = await getDoc(doc(db, "attendance", attDocId));
      if (snap.exists()) { const att = {}; (snap.data().records || []).forEach(r => { att[r.email] = r.present; }); setAttendance(att); }
      else { const def = {}; students.forEach(s => { def[s.email] = true; }); setAttendance(def); }
    } catch {}
  };

  const toggle = (email, val) => setAttendance(prev => ({ ...prev, [email]: val }));
  const markAll = (val) => { const all = {}; students.forEach(s => { all[s.email] = val; }); setAttendance(all); };

  const saveAttendance = async () => {
    setSaving(true);
    try {
      const records = students.map(s => ({ studentId: s.studentId, name: s.name, email: s.email, batch: s.batch, present: attendance[s.email] !== false }));
      const present = records.filter(r => r.present).length;
      await setDoc(doc(db, "attendance", attDocId), { date, batch: batchFilter, hour, hourLabel: hourLabel(hour), class: "Engineering Physics", records, totalPresent: present, totalAbsent: records.length - present, total: records.length, savedAt: new Date().toISOString() });
      alert(`✅ Saved for ${date} · Batch ${batchFilter} · ${hourLabel(hour)}\nPresent: ${present} | Absent: ${records.length - present}`);
    } catch (e) { alert("Failed to save: " + e.message); }
    setSaving(false);
  };

  const fetchPastDates = async () => {
    setReportLoading(true);
    const snap = await getDocs(collection(db, "attendance"));
    setPastDates(snap.docs.map(d => ({ id: d.id, ...d.data() })).sort((a, b) => b.date.localeCompare(a.date) || (a.batch || "").localeCompare(b.batch || "") || (a.hour || 0) - (b.hour || 0)));
    setReportLoading(false);
  };

  const exportAttendance = async () => {
    setExporting(true);
    try {
      const wb = new ExcelJS.Workbook();
      pastDates.forEach(rec => {
        const ws = wb.addWorksheet(`${rec.date} ${rec.batch}${rec.hour ? " H" + rec.hour : ""}`.substring(0, 31));
        ws.columns = [{ header: "#", key: "no", width: 5 }, { header: "Student ID", key: "sid", width: 14 }, { header: "Name", key: "name", width: 25 }, { header: "Email", key: "email", width: 32 }, { header: "Batch", key: "batch", width: 8 }, { header: "Status", key: "status", width: 12 }];
        ws.getRow(1).eachCell(cell => { cell.font = { bold: true, color: { argb: "FFFFFFFF" } }; cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFE65100" } }; cell.alignment = { horizontal: "center" }; });
        ws.addRow({ no: "", sid: `Date: ${rec.date}`, name: `Batch: ${rec.batch}${rec.hour ? " · " + hourLabel(rec.hour) : ""}`, email: `Present: ${rec.totalPresent}`, batch: `Absent: ${rec.totalAbsent}`, status: `Total: ${rec.total}` });
        ws.getRow(2).font = { bold: true, italic: true, color: { argb: "FF1A237E" } };
        ws.addRow({});
        (rec.records || []).forEach((r, i) => {
          const row = ws.addRow({ no: i + 1, sid: r.studentId, name: r.name, email: r.email, batch: r.batch, status: r.present ? "Present" : "Absent" });
          row.getCell("status").font = { bold: true, color: { argb: r.present ? "FF2E7D32" : "FFC62828" } };
          if (i % 2 === 0) row.eachCell(cell => { cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFF5F5F5" } }; });
        });
      });
      const buffer = await wb.xlsx.writeBuffer();
      saveAs(new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }), `Attendance_${todayStr()}.xlsx`);
    } catch (e) { alert("Export failed: " + e.message); }
    setExporting(false);
  };

  const presentCount = students.filter(s => attendance[s.email] !== false).length;

  return (
    <div className="attendance-section">
      <div className="attendance-mode-toggle">
        <button className={viewMode === "mark" ? "active" : ""} onClick={() => setViewMode("mark")}>✏️ Mark Attendance</button>
        <button className={viewMode === "report" ? "active" : ""} onClick={() => { setViewMode("report"); fetchPastDates(); }}>📋 View Reports</button>
      </div>

      {viewMode === "mark" && (<>
        <div className="attendance-controls">
          <div className="att-control-row"><label>📅 Date:</label><input type="date" value={date} onChange={e => setDate(e.target.value)} className="att-date-input" max={todayStr()} /></div>
          <div className="att-control-row"><label>🏷 Batch:</label><div style={{ display: "flex", gap: 8 }}>{["C1", "C2"].map(b => <button key={b} className={`batch-select-btn ${batchFilter === b ? "active" : ""}`} onClick={() => setBatchFilter(b)}>Batch {b}</button>)}</div></div>
          <div className="att-control-row"><label>🕘 Hour:</label>
            <select value={hour} onChange={e => setHour(Number(e.target.value))} className="att-date-input" style={{ minWidth: 200 }}>
              {ATT_HOURS.map(x => <option key={x.h} value={x.h}>Hour {x.h} · {x.t}</option>)}
            </select>
          </div>
        </div>
        <div className="attendance-summary-bar">
          <div className="att-summary-box present">✅ Present: <strong>{presentCount}</strong></div>
          <div className="att-summary-box absent">❌ Absent: <strong>{students.length - presentCount}</strong></div>
          <div className="att-summary-box total">👥 Total: <strong>{students.length}</strong></div>
          <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
            <button className="att-bulk-btn present" onClick={() => markAll(true)}>✅ All Present</button>
            <button className="att-bulk-btn absent" onClick={() => markAll(false)}>❌ All Absent</button>
          </div>
        </div>
        {loading ? <div className="loading">Loading...</div> : students.length === 0 ? <div className="quiz-empty"><p>No students for Batch {batchFilter}. Upload in 🎓 Student Roster first.</p></div> : (
          <div className="attendance-list">
            {students.map((s, i) => {
              const isPresent = attendance[s.email] !== false;
              return (
                <div key={s.email} className={`attendance-row ${isPresent ? "present" : "absent"}`}>
                  <span className="att-num">{i + 1}</span>
                  <span className="att-id">{s.studentId}</span>
                  <span className="att-name">{s.name}</span>
                  <span className="att-email">{s.email}</span>
                  <div className="att-btn-group">
                    <button className={`att-btn present-btn ${isPresent ? "active" : ""}`} onClick={() => toggle(s.email, true)}>✅ Present</button>
                    <button className={`att-btn absent-btn ${!isPresent ? "active" : ""}`} onClick={() => toggle(s.email, false)}>❌ Absent</button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        <button className="save-btn" style={{ marginTop: 20 }} onClick={saveAttendance} disabled={saving || students.length === 0}>{saving ? "⏳ Saving..." : `💾 Save Attendance — ${date} · Batch ${batchFilter} · Hour ${hour}`}</button>
      </>)}

      {viewMode === "report" && (
        <div className="report-section">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
            <h3 style={{ color: "#1a237e" }}>📋 Records ({pastDates.length})</h3>
            <button className="export-btn" onClick={exportAttendance} disabled={exporting || pastDates.length === 0}>{exporting ? "⏳ Exporting..." : "📥 Export All to Excel"}</button>
          </div>
          {reportLoading ? <div className="loading">Loading...</div> : pastDates.length === 0 ? <p style={{ color: "#999" }}>No records yet.</p> : (
            <div className="report-list">
              {pastDates.map(rec => (
                <div key={rec.id} className="report-card" onClick={() => setSelectedReport(selectedReport?.id === rec.id ? null : rec)}>
                  <div className="report-card-header">
                    <div><span className="report-date">📅 {rec.date}</span><span className={`batch-chip ${rec.batch === "C1" ? "c1" : "c2"}`} style={{ marginLeft: 10 }}>{rec.batch}</span>{rec.hour && <span className="batch-chip" style={{ marginLeft: 8, background: "#ede7f6", color: "#4527a0" }}>🕘 {hourLabel(rec.hour)}</span>}</div>
                    <div className="report-stats">
                      <span className="att-stat present">✅ {rec.totalPresent}</span>
                      <span className="att-stat absent">❌ {rec.totalAbsent}</span>
                      <span className="att-stat total">👥 {rec.total}</span>
                      <span style={{ fontSize: "0.82rem", color: rec.totalPresent / rec.total >= 0.75 ? "#2e7d32" : "#c62828", fontWeight: 700 }}>{Math.round((rec.totalPresent / rec.total) * 100)}%</span>
                      <span style={{ color: "#999", fontSize: "0.85rem" }}>{selectedReport?.id === rec.id ? "▲" : "▼"}</span>
                    </div>
                  </div>
                  {selectedReport?.id === rec.id && (
                    <div className="report-detail" onClick={e => e.stopPropagation()}>
                      <table className="users-table" style={{ marginTop: 12 }}>
                        <thead><tr><th>#</th><th>ID</th><th>Name</th><th>Status</th></tr></thead>
                        <tbody>{(rec.records || []).map((r, i) => <tr key={r.email}><td>{i + 1}</td><td>{r.studentId}</td><td>{r.name}</td><td style={{ color: r.present ? "#2e7d32" : "#c62828", fontWeight: 700 }}>{r.present ? "✅ Present" : "❌ Absent"}</td></tr>)}</tbody>
                      </table>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}